import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TextureFactory, SURFACES } from '../render/Materials.js';

const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _boreAxis = new THREE.Vector3(0, 0, 1);
const _xAxis = new THREE.Vector3(1, 0, 0);
const _e = new THREE.Euler();
const _m = new THREE.Matrix4();
const _ax = new THREE.Vector3();
const _ay = new THREE.Vector3();
const _az = new THREE.Vector3();
const _UP = new THREE.Vector3(0, 1, 0);
// Arm IK scratch. The solver runs every frame for every weapon, so it must not
// allocate — same rule as the combat effects.
const _ik0 = new THREE.Vector3();
const _ik1 = new THREE.Vector3();
const _ik2 = new THREE.Vector3();
const _ik3 = new THREE.Vector3();
const _ikQ = new THREE.Quaternion();

// Viewmodel vertical FOV.
//
// Narrowing this magnifies the weapon rather than shrinking it — a smaller FOV
// is a longer lens. At 42 the carbine loomed over a third of the screen. AAA
// shooters run the viewmodel wider than the world camera's vertical FOV, which
// keeps the weapon compact in the lower-right while still giving the hands
// enough perspective divergence to read as three-dimensional.
const VM_FOV = 58;

/* ============================================================ geometry ====
 * Everything is built from two primitives: a chamfered box and a lathed
 * profile. Real edges are never perfectly sharp — they catch a highlight —
 * and that single fact does more for "this is a photographed object" than
 * any amount of extra polygons.
 * ========================================================================= */

function roundedRect(w, h, r) {
  const x = w / 2, y = h / 2;
  r = Math.min(r, x * 0.98, y * 0.98);
  const s = new THREE.Shape();
  s.moveTo(-x + r, -y);
  s.lineTo(x - r, -y); s.quadraticCurveTo(x, -y, x, -y + r);
  s.lineTo(x, y - r); s.quadraticCurveTo(x, y, x - r, y);
  s.lineTo(-x + r, y); s.quadraticCurveTo(-x, y, -x, y - r);
  s.lineTo(-x, -y + r); s.quadraticCurveTo(-x, -y, -x + r, -y);
  return s;
}

function finish(g) {
  if (g.attributes.uv && !g.attributes.uv1) g.setAttribute('uv1', g.attributes.uv);
  return g;
}

/** Box with all twelve edges chamfered. Extent (w, h, d), centred on origin. */
function box(w, h, d, c = 0.0022) {
  c = Math.max(0.0004, Math.min(c, w * 0.32, h * 0.32, d * 0.32));
  const depth = Math.max(1e-4, d - 2 * c);
  const g = new THREE.ExtrudeGeometry(roundedRect(w, h, c * 1.7), {
    depth, bevelEnabled: true, bevelThickness: c, bevelSize: c,
    bevelOffset: 0, bevelSegments: 1, curveSegments: 2,
  });
  g.center();
  return finish(g);
}

/**
 * Box for ORGANIC parts — hands, pads, anything that should read as flesh.
 *
 * Same shape as `box`, three times the bevel resolution. `box` uses one bevel
 * segment and two curve segments, which is right for machined steel and wrong
 * for a hand: every rounded corner becomes a single flat facet, and a palm
 * built from those reads as folded cardboard. Reference (CS:GO, BO6, BOCW
 * viewmodels) shows hands as smooth rounded masses with soft knuckle bumps and
 * no visible steps anywhere.
 */
function softBox(w, h, d, c = 0.004) {
  c = Math.max(0.0004, Math.min(c, w * 0.34, h * 0.34, d * 0.34));
  const depth = Math.max(1e-4, d - 2 * c);
  const g = new THREE.ExtrudeGeometry(roundedRect(w, h, c * 1.7), {
    depth, bevelEnabled: true, bevelThickness: c, bevelSize: c,
    bevelOffset: 0, bevelSegments: 3, curveSegments: 5,
  });
  g.center();
  return finish(g);
}

/**
 * A finger segment: a capsule lying along +X, spanning 0..L when placed at
 * x = L/2. Capsules are smooth by construction, which is the whole point —
 * boxed phalanges are what made the fingers read as stacked blocks.
 */
function limb(L, thick, squash = 0.92) {
  const r = Math.max(1e-4, thick * 0.5);
  const g = new THREE.CapsuleGeometry(r, Math.max(1e-4, L - thick), 4, 10);
  g.rotateZ(-Math.PI / 2);
  g.scale(1, 1, squash);              // fingers are wider than they are deep
  return finish(g);
}

/**
 * Lathe about the bore axis. `profile` is [[radius, forward], ...] where
 * forward is +Z-back / -Z-front distance; the result has its axis on -Z.
 */
function tube(profile, seg = 20, phiStart = 0) {
  const pts = profile.map(([r, z]) => new THREE.Vector2(Math.max(1e-5, r), z));
  const g = new THREE.LatheGeometry(pts, seg, phiStart, Math.PI * 2);
  g.rotateX(-Math.PI / 2);
  return finish(g);
}

function merged(list) {
  const g = mergeGeometries(list, false);
  for (const x of list) x.dispose?.();
  return finish(g);
}

/** Copies of `geo` scattered by [x,y,z,rz] — one draw call for repeated detail. */
function scatter(geo, xforms) {
  const out = [];
  for (const t of xforms) {
    const c = geo.clone();
    if (t[3]) c.rotateZ(t[3]);
    if (t[4]) c.rotateX(t[4]);
    c.translate(t[0], t[1], t[2]);
    out.push(c);
  }
  geo.dispose();
  return merged(out);
}

/* ============================================================ textures ==== */

function hash2(x, y, s) {
  const n = Math.sin(x * 127.1 + y * 311.7 + s * 74.7) * 43758.5453;
  return n - Math.floor(n);
}

function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi, s), b = hash2(xi + 1, yi, s);
  const c = hash2(xi, yi + 1, s), d = hash2(xi + 1, yi + 1, s);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

/**
 * Height field -> tangent-space normal + roughness pair. Used for the
 * moulding stipple on polymer and the micro-tooling on machined steel.
 */
function procSurface(size, hfn, roughFn, strength) {
  const h = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) h[y * size + x] = hfn(x / size, y / size);
  }
  const nd = new Uint8Array(size * size * 4);
  const rd = new Uint8Array(size * size * 4);
  const at = (x, y) => h[((y + size) % size) * size + ((x + size) % size)];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
      const len = Math.hypot(dx, dy, 1);
      const i = (y * size + x) * 4;
      nd[i] = ((-dx / len) * 0.5 + 0.5) * 255;
      nd[i + 1] = ((-dy / len) * 0.5 + 0.5) * 255;
      nd[i + 2] = (1 / len) * 0.5 * 255 + 127;
      nd[i + 3] = 255;
      const r = THREE.MathUtils.clamp(roughFn(h[y * size + x], x / size, y / size), 0, 1) * 255;
      rd[i] = 255; rd[i + 1] = r; rd[i + 2] = 0; rd[i + 3] = 255;
    }
  }
  const mk = (data) => {
    const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = true;
    t.needsUpdate = true;
    return t;
  };
  return { normalMap: mk(nd), roughnessMap: mk(rd) };
}

/**
 * Radial alpha ramp for the scope shadow, authored for a RingGeometry whose
 * UVs map the outer radius to the texture edge. Stacked rings of constant
 * opacity band visibly; a ramp is one draw call and reads as a soft falloff.
 */
function radialRamp(size, inner) {
  const d = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const nx = (x + 0.5) / size * 2 - 1, ny = (y + 0.5) / size * 2 - 1;
      const r = Math.hypot(nx, ny);
      let t = THREE.MathUtils.clamp((r - inner) / (1 - inner), 0, 1);
      t = t * t * (3 - 2 * t);
      const i = (y * size + x) * 4;
      d[i] = d[i + 1] = d[i + 2] = t * 255; d[i + 3] = 255;
    }
  }
  const t = new THREE.DataTexture(d, size, size, THREE.RGBAFormat);
  t.minFilter = THREE.LinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}

/* ============================================================== specs ===== */

const DEFAULT_ART = {
  // Which builder runs. 'rifle' is the original AR/MP5/MK14 pipeline; the
  // others swap in a completely different receiver, sight line and grip.
  family: 'rifle',
  scale: 1.0,
  steel: 0x4e545c, steelRough: 0.58,
  furniture: 0x2b2f34, furnitureRough: 0.50,
  upperLen: 0.205, upperH: 0.050, upperW: 0.038, receiverRound: false,
  hgLen: 0.255, hgR: 0.0295, hgStyle: 'mlok', hgSeg: 8,
  barrelExposed: 0.055, barrelR: 0.0098, muzzle: 'a2',
  stock: 'collapsible', stockLen: 0.115, combRise: 0,
  magLen: 0.185, magW: 0.030, magD: 0.058, magCurve: 0.13, witness: 4,
  optic: 'reddot', riser: 0.030,
  gripAngle: 0.30, gripLen: 0.105,
  chargeStyle: 'ar',
  // Share of the screen HEIGHT the ocular should cover at full ADS. `adsEye`
  // is no longer authored — _build solves it from this and the optic geometry,
  // then writes the result back here for tooling to read.
  adsFill: 0.28,
  adsEye: 0.250,
  // Support hand: where on the fore-end the knuckle row lands. `lhY` shifts the
  // tube centre off the bore (a shotgun's forend rides under the barrel).
  lhY: 0,
  // Iron-sighted weapons publish the eye relief they want directly; see
  // _setIrons(). Optics solve it from `adsFill` instead.
  ironRelief: 0.178,
  // Magnified optics only: share of screen height the scope's sight picture
  // fills once the overlay has taken over. See _scopeOverlay().
  scopeFill: 0.66,
  hipPos: [0.188, -0.128, -0.448], hipRot: [0.018, 0.280, 0.060],
  lhZ: -0.170,
};

export const WEAPONS = {
  /* ---------------------------------------------------------------------- *
   * Knife. Melee: no magazine, no reload, no sight. `melee: true` routes the
   * attack in main.js to a short trace instead of a hitscan round, and
   * suppresses the muzzle flash, shell and report that every firearm emits.
   * ---------------------------------------------------------------------- */
  knife: {
    name: 'KNIFE', mag: 0, reserve: 0, rpm: 160, damage: 78, headMult: 1.35,
    spread: 0, adsSpread: 0, recoil: { pitch: 0.012, yaw: 0.008, kick: 0.030 },
    reloadTime: 0, adsTime: 0.16, range: 1.9, mode: 'semi', fovZoom: 1.0,
    melee: true, ejects: false, flashScale: 0, report: 0, sound: 'melee',
    art: {
      family: 'knife', scale: 1.0,
      steel: 0xa8aeb6, steelRough: 0.20, furniture: 0x191b1d, furnitureRough: 0.74,
      gripAngle: 0.34, gripLen: 0.105,
      adsFill: 0.28,
      optic: 'none',
      hipPos: [0.168, -0.132, -0.330], hipRot: [0.06, 0.16, 0.22],
    },
  },

  m4: {
    name: 'M4A1', mag: 30, reserve: 210, rpm: 780,
    // Assault rifle: the reference weapon. Four to the chest close, five far.
    damage: 33, damageMin: 22, nearRange: 25, farRange: 45, headMult: 1.4,
    spread: 0.011, adsSpread: 0.0016, recoil: { pitch: 0.028, yaw: 0.010, kick: 0.055 },
    reloadTime: 2.15, adsTime: 0.19, range: 180, mode: 'auto', fovZoom: 0.72,
    art: {},
  },
  mp5: {
    name: 'MP5', mag: 32, reserve: 240, rpm: 900,
    // SMG: hits harder than the rifle up close and falls apart past a room.
    // Three to the chest inside 12 m, five beyond 26.
    damage: 34, damageMin: 19, nearRange: 12, farRange: 26, headMult: 1.4,
    spread: 0.017, adsSpread: 0.0035, recoil: { pitch: 0.020, yaw: 0.012, kick: 0.042 },
    reloadTime: 1.85, adsTime: 0.15, range: 90, mode: 'auto', fovZoom: 0.78,
    art: {
      scale: 0.96,
      steel: 0x4c5158, steelRough: 0.42, furniture: 0x25282c, furnitureRough: 0.44,
      upperLen: 0.225, upperH: 0.044, upperW: 0.035, receiverRound: true,
      hgLen: 0.150, hgR: 0.0245, hgStyle: 'ribbed', hgSeg: 14,
      barrelExposed: 0.030, barrelR: 0.0088, muzzle: 'trilug',
      stock: 'fixed', stockLen: 0.150, combRise: 0.004,
      magLen: 0.205, magW: 0.027, magD: 0.048, magCurve: 0.30, witness: 0,
      optic: 'irons', riser: 0.024,
      gripAngle: 0.24, gripLen: 0.098,
      chargeStyle: 'hk',
      hipPos: [0.180, -0.122, -0.408], hipRot: [0.018, 0.285, 0.065],
      lhZ: -0.104,
    },
  },
  dmr: {
    name: 'MK14', mag: 20, reserve: 120, rpm: 320,
    // Marksman: a two-shot at any range worth taking, and it keeps most of its
    // damage where the others have given up.
    damage: 65, damageMin: 50, nearRange: 45, farRange: 90, headMult: 1.6,
    spread: 0.008, adsSpread: 0.0006, recoil: { pitch: 0.055, yaw: 0.014, kick: 0.11 },
    reloadTime: 2.4, adsTime: 0.26, range: 300, mode: 'semi', fovZoom: 0.5,
    art: {
      scale: 1.04,
      steel: 0x53585f, steelRough: 0.48, furniture: 0x33362c, furnitureRough: 0.56,
      upperLen: 0.245, upperH: 0.056, upperW: 0.042, receiverRound: false,
      hgLen: 0.275, hgR: 0.0315, hgStyle: 'quad', hgSeg: 4,
      barrelExposed: 0.150, barrelR: 0.0125, muzzle: 'brake',
      stock: 'precision', stockLen: 0.135, combRise: 0.020,
      magLen: 0.150, magW: 0.032, magD: 0.070, magCurve: 0.06, witness: 0,
      optic: 'scope', riser: 0.024,
      gripAngle: 0.26, gripLen: 0.110,
      chargeStyle: 'ar',
      hipPos: [0.196, -0.136, -0.478], hipRot: [0.018, 0.270, 0.055],
      lhZ: -0.196,
    },
  },

  /* ---------------------------------------------------------------------- *
   * Sidearm. Reference: Modern Warfare's Renetti/1911 viewmodel and Escape
   * from Tarkov's Glock 17 — a pistol reads MUCH smaller than a rifle in
   * frame (roughly a third of the screen height, not two thirds), sits closer
   * to the centre because both arms are extended in front of the face, and
   * shows BOTH hands: the support hand cups the firing hand and both thumbs
   * run forward along the left of the frame. That thumb line is the single
   * cue that says "this person has held a pistol".
   * ---------------------------------------------------------------------- */
  m9: {
    name: 'M9A1', mag: 15, reserve: 90, rpm: 430,
    // Sidearm: respectable in a corridor, poor anywhere else.
    damage: 32, damageMin: 20, nearRange: 10, farRange: 22, headMult: 1.5,
    spread: 0.021, adsSpread: 0.0032, recoil: { pitch: 0.030, yaw: 0.013, kick: 0.048 },
    reloadTime: 1.85, adsTime: 0.13, range: 70, mode: 'semi', fovZoom: 0.86,
    flashScale: 0.7, report: 0.78,
    art: {
      family: 'pistol', scale: 1.0,
      steel: 0x3c4045, steelRough: 0.44, furniture: 0x191b1e, furnitureRough: 0.52,
      // 335 mm of eye relief is how far a real shooter's eye is from the rear
      // notch, and it measured out at 13 % of screen height — a toy. The CoD4
      // / MWR / Ghosts M9 ADS captures put the slide across roughly a fifth of
      // the frame with the firing hand filling everything below it, so the
      // relief comes in to where the gun reads at that size.
      optic: 'irons', ironRelief: 0.248, adsFill: 0.24,
      gripAngle: 0.30, gripLen: 0.088,
      slideLen: 0.196, slideW: 0.025, slideH: 0.026, barrelR: 0.0082,
      magLen: 0.090, magW: 0.024, magD: 0.040,
      hipPos: [0.124, -0.086, -0.344], hipRot: [0.040, 0.206, 0.058],
    },
  },

  /* ---------------------------------------------------------------------- *
   * Pump gun. Reference: Insurgency: Sandstorm's M870 first-person view —
   * barrel and magazine tube read as two parallel cylinders joined by a clamp,
   * the ribbed forend sits UNDER the bore with the support hand wrapped round
   * it from below, and the receiver comes back close to the camera on the
   * right. The pump stroke is the whole personality of the weapon.
   * ---------------------------------------------------------------------- */
  m870: {
    name: 'M870', mag: 6, reserve: 42, rpm: 76,
    // Per PELLET, of nine. Lethal inside 5 m, two shots to about 10, and
    // nothing at all past 13 — the steepest falloff in the game by far.
    damage: 24, damageMin: 6, nearRange: 5, farRange: 13, headMult: 1.25,
    spread: 0.030, adsSpread: 0.021, recoil: { pitch: 0.105, yaw: 0.028, kick: 0.20 },
    reloadTime: 4.4, adsTime: 0.25, range: 60, mode: 'semi', fovZoom: 0.90,
    pellets: 9, pelletCone: 0.042,
    ejects: false, flashScale: 1.8, report: 1.45,
    art: {
      family: 'shotgun', scale: 1.0,
      steel: 0x2f3338, steelRough: 0.40, furniture: 0x22262a, furnitureRough: 0.62,
      // The stock used to run 60 mm past the eye and filled the bottom of the
      // ADS frame with an unreadable marbled lump. Standing the ghost ring
      // higher off the bore and pushing the eye back puts the comb behind the
      // camera, which is where a cheek weld actually puts it — and matches the
      // W1200 / M1014 ADS captures, where nothing but barrel and ring is in
      // frame above the support hand.
      optic: 'irons', ironRelief: 0.238, adsFill: 0.26, sightY: 0.0340,
      gripAngle: 0.50, gripLen: 0.112,
      recvLen: 0.176, recvW: 0.040, recvH: 0.050,
      barrelLen: 0.320, barrelR: 0.0118, tubeR: 0.0128, tubeDrop: 0.0268,
      forendLen: 0.150, forendR: 0.0250, forendZ: -0.140, pumpTravel: 0.052,
      stockLen: 0.180,
      lhZ: -0.140, lhY: -0.0268,
      // Pulled inboard: at x = 0.196 the receiver, ghost ring and stock all sat
      // off the right edge and the hip pose read as a length of pipe.
      hipPos: [0.166, -0.124, -0.428], hipRot: [0.020, 0.268, 0.062],
    },
  },

  /* ---------------------------------------------------------------------- *
   * Shoulder-fired launcher.
   *
   * Reference, and this one overturned the previous build entirely:
   *   * Call of Duty 4 / MWR "Empty RPG-7" and "RPG-7 ADS" viewmodel captures,
   *   * Advanced Warfare's RPG-7 three-quarter viewmodel,
   *   * an Afghan/Ukrainian RPG gunner firing photograph on Commons.
   * All three sight the launcher through its FOLDING LEAF IRON SIGHTS, not
   * through a PGO-7. That is not an art shortcut, it is forced by the same
   * geometry that broke the MK14: a 30 mm ocular at any believable eye relief
   * is a keyhole, and the previous build proved it — the PGO-7 filled the
   * middle of the frame with a black disc and the tube filled the rest.
   *
   * The other thing the reference settles is HEIGHT. In every shipped capture
   * the sight line stands a long way proud of the tube — the rear leaf top is
   * about one and a half tube diameters above the tube crest — so the launch
   * tube reads as a lit wedge across the LOWER THIRD converging toward the
   * crosshair, and the sight picture itself is open sky. The old build put the
   * sight line at y = 0.014, *below* the tube's own crest at y = 0.025, which
   * is why aligning it to the screen centre buried the crosshair in tube.
   *
   * The irons sit slightly left of the tube's centreline, as they do on the
   * real weapon, which is what keeps the bore where a gunner actually sees it.
   * ---------------------------------------------------------------------- */
  rpg: {
    name: 'RPG-7', mag: 1, reserve: 4, rpm: 42, damage: 0, headMult: 1,
    spread: 0.014, adsSpread: 0.0045, recoil: { pitch: 0.085, yaw: 0.020, kick: 0.155 },
    reloadTime: 3.6, adsTime: 0.32, range: 400, mode: 'semi', fovZoom: 0.80,
    projectile: { speed: 44, radius: 6.5, damage: 165, life: 8 },
    ejects: false, flashScale: 3.2, report: 1.9,
    art: {
      family: 'launcher', scale: 1.0,
      steel: 0x4a4f55, steelRough: 0.62, furniture: 0x513a25, furnitureRough: 0.86,
      optic: 'irons', ironRelief: 0.182, adsFill: 0.30,
      gripAngle: 0.22, gripLen: 0.104,
      tubeR: 0.0250, tubeFront: -0.300, tubeRear: 0.290,
      // Sight line: 47 mm proud of the tube crest, 10 mm left of the bore.
      sightX: -0.0100, sightY: 0.0720, sightRearZ: 0.0680, sightFrontZ: -0.1280,
      hipPos: [0.182, -0.146, -0.392], hipRot: [0.050, 0.238, 0.092],
    },
  },
};

/* =============================================================== weapon === */

/**
 * Viewmodel + firing logic. The gun lives in `engine.viewmodelScene` so it is
 * rendered by a separate near-plane camera and can never clip into the world.
 * All motion is spring-damped toward pose targets rather than keyframed.
 */
export class Weapon {
  /** Angle round the handguard at which the support palm contacts it. */
  static GRIP_THETA = 4.20;

  /**
   * Support-hand poses calibrated by hand at /debug/weapon-grip/ and signed off
   * by eye against the reference screenshots. Keyed by weapon name, because
   * only what has actually been tuned and looked at belongs here — every other
   * weapon stays on the old family solver until it gets the same treatment.
   *
   * The grip transform is stored as a quaternion rather than an Euler triple
   * on purpose: the stand's rotation sliders are Euler and two different
   * triples can name the same orientation, which is exactly the kind of
   * ambiguity that loses a tuned pose.
   *
   * Provenance, including the user's own tuned values before they were baked:
   * docs/reference/grips/tuned-m4a1-user.json
   */
  static LEFT_GRIP = {
    M4A1: {
      // Wrist transform. Baked from the user's tuned blend so the weights can
      // sit at 1.0 and the numbers mean what they say.
      position: [-0.03795, -0.03971, -0.10411],
      quaternion: [0.908339, -0.057698, -0.394569, -0.126123],
      // Shoulder, pole and upper-arm length come from a constrained search over
      // 122k arm configurations (tools/arm-solve.mjs). None of the three is
      // visible on screen — only their effect on the forearm is — so they are
      // free to be solved rather than authored.
      //
      // The elbow cannot simply be dragged inboard: rolling it around the
      // shoulder-wrist axis swings the forearm, and the hand's orientation is
      // already fixed by the grip, so every degree of roll lands in the wrist.
      // The exchange rate is about 4 mm of tuck per degree, and pulling the
      // elbow all the way to the centreline costs a 60 degree wrist.
      //
      // This is the furthest tuck that keeps BOTH bands from the method doc:
      // elbow 147 mm off the centreline (in from 186 mm), wrist 19.9 deg,
      // inside elbow 111.9 deg. Spending up to 25 deg of wrist would buy
      // another 19 mm.
      shoulder: [-0.100, -0.220, 0.200],
      elbowHint: [-0.2151, 0.0572, 0.1243],
      upper: 0.200,
      fore: 0.240,
      // Finger curls from the `handguardGrip` preset; the thumb solved against
      // the reference screenshots by tools/thumb-solve.mjs.
      //
      // 01-CONTROL-bo6-xm4, 02-CONTROL-bocw-ak47 and ref-bocw-bullfrog-thumb
      // all show the same thing: the thumb lies along the UPPER flank of the
      // handguard, parallel to the bore, tip at about bore height. The preset
      // had it drooping 43 mm below the bore line and 95 mm off the axis. This
      // brings the tip up to -6 mm and in to 58 mm while staying parallel
      // (0.95 against the barrel).
      fingers: {
        index: 0.76, middle: 0.80, ring: 0.78, pinky: 0.72,
        thumbSpread: -0.50, thumbRoll: -0.50, thumbLift: -0.70, thumbCurl: 0.05,
      },
    },

    /* --------------------------------------------------------------------
     * The other long guns, derived from the M4A1 by tools/grip-derive.mjs.
     *
     * NOT copied — copied numbers would be meaningless, since the MP5's
     * handguard is 5 mm thinner and 66 mm further back and the M870's fore-end
     * hangs 27 mm below the bore. The approved pose is re-expressed relative to
     * the fore-end (angle round the tube, clearance above its surface, offset
     * from that gun's own support station) and rebuilt against each weapon's
     * own geometry. Orientation carries over unchanged because every bore runs
     * along -Z.
     *
     * The arm and the thumb do not transfer at all, so both are re-solved per
     * weapon: the arm for wrist <=20 deg with the elbow tucked as far inboard
     * as that allows, the thumb for parallel-to-the-bore and seated on the
     * fore-end.
     * ----------------------------------------------------------------- */

    // Handguard R 24.5 mm, station -104 mm. Measured: elbow 116.6, wrist 19.9.
    MP5: {
      position: [-0.0345, -0.0361, -0.03811],
      quaternion: [0.90834, -0.0577, -0.39457, -0.12612],
      shoulder: [-0.300, -0.140, 0.200],
      elbowHint: [-0.04873, 0.03196, 0.26547],
      upper: 0.200, fore: 0.240,
      fingers: {
        index: 0.76, middle: 0.80, ring: 0.78, pinky: 0.72,
        thumbSpread: -0.65, thumbRoll: -0.05, thumbLift: 0, thumbCurl: 0.05,
      },
    },

    // Handguard R 31.5 mm, station -196 mm. Measured: elbow 118.4, wrist 20.0.
    MK14: {
      position: [-0.03933, -0.04116, -0.13011],
      quaternion: [0.90834, -0.0577, -0.39457, -0.12612],
      shoulder: [-0.060, -0.220, 0.260],
      elbowHint: [-0.21436, 0.04355, 0.13605],
      upper: 0.260, fore: 0.240,
      fingers: {
        index: 0.76, middle: 0.80, ring: 0.78, pinky: 0.72,
        thumbSpread: -0.75, thumbRoll: -0.15, thumbLift: -0.25, thumbCurl: 0.18,
      },
    },

    // Fore-end R 25 mm sitting 26.8 mm BELOW the bore, station -140 mm.
    // Measured: elbow 114.4, wrist 19.8. The pump hand rides the stroke, which
    // `_pumpApply` adds on top of this pose rather than replacing it.
    M870: {
      position: [-0.03484, -0.06326, -0.07411],
      quaternion: [0.90834, -0.0577, -0.39457, -0.12612],
      shoulder: [-0.140, -0.340, 0.260],
      elbowHint: [-0.17465, -0.00845, 0.22550],
      upper: 0.290, fore: 0.240,
      fingers: {
        index: 0.76, middle: 0.80, ring: 0.78, pinky: 0.72,
        thumbSpread: -0.65, thumbRoll: 0.05, thumbLift: 0, thumbCurl: 0.18,
      },
    },

    // The RPG is deliberately absent. It has a VERTICAL foregrip, where the
    // curl plane is perpendicular to the post and the thumb runs up it rather
    // than along the bore — a different grip, not a scaled version of this one.
    // It stays on `_supportOnPost` until it gets its own calibration.
  };

  constructor(engine, player, spec = WEAPONS.m4) {
    this.engine = engine;
    this.player = player;
    this.spec = spec;
    this.art = { ...DEFAULT_ART, ...(spec.art || {}) };

    this.ammo = spec.mag;
    this.reserve = spec.reserve;
    this.cooldown = 0;
    this.reloadTimer = 0;
    this.reloading = false;
    this.reloadFromEmpty = false;
    this.wantsAds = false;
    this.lastFire = -99;
    this.heat = 0;
    this.shotIndex = 0;
    this.t = 0;

    // ADS is a second-order spring so it overshoots a hair and settles.
    this._adsRaw = 0;
    this._adsVel = 0;
    this._adsPublic = 0;
    this._adsForce = false;

    this.group = new THREE.Group();
    this.sway = new THREE.Vector2();
    this.swayVel = new THREE.Vector2();

    // Recoil springs: rearward translation, muzzle rise, roll, lateral.
    this.rc = { z: 0, zv: 0, p: 0, pv: 0, r: 0, rv: 0, y: 0, yv: 0 };
    this.boltT = 0;
    this.swingT = -1;      // knife swing progress, -1 = idle
    this.trigger = 0;
    this.triggerV = 0;
    // Trigger discipline. `fingerOn` springs 0 (finger straight, indexed along
    // the receiver) -> 1 (pad on the trigger). `_fireHold` keeps it there
    // between shots so a burst does not chatter the finger on and off.
    this.fingerOn = 0;
    this._fingerVel = 0;
    this._fireHold = 0;
    // Bolt held to the rear on an empty gun: the AR's bolt catch, the MP5's
    // cocking lever rotated up into its notch.
    this.boltLocked = false;
    this._lever = 0;
    this._sprint = 0;
    this.inspectT = 0;
    // Pump-action cycle: 1 at the instant of the shot, counting down through
    // the rearward stroke and the return.
    this.pumpT = 0;
    this._pumpS = 0;
    this._pumpPrevDz = 0;
    this._ejected = true;
    this._fx = null;
    // Shell-by-shell reload bookkeeping.
    this._shellsLoaded = 0;

    this.hipPose = {
      pos: new THREE.Vector3().fromArray(this.art.hipPos),
      rot: new THREE.Euler().fromArray(this.art.hipRot),
    };
    this.adsPose = { pos: new THREE.Vector3(0, 0, -0.2), rot: new THREE.Euler(0, 0, 0) };

    this._build();
    this._lightRig();

    // A tighter viewmodel FOV is what makes the weapon read at CoD scale
    // instead of looming like a prop held at arm's length from a fisheye.
    const vc = engine.viewmodelCamera;
    if (vc.fov !== VM_FOV) { vc.fov = VM_FOV; vc.updateProjectionMatrix(); }

    engine.viewmodelScene.add(this.group);
  }

  /** debugPose() pokes `.ads` directly; make that snap the spring and stick. */
  get ads() { return this._adsPublic; }
  set ads(v) {
    this._adsRaw = v; this._adsVel = 0; this._adsPublic = v;
    this._adsForce = v > 0.5;
  }

  /* ---------------------------------------------------------- lighting --- */

  /**
   * The viewmodel scene only gets an environment map from Atmosphere, which
   * leaves the gun flat. A three-point rig lives on the scene (created once,
   * shared across weapon switches) so edges and chamfers actually catch light.
   */
  _lightRig() {
    const scene = this.engine.viewmodelScene;
    if (scene.__vmRig) return;
    const rig = new THREE.Group();
    const key = new THREE.DirectionalLight(0xfff2df, 2.6);
    key.position.set(-0.55, 0.9, 0.35);
    rig.add(key);
    const fill = new THREE.DirectionalLight(0x9fb6d8, 1.05);
    fill.position.set(0.8, -0.25, 0.5);
    rig.add(fill);
    // The rim runs almost straight down the bore, so at ADS it hits the whole
    // length of the top rail edge-on. Above about 1.6 the rail turns into a
    // white staircase across the bottom of the sight picture.
    const rim = new THREE.DirectionalLight(0xdce8ff, 1.55);
    rim.position.set(0.35, 0.25, -1.0);
    rig.add(rim);
    const amb = new THREE.HemisphereLight(0x8fa6c4, 0x2a2622, 0.75);
    rig.add(amb);
    scene.add(rig);
    scene.__vmRig = rig;
  }

  /* ------------------------------------------------------------- build --- */

  _materials() {
    const A = this.art;
    const T = new TextureFactory(this.engine.renderer, 128);
    const set = T.surface('gunmetal', SURFACES.gunmetal);
    const micro = set.normalMap.clone();
    micro.wrapS = micro.wrapT = THREE.RepeatWrapping;
    micro.repeat.set(6, 6);
    micro.needsUpdate = true;

    const poly = procSurface(
      96,
      (x, y) => vnoise(x * 46, y * 46, 3) * 0.55 + vnoise(x * 130, y * 130, 9) * 0.45,
      (h) => 0.40 + h * 0.24,
      1.6
    );
    const fabric = procSurface(
      64,
      (x, y) => 0.5 + 0.3 * Math.sin(x * Math.PI * 8) * Math.sin(y * Math.PI * 8)
        + 0.2 * vnoise(x * 6, y * 6, 21),
      (h) => 0.76 + h * 0.16,
      1.1
    );
    for (const t of [poly.normalMap, poly.roughnessMap]) { t.repeat.set(6, 6); t.needsUpdate = true; }
    // Glove cloth is fine-weave: tile it hard or it reads as a golf ball.
    for (const t of [fabric.normalMap, fabric.roughnessMap]) { t.repeat.set(16, 16); t.needsUpdate = true; }

    const std = (o) => new THREE.MeshStandardMaterial(o);
    const m = {
      // Phosphate / parkerised: a real metal, but a dull matte one.
      steel: std({
        color: A.steel, metalness: 1.0, roughness: A.steelRough,
        normalMap: micro, normalScale: new THREE.Vector2(0.4, 0.4), envMapIntensity: 0.75,
      }),
      // Bluing worn through: bright, but bright STEEL, not chrome. At 0xb2b8c0
      // with roughness 0.24 the magwell lip and the mount screws clipped to
      // white in daylight and read as plastic highlights stuck on the gun.
      wear: std({
        color: 0x8c9199, metalness: 1.0, roughness: 0.33,
        normalMap: micro, normalScale: new THREE.Vector2(0.3, 0.3), envMapIntensity: 0.95,
      }),
      dark: std({ color: 0x080a0c, metalness: 0.6, roughness: 0.85, envMapIntensity: 0.35 }),
      poly: std({
        color: A.furniture, metalness: 0.02, roughness: A.furnitureRough,
        normalMap: poly.normalMap, roughnessMap: poly.roughnessMap,
        normalScale: new THREE.Vector2(0.55, 0.55), envMapIntensity: 0.6,
      }),
      // Matte furniture.
      //
      // `poly` carries the moulding stipple in a roughness map that tops out
      // at 0.64, which is fine on a rifle handguard 300 mm away and disastrous
      // on a shotgun stock 150 mm from the lens: the specular lobe is still
      // tight enough to break into hot marbled blobs, and that is exactly what
      // the ID probe caught filling the bottom of the M870's sight picture.
      // Anything that sits close to the camera gets this instead — the same
      // stipple normal, but a flat high roughness and almost no sky in it.
      matte: std({
        color: A.furniture, metalness: 0.0, roughness: 0.88,
        normalMap: poly.normalMap,
        normalScale: new THREE.Vector2(0.35, 0.35), envMapIntensity: 0.28,
      }),
      rubber: std({
        color: 0x191c20, metalness: 0.0, roughness: 0.78,
        normalMap: poly.normalMap, normalScale: new THREE.Vector2(0.9, 0.9), envMapIntensity: 0.3,
      }),
      anod: std({
        color: 0x26292d, metalness: 0.85, roughness: 0.34,
        normalMap: micro, normalScale: new THREE.Vector2(0.2, 0.2), envMapIntensity: 1.0,
      }),
      // Gloves and sleeve are the only large diffuse areas on the viewmodel,
      // so they set how "lit" the whole thing reads. Authored dark they went
      // to pure black against a dusk street: an 0x30 albedo is 0.029 linear,
      // which no amount of key light rescues. These are mid-greys that still
      // read as black nomex once the scene grade is on top.
      // Nomex is matte. Authored at roughness 0.80 with envMapIntensity 0.7 the
      // gloves picked up a broad specular sheen off the sky probe, and on the
      // weapons whose firing hand sits 180 mm from the lens — the shotgun and
      // the pistol — that sheen blew the back of the hand to near white and
      // turned it into an unreadable marbled lump. Measured: median pixel 28,
      // 95th percentile 189. The albedo was never the problem; the gloss was.
      glove: std({
        color: 0x35393f, metalness: 0.0, roughness: 0.93,
        normalMap: fabric.normalMap, roughnessMap: fabric.roughnessMap,
        normalScale: new THREE.Vector2(0.22, 0.22), envMapIntensity: 0.34,
      }),
      pad: std({
        color: 0x3a4048, metalness: 0.04, roughness: 0.74,
        normalMap: poly.normalMap, normalScale: new THREE.Vector2(0.5, 0.5), envMapIntensity: 0.34,
      }),
      // Nomex combat-shirt sleeve. Authored at 0x4a4e41 it was the brightest
      // thing in frame — a lit olive tube reading as a bare arm in a sock.
      // Ranger green sits well below the gloves in value and lets the glove
      // and the weapon own the contrast.
      sleeve: std({
        color: 0x2c2f27, metalness: 0.0, roughness: 0.94,
        normalMap: fabric.normalMap, roughnessMap: fabric.roughnessMap,
        normalScale: new THREE.Vector2(0.5, 0.5), envMapIntensity: 0.6,
      }),
      brass: std({ color: 0xb08a3c, metalness: 1.0, roughness: 0.3, envMapIntensity: 1.0 }),
    };
    return m;
  }

  _build() {
    const A = this.art;
    const M = this._materials();
    this.mats = M;
    const g = new THREE.Group();
    this.model = g;

    // Optional parts. Every family fills in what it has; update() and the
    // reload animation guard on these being null rather than assuming an AR.
    this.bolt = null; this.boltCatchMesh = null; this.magReleaseMesh = null;
    this.charge = null; this.chargeLever = null; this.triggerMesh = null;
    this.pumpNode = null; this.warhead = null; this.hammerNode = null;
    this.slideStop = null;
    this._boltTravel = 0.030;

    if (A.family === 'knife') this._buildKnife(g);
    else if (A.family === 'pistol') this._buildPistol(g);
    else if (A.family === 'shotgun') this._buildShotgun(g);
    else if (A.family === 'launcher') this._buildLauncher(g);
    else {
      this._upper(g);
      this._lower(g);
      this._handguard(g);
      this._barrel(g);
      this._stock(g);
      this._magazine(g);
      this._optic(g);
    }
    this._hands(g);

    g.scale.setScalar(A.scale);
    g.traverse((o) => { if (o.isMesh) { o.castShadow = false; o.receiveShadow = false; } });
    this.group.add(g);

    // ADS alignment: put the sight line exactly on the screen centre. The
    // viewmodel camera is the identity, so "screen centre" is x = y = 0.
    this.group.updateMatrixWorld(true);
    const s = this.sightNode.getWorldPosition(_v).clone();
    const rear = this.rearNode.getWorldPosition(new THREE.Vector3());

    // Eye relief, solved rather than tuned. Three hand-picked adsEye constants
    // drifted apart until the MK14 sat 124 mm from a 62 mm optic and filled
    // four fifths of the frame. Instead: pick how much of the screen height the
    // ocular should occupy, and put the eye at the distance that produces it.
    const half = Math.tan(THREE.MathUtils.degToRad(VM_FOV) / 2);
    const frac = A.optic === 'scope' ? A.adsFill * 1.18 : A.adsFill;
    const relief = (this.opticRearR * A.scale) / (half * frac);
    const eyeZ = rear.z + relief;
    // group.z = -eyeZ puts the model-space point `eyeZ` on the camera origin.
    this.adsPose.pos.set(-s.x, -s.y, -eyeZ);
    this.art.adsEye = eyeZ - s.z;

    this._scopeOverlay();
  }

  /**
   * Scope overlay for magnified optics.
   *
   * A rasteriser is a pinhole camera: the only way to see "through" a modelled
   * scope tube is the solid angle its bore subtends, which for a 30 mm tube at
   * any believable eye relief is a keyhole a hundred pixels across. Real optics
   * cheat with magnification and an exit pupil; games cheat by zooming the
   * world camera and masking the frame. That is what this is — at full ADS the
   * weapon is hidden and a camera-locked black surround with a circular window
   * takes over, which is also why the DMR carries `fovZoom: 0.5`.
   *
   * It lives on the scene rather than on `this.group` because it must not
   * inherit weapon sway, and it is rebuilt per weapon so switching guns cannot
   * leak a stale overlay.
   */
  _scopeOverlay() {
    const scene = this.engine.viewmodelScene;
    if (scene.__vmScope) {
      scene.remove(scene.__vmScope);
      scene.__vmScope.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
      scene.__vmScope = null;
    }
    this.scopeOverlay = null;
    this._scopeFades = null;
    if (this.art.optic !== 'scope') return;

    const Z = 0.060;
    const half = Math.tan(THREE.MathUtils.degToRad(VM_FOV) / 2);
    const hole = this.art.scopeFill * Z * half;

    const grp = new THREE.Group();
    grp.position.z = -Z;
    grp.renderOrder = 50;
    const fades = [];
    const flat = (color, opacity) => new THREE.MeshBasicMaterial({
      color, transparent: true, opacity, depthTest: false, depthWrite: false,
      toneMapped: false, side: THREE.DoubleSide,
    });
    const add = (geo, color, opacity, order) => {
      const m = new THREE.Mesh(geo, flat(color, opacity));
      m.renderOrder = 50 + order;
      grp.add(m);
      fades.push({ mat: m.material, base: opacity });
      return m;
    };

    // Surround. Outer radius is deliberately absurd so no aspect ratio can
    // expose a corner.
    add(new THREE.RingGeometry(hole, 0.60, 96, 1), 0x000000, 1.0, 0);
    // Scope shadow: the soft dark falloff just inside the field stop. It is
    // drawn OVER the reticle so the posts fade out at the rim like real glass
    // rather than running on into the black surround.
    const shadow = add(new THREE.RingGeometry(hole * 0.80, hole, 96, 1), 0x000000, 0.9, 2);
    shadow.material.alphaMap = radialRamp(256, 0.80);

    // Duplex reticle: heavy posts thinning to a fine cross, which is what makes
    // a reticle readable against both sky and rubble.
    // Line weights in screen terms: a duplex is a ~1.5 px crosshair thickening
    // to a ~5 px post. Authored in world units they have to be derived from the
    // plane distance or they land at bargepole width.
    const px = (2 * Z * half) / 1080;
    const fine = px * 1.6, heavy = px * 5.0;
    const gap = hole * 0.045, thin = hole * 0.55, post = hole * 0.995;
    for (let i = 0; i < 4; i++) {
      const vert = i % 2 === 1;
      const sgn = i < 2 ? -1 : 1;
      // fine section
      const fl = thin - gap;
      const f = add(new THREE.PlaneGeometry(vert ? fine : fl, vert ? fl : fine), 0x07090c, 0.92, 1);
      f.position[vert ? 'y' : 'x'] = sgn * (gap + fl / 2);
      // heavy post
      const hl = post - thin;
      const h = add(new THREE.PlaneGeometry(vert ? heavy : hl, vert ? hl : heavy), 0x07090c, 0.98, 1);
      h.position[vert ? 'y' : 'x'] = sgn * (thin + hl / 2);
    }
    // Illuminated centre dot.
    const dot = add(new THREE.CircleGeometry(hole * 0.012, 12), 0xff3a20, 0.95, 3);
    dot.material.blending = THREE.AdditiveBlending;

    grp.visible = false;
    scene.add(grp);
    scene.__vmScope = grp;
    this.scopeOverlay = grp;
    this._scopeFades = fades;
  }

  /** Upper receiver, ejection port, brass deflector, forward assist, handle. */
  _upper(g) {
    const A = this.art, M = this.mats;
    const L = A.upperLen, W = A.upperW, H = A.upperH;
    const zc = L / 2 - 0.006;

    if (A.receiverRound) {
      // Stamped tubular receiver. Lathes run forward on -Z, so spin it 180.
      const body = new THREE.Mesh(tube([
        [0.0, 0.0], [W * 0.42, 0.004], [W * 0.5, 0.014], [W * 0.5, L - 0.014],
        [W * 0.42, L - 0.004], [0.0, L],
      ], 18), M.steel);
      body.rotation.y = Math.PI;
      body.position.set(0, H * 0.06, -0.006);
      g.add(body);
      const flat = new THREE.Mesh(box(W * 0.6, 0.008, L * 0.92, 0.0018), M.steel);
      flat.position.set(0, W * 0.5 + 0.001, zc);
      g.add(flat);
    } else {
      const body = new THREE.Mesh(box(W, H, L, 0.0035), M.steel);
      body.position.set(0, H / 2 - 0.021, zc);
      g.add(body);
      // Machined relief along the flanks catches a highlight.
      const relief = new THREE.Mesh(box(W + 0.0012, 0.010, L * 0.72, 0.0014), M.steel);
      relief.position.set(0, -0.012, zc - 0.01);
      g.add(relief);
    }

    const top = A.receiverRound ? A.upperW * 0.5 + 0.005 : A.upperH - 0.021;
    this.railTop = top;

    // Continuous top rail from the charging handle forward over the handguard.
    const railLen = A.upperLen * 0.86 + A.hgLen * (A.hgStyle === 'ribbed' ? 0.22 : 0.46);
    this._rail(g, railLen, top, A.upperLen - 0.012 - railLen / 2);

    // Ejection port: a recess, a hinged cover hanging open, a brass deflector.
    const side = A.upperW / 2;
    const port = new THREE.Mesh(box(0.008, 0.024, 0.052, 0.0012), M.dark);
    port.position.set(side - 0.002, -0.002, 0.062);
    g.add(port);
    const rim = new THREE.Mesh(box(0.004, 0.030, 0.058, 0.0012), M.wear);
    rim.position.set(side - 0.004, -0.002, 0.062);
    g.add(rim);
    const cover = new THREE.Mesh(box(0.006, 0.026, 0.050, 0.0012), M.steel);
    cover.position.set(side + 0.010, -0.020, 0.062);
    cover.rotation.z = -1.05;
    g.add(cover);
    const defl = new THREE.Mesh(box(0.012, 0.018, 0.026, 0.003), M.steel);
    defl.position.set(side + 0.001, 0.006, 0.105);
    defl.rotation.z = 0.5;
    g.add(defl);

    // Bolt carrier seen through the port — this is what actually reciprocates.
    this.bolt = new THREE.Mesh(box(0.020, 0.020, 0.046, 0.002), M.wear);
    this.bolt.position.set(side - 0.010, -0.001, 0.060);
    g.add(this.bolt);
    this._boltZ = this.bolt.position.z;

    // Forward assist boss.
    const fa = new THREE.Mesh(tube([
      [0, 0], [0.007, 0], [0.007, 0.014], [0.010, 0.014], [0.010, 0.020], [0, 0.020],
    ], 12), M.steel);
    fa.rotation.y = -Math.PI / 2;
    fa.position.set(side - 0.004, 0.014, A.upperLen - 0.030);
    g.add(fa);

    if (A.chargeStyle === 'ar') {
      // AR charging handle: T-latch on the left, does not cycle when firing.
      const ch = new THREE.Group();
      const shaft = new THREE.Mesh(box(0.026, 0.009, 0.048, 0.0015), M.steel);
      shaft.position.set(0, 0, -0.006);
      ch.add(shaft);
      const bar = new THREE.Mesh(box(0.052, 0.011, 0.014, 0.0022), M.wear);
      bar.position.set(-0.010, 0, 0.020);
      ch.add(bar);
      const latch = new THREE.Mesh(box(0.020, 0.008, 0.011, 0.002), M.wear);
      latch.position.set(-0.030, 0, 0.020);
      ch.add(latch);
      ch.position.set(0, top - 0.004, A.upperLen - 0.012);
      g.add(ch);
      this.charge = ch;
      this.chargeLever = null;
    } else {
      // HK cocking tube: the lever runs in its own tube on the upper left,
      // forward of the receiver and above the handguard.
      const tx = -A.upperW * 0.42, ty = A.upperW * 0.30;
      const t = new THREE.Mesh(tube([
        [0, 0], [0.011, 0.0015], [0.011, A.hgLen * 0.98],
        [0.008, A.hgLen * 0.98 + 0.002], [0, A.hgLen * 0.98 + 0.003],
      ], 12), M.steel);
      t.position.set(tx, ty, -0.004);
      g.add(t);

      // Cocking-lever support housing at the front of the tube, and the notch
      // in it that the lever rotates UP into to hold the bolt back. Without a
      // notch there is nothing for the HK slap to knock the lever out of, and
      // the whole manual of arms reads as a shrug.
      // Right at the muzzle end of the handguard, where the support hand can
      // actually reach it — behind the hand it is a control nobody can work.
      const cz = -A.hgLen * 0.88;
      const hous = new THREE.Mesh(box(0.026, 0.030, 0.030, 0.003), M.steel);
      hous.position.set(tx, ty + 0.003, cz);
      g.add(hous);
      const notch = new THREE.Mesh(box(0.012, 0.017, 0.008, 0.0009), M.dark);
      notch.position.set(tx - 0.010, ty + 0.013, cz - 0.013);
      notch.rotation.z = 0.62;
      g.add(notch);

      // The lever pivots about the cocking tube's axis: rotation.z = 0 is the
      // forward/down running position, negative swings the paddle up.
      const ch = new THREE.Group();
      const stem = new THREE.Mesh(box(0.026, 0.011, 0.014, 0.002), M.steel);
      stem.position.x = -0.017;
      ch.add(stem);
      const knob = new THREE.Mesh(box(0.016, 0.021, 0.020, 0.004), M.wear);
      knob.position.x = -0.035;
      ch.add(knob);
      ch.position.set(tx, ty, cz - 0.019);
      g.add(ch);
      this.charge = ch;
      this.chargeLever = ch;
    }
    this._chargeZ = this.charge.position.z;

    // Folded back-up rear sight behind the optic.
    const buis = new THREE.Group();
    const leaf = new THREE.Mesh(box(0.020, 0.006, 0.024, 0.0012), M.steel);
    buis.add(leaf);
    const bbase = new THREE.Mesh(box(0.024, 0.008, 0.030, 0.0012), M.steel);
    bbase.position.y = -0.006;
    buis.add(bbase);
    buis.position.set(0, top + 0.011, A.upperLen - 0.058);
    g.add(buis);
  }

  /** Picatinny strip: base plus cross ribs, merged into a single draw call. */
  _rail(g, len, y, zCentre) {
    const M = this.mats;
    const base = new THREE.Mesh(box(0.021, 0.006, len, 0.0012), M.steel);
    base.position.set(0, y + 0.003, zCentre);
    g.add(base);
    const n = Math.max(3, Math.floor(len / 0.0104));
    const rib = box(0.0212, 0.0058, 0.0062, 0.0011);
    const xf = [];
    for (let i = 0; i < n; i++) xf.push([0, 0, -len / 2 + 0.006 + i * (len - 0.012) / (n - 1)]);
    const ribs = new THREE.Mesh(scatter(rib, xf), M.steel);
    ribs.position.set(0, y + 0.0085, zCentre);
    g.add(ribs);
  }

  /** Lower receiver: magwell, trigger group, controls, pistol grip. */
  _lower(g) {
    const A = this.art, M = this.mats;
    const W = A.upperW * 0.94;
    const bottom = -0.021;

    const body = new THREE.Mesh(box(W, 0.044, 0.150, 0.004), M.steel);
    body.position.set(0, bottom - 0.022, 0.094);
    g.add(body);

    // Flared magwell, with the bluing worn off the lip where mags scrape in.
    const magwell = new THREE.Mesh(box(W, 0.050, 0.072, 0.0035), M.steel);
    magwell.position.set(0, bottom - 0.030, 0.036);
    g.add(magwell);
    const lip = new THREE.Mesh(box(W + 0.0016, 0.008, 0.074, 0.0016), M.wear);
    lip.position.set(0, bottom - 0.052, 0.036);
    g.add(lip);

    // Trigger guard.
    const gbar = new THREE.Mesh(box(0.010, 0.006, 0.046, 0.0016), M.steel);
    gbar.position.set(0, bottom - 0.070, 0.140);
    g.add(gbar);
    const gpost = new THREE.Mesh(box(0.010, 0.024, 0.007, 0.0014), M.steel);
    gpost.position.set(0, bottom - 0.058, 0.119);
    gpost.rotation.x = -0.18;
    g.add(gpost);

    const trg = new THREE.Mesh(box(0.007, 0.021, 0.006, 0.0012), M.wear);
    trg.position.set(0, bottom - 0.043, 0.136);
    trg.rotation.x = 0.12;
    g.add(trg);
    this.triggerMesh = trg;
    this._trgZ = trg.position.z;

    // Ambidextrous selector, both sides, worn to bright steel.
    for (const sx of [-1, 1]) {
      const pin = new THREE.Mesh(tube([[0, 0], [0.006, 0], [0.006, 0.006], [0, 0.006]], 12), M.wear);
      pin.rotation.y = -sx * Math.PI / 2;
      pin.position.set(sx * (W / 2 - 0.001), bottom - 0.014, 0.152);
      g.add(pin);
      const lev = new THREE.Mesh(box(0.005, 0.010, 0.028, 0.0012), M.wear);
      lev.position.set(sx * (W / 2 + 0.003), bottom - 0.019, 0.164);
      lev.rotation.x = 0.55;
      g.add(lev);
    }

    // Mag release (right) and bolt catch (left).
    const mr = new THREE.Mesh(tube([[0, 0], [0.007, 0], [0.007, 0.005], [0, 0.005]], 12), M.wear);
    mr.rotation.y = -Math.PI / 2;
    mr.position.set(W / 2 - 0.001, bottom - 0.012, 0.070);
    g.add(mr);
    this.magReleaseMesh = mr;
    const bc = new THREE.Mesh(box(0.006, 0.016, 0.024, 0.0012), M.wear);
    bc.position.set(-(W / 2 + 0.001), bottom - 0.012, 0.056);
    g.add(bc);
    this.boltCatchMesh = bc;

    // Pistol grip: tapered, with a bottom flare and a rubberised front strap.
    const grip = new THREE.Group();
    const gm = new THREE.Mesh(box(0.030, A.gripLen, 0.044, 0.006), M.poly);
    grip.add(gm);
    const flare = new THREE.Mesh(box(0.036, 0.010, 0.050, 0.004), M.poly);
    flare.position.y = -A.gripLen / 2 - 0.002;
    grip.add(flare);
    const strap = new THREE.Mesh(box(0.028, A.gripLen * 0.8, 0.006, 0.0022), M.rubber);
    strap.position.set(0, 0.004, -0.022);
    grip.add(strap);
    const beaver = new THREE.Mesh(box(0.030, 0.014, 0.030, 0.004), M.poly);
    beaver.position.set(0, A.gripLen / 2 + 0.004, 0.014);
    grip.add(beaver);
    grip.position.set(0, bottom - 0.036 - A.gripLen / 2, 0.176);
    grip.rotation.x = A.gripAngle;
    g.add(grip);
    this.gripNode = grip;
  }

  /** Handguard: M-LOK, quad rail or ribbed polymer depending on the spec. */
  _handguard(g) {
    const A = this.art, M = this.mats;
    const L = A.hgLen, R = A.hgR;
    const z0 = -0.004;
    const mat = A.hgStyle === 'ribbed' ? M.poly : M.steel;

    if (A.hgStyle === 'quad') {
      const bmesh = new THREE.Mesh(box(R * 1.8, R * 1.8, L, 0.003), mat);
      bmesh.position.set(0, 0.001, z0 - L / 2);
      g.add(bmesh);
      for (const [ax, ay] of [[1, 0], [-1, 0], [0, -1]]) {
        const base = box(0.020, 0.005, L * 0.92, 0.001);
        const n = Math.floor(L / 0.0106);
        const rg = box(0.0202, 0.0052, 0.0062, 0.001);
        const ribs = [];
        for (let i = 0; i < n; i++) ribs.push([0, 0, -L * 0.46 + i * (L * 0.92) / (n - 1)]);
        const rm = new THREE.Mesh(merged([base, scatter(rg, ribs).translate(0, 0.005, 0)]), M.steel);
        rm.position.set(ax * R * 0.92, ay * R * 0.92 + 0.001, z0 - L / 2);
        rm.rotation.z = ax ? -ax * Math.PI / 2 : Math.PI;
        g.add(rm);
      }
    } else {
      const hg = new THREE.Mesh(tube([
        [0, 0.0], [R * 0.5, 0.0], [R * 0.86, 0.006], [R, 0.013],
        [R, L - 0.010], [R * 0.80, L - 0.002], [0, L - 0.001],
      ], A.hgSeg, Math.PI / A.hgSeg), mat);
      hg.position.set(0, 0, z0);
      g.add(hg);

      if (A.hgStyle === 'mlok') {
        // M-LOK: rows of slots at 3, 6 and 9 o'clock.
        const rows = [];
        for (let i = 0; i < 5; i++) rows.push([0, 0, -0.030 - i * 0.040]);
        for (const [ax, ay] of [[1, 0], [-1, 0], [0, -1]]) {
          const s = new THREE.Mesh(scatter(box(0.008, 0.011, 0.030, 0.0012), rows), M.dark);
          s.position.set(ax * (R - 0.0032), ay * (R - 0.0032), z0);
          s.rotation.z = ax ? 0 : Math.PI / 2;
          g.add(s);
        }
      } else {
        // Ribbed polymer furniture: mould lines running fore-aft.
        const ribG = box(0.004, 0.004, L * 0.8, 0.0012);
        const xf = [];
        for (let i = 0; i < 9; i++) {
          const a = -Math.PI * 0.75 + i * (Math.PI * 1.5) / 8;
          xf.push([Math.cos(a) * (R + 0.0008), Math.sin(a) * (R + 0.0008), 0, a - Math.PI / 2]);
        }
        const ribs = new THREE.Mesh(scatter(ribG, xf), M.poly);
        ribs.position.set(0, 0, z0 - L / 2);
        g.add(ribs);
      }
    }

    // Front sling QD socket + hand stop.
    const stop = new THREE.Mesh(box(0.020, 0.016, 0.020, 0.003), M.poly);
    stop.position.set(0, -(A.hgR + 0.008), z0 - L * 0.78);
    g.add(stop);
    const qd = new THREE.Mesh(tube([[0.004, 0], [0.008, 0], [0.008, 0.006], [0.004, 0.006]], 10), M.wear);
    qd.rotation.y = Math.PI / 2;
    qd.position.set(-(A.hgR + 0.002), -0.010, z0 - L * 0.86);
    g.add(qd);
  }

  /** Barrel, gas block, muzzle device, muzzle node. */
  _barrel(g) {
    const A = this.art, M = this.mats;
    const z0 = -0.004 - A.hgLen;
    const R = A.barrelR;
    const ex = A.barrelExposed;

    const bar = new THREE.Mesh(tube([
      [0, 0], [R * 1.25, 0], [R * 1.25, 0.008], [R, 0.013],
      [R, ex - 0.004], [R * 1.1, ex - 0.002], [R * 1.1, ex], [0, ex],
    ], 16), M.steel);
    bar.position.set(0, 0, z0 + 0.004);
    g.add(bar);

    // Low-profile gas block sits at the handguard mouth.
    const gb = new THREE.Mesh(box(R * 2.2, R * 2.4, 0.022, 0.002), M.steel);
    gb.position.set(0, R * 0.35, z0 - 0.004);
    g.add(gb);

    let zEnd = z0 - ex;
    if (A.muzzle === 'a2') {
      const fh = new THREE.Mesh(tube([
        [0, 0], [R * 1.05, 0], [R * 1.5, 0.004], [R * 1.55, 0.008],
        [R * 1.5, 0.012], [R * 1.62, 0.016], [R * 1.62, 0.052],
        [R * 1.70, 0.054], [R * 1.70, 0.058], [R * 0.62, 0.058],
      ], 18), M.steel);
      fh.position.set(0, 0, zEnd + 0.001);
      g.add(fh);
      // Five ports, closed on the bottom like the real A2.
      const portG = box(0.0035, 0.006, 0.026, 0.0008);
      const xf = [];
      for (let i = 0; i < 5; i++) {
        const a = Math.PI * 0.28 + i * (Math.PI * 1.44) / 4;
        xf.push([Math.cos(a) * R * 1.5, Math.sin(a) * R * 1.5, 0, a + Math.PI / 2]);
      }
      const ports = new THREE.Mesh(scatter(portG, xf), M.dark);
      ports.position.set(0, 0, zEnd - 0.030);
      g.add(ports);
      zEnd -= 0.058;
    } else if (A.muzzle === 'brake') {
      const br = new THREE.Mesh(tube([
        [0, 0], [R * 1.1, 0], [R * 1.5, 0.005], [R * 1.5, 0.062],
        [R * 1.3, 0.064], [R * 0.6, 0.064],
      ], 18), M.steel);
      br.position.set(0, 0, zEnd + 0.001);
      g.add(br);
      const slotG = box(0.006, R * 1.4, 0.007, 0.0009);
      const xf = [];
      for (let i = 0; i < 3; i++) for (const sx of [-1, 1]) xf.push([sx * R * 1.2, 0, -0.016 - i * 0.014]);
      const slots = new THREE.Mesh(scatter(slotG, xf), M.dark);
      slots.position.set(0, 0, zEnd);
      g.add(slots);
      zEnd -= 0.064;
    } else {
      // MP5-style three-lug barrel end.
      const lugs = new THREE.Mesh(tube([
        [0, 0], [R * 1.15, 0], [R * 1.15, 0.026], [R * 0.7, 0.026],
      ], 14), M.steel);
      lugs.position.set(0, 0, zEnd + 0.001);
      g.add(lugs);
      const lugG = box(0.005, 0.008, 0.010, 0.001);
      const xf = [];
      for (let i = 0; i < 3; i++) {
        const a = Math.PI / 2 + i * (Math.PI * 2) / 3;
        xf.push([Math.cos(a) * R * 1.15, Math.sin(a) * R * 1.15, 0, a]);
      }
      const l = new THREE.Mesh(scatter(lugG, xf), M.wear);
      l.position.set(0, 0, zEnd - 0.008);
      g.add(l);
      zEnd -= 0.026;
    }

    // Dark crown so the bore reads as a hole, not a capped cylinder.
    const crown = new THREE.Mesh(tube([
      [R * 0.62, 0], [R * 0.62, 0.020], [0, 0.022],
    ], 14), M.dark);
    crown.position.set(0, 0, zEnd + 0.002);
    g.add(crown);

    this.muzzle = new THREE.Object3D();
    this.muzzle.position.set(0, 0, zEnd - 0.006);
    g.add(this.muzzle);
  }

  /** Buffer tube, collapsible stock with detents, butt pad, sling loop. */
  _stock(g) {
    const A = this.art, M = this.mats;
    const z0 = A.upperLen - 0.010;

    if (A.stock === 'fixed') {
      const body = new THREE.Mesh(box(0.040, 0.052, A.stockLen, 0.008), M.poly);
      body.position.set(0, -0.014, z0 + A.stockLen / 2);
      g.add(body);
      const comb = new THREE.Mesh(box(0.034, 0.020, A.stockLen * 0.8, 0.006), M.poly);
      comb.position.set(0, 0.012 + A.combRise, z0 + A.stockLen * 0.52);
      g.add(comb);
      const pad = new THREE.Mesh(box(0.038, 0.070, 0.014, 0.005), M.rubber);
      pad.position.set(0, -0.014, z0 + A.stockLen + 0.006);
      g.add(pad);
      return;
    }

    // Buffer tube + castle nut.
    // Lathes run forward on -Z; the buffer tube runs rearward, so spin it 180.
    const bt = new THREE.Mesh(tube([
      [0, 0], [0.0175, 0.0], [0.0175, 0.012], [0.0163, 0.014],
      [0.0163, A.stockLen + 0.062], [0.0140, A.stockLen + 0.064], [0, A.stockLen + 0.064],
    ], 16), M.steel);
    bt.rotation.y = Math.PI;
    bt.position.set(0, -0.006, z0);
    g.add(bt);

    // Adjustment detent notches on the underside.
    const det = box(0.006, 0.004, 0.006, 0.0009);
    const xf = [];
    for (let i = 0; i < 6; i++) xf.push([0, 0, 0.026 + i * 0.019]);
    const dets = new THREE.Mesh(scatter(det, xf), M.dark);
    dets.position.set(0, -0.006 - 0.0165, z0);
    g.add(dets);

    // Sliding stock body.
    const st = new THREE.Group();
    const shell = new THREE.Mesh(box(0.044, 0.056, A.stockLen, 0.007), M.poly);
    st.add(shell);
    const comb = new THREE.Mesh(box(0.030, 0.018, A.stockLen * 0.72, 0.006), M.poly);
    comb.position.set(0, 0.032 + A.combRise, -0.006);
    st.add(comb);
    if (A.stock === 'precision') {
      const cheek = new THREE.Mesh(box(0.034, 0.014, A.stockLen * 0.6, 0.005), M.rubber);
      cheek.position.set(0, 0.048 + A.combRise, -0.004);
      st.add(cheek);
      for (const sx of [-1, 1]) {
        const post = new THREE.Mesh(tube([[0, 0], [0.004, 0], [0.004, 0.024], [0, 0.024]], 10), M.wear);
        post.rotation.x = Math.PI / 2;
        post.position.set(sx * 0.012, 0.036, -0.004);
        st.add(post);
      }
    }
    const pad = new THREE.Mesh(box(0.040, 0.074, 0.016, 0.005), M.rubber);
    pad.position.set(0, -0.006, A.stockLen / 2 + 0.008);
    st.add(pad);
    const lever = new THREE.Mesh(box(0.014, 0.016, 0.036, 0.003), M.poly);
    lever.position.set(0, -0.032, -0.010);
    st.add(lever);
    const loop = new THREE.Mesh(tube([[0.006, 0], [0.010, 0], [0.010, 0.005], [0.006, 0.005]], 10), M.wear);
    loop.rotation.y = Math.PI / 2;
    loop.position.set(-0.023, -0.018, A.stockLen * 0.3);
    st.add(loop);
    st.position.set(0, -0.004, z0 + 0.052 + A.stockLen / 2);
    g.add(st);
    this.stockNode = st;
  }

  /** Magazine: curved body, floor plate, witness holes, ribs. */
  _magazine(g) {
    const A = this.art, M = this.mats;
    const grp = new THREE.Group();
    const segs = 4;
    const segLen = A.magLen / segs;
    let y = -0.020, z = 0, ang = 0;
    for (let i = 0; i < segs; i++) {
      ang = (A.magCurve * i) / segs;
      const w = A.magW * (1 - i * 0.012);
      const s = new THREE.Mesh(box(w, segLen * 1.06, A.magD * (1 - i * 0.02), 0.0028), M.poly);
      s.position.set(0, y - segLen / 2, z);
      s.rotation.x = -ang;
      grp.add(s);
      y -= Math.cos(ang) * segLen;
      z -= Math.sin(ang) * segLen;
      if (i === segs - 1) {
        const plate = new THREE.Mesh(box(A.magW + 0.004, 0.008, A.magD * 0.94, 0.0022), M.wear);
        plate.position.set(0, y + 0.002, z);
        plate.rotation.x = -ang;
        grp.add(plate);
      }
    }
    // Feed lips.
    const lips = new THREE.Mesh(box(A.magW + 0.002, 0.010, A.magD * 0.98, 0.0018), M.wear);
    lips.position.set(0, -0.018, 0);
    grp.add(lips);
    if (A.witness) {
      const hole = box(0.0035, 0.006, 0.006, 0.0008);
      const xf = [];
      for (let i = 0; i < A.witness; i++) xf.push([0, -0.048 - i * 0.030, 0]);
      for (const sx of [-1, 1]) {
        const hs = new THREE.Mesh(scatter(hole.clone(), xf), M.dark);
        hs.position.set(sx * (A.magW / 2 - 0.001), 0, 0);
        grp.add(hs);
      }
      hole.dispose();
    }
    grp.position.set(0, -0.048, 0.036);
    g.add(grp);
    this.magazine = grp;
    this._magHome = grp.position.clone();
  }

  /** Optic: riser/rings, body, lenses, turrets, reticle. */
  _optic(g) {
    const A = this.art, M = this.mats;
    if (A.optic === 'irons') { this._ironSights(g); return; }
    const grp = new THREE.Group();
    const railY = this.railTop + 0.011;
    const scope = A.optic === 'scope';
    // Real dimensions matter here because the optic is the ONE object the
    // player stares at. A 30 mm scope tube is R = 0.015 with a 40 mm ocular
    // bell; the old 0.0215/0.155 was a 62 mm tube 155 mm long, which at any
    // sane eye relief swallowed half the screen as a black donut.
    const R = scope ? 0.0150 : 0.0195;
    const len = scope ? 0.118 : 0.062;

    // Riser / rings.
    //
    // Everything below is measured DOWN from the tube's underside. The mount
    // used to be authored from the tube's centreline, so the riser post ran up
    // to y = 0 and stood squarely in the middle of the sight picture — that is
    // the black slab that ate the bottom half of every optic. A mount that
    // reaches the bore axis is a mount you cannot see through.
    const tubeBottom = -R * (scope ? 1.02 : 1.00);
    const mountY = -(A.riser + R * (scope ? 0.9 : 0.55));  // grp-space rail top
    if (scope) {
      // Rings are annular. A solid box "ring" is a wall across the tube.
      for (const rz of [-len * 0.28, len * 0.28]) {
        const ring = new THREE.Mesh(tube([
          [R * 1.02, 0], [R * 1.28, 0], [R * 1.28, 0.020], [R * 1.02, 0.020],
        ], 20), M.anod);
        ring.position.set(0, 0, rz - 0.010);
        grp.add(ring);
        const foot = new THREE.Mesh(box(0.026, tubeBottom - mountY, 0.024, 0.0026), M.anod);
        foot.position.set(0, (tubeBottom + mountY) / 2, rz);
        grp.add(foot);
        const screw = new THREE.Mesh(tube([[0, 0], [0.004, 0], [0.004, 0.005], [0, 0.005]], 8), M.wear);
        screw.rotation.y = Math.PI / 2;
        screw.position.set(-0.014, tubeBottom + 0.004, rz);
        grp.add(screw);
      }
      const rail = new THREE.Mesh(box(0.030, 0.009, len * 0.80, 0.0022), M.anod);
      rail.position.set(0, mountY + 0.0045, 0);
      grp.add(rail);
    } else {
      const base = new THREE.Mesh(box(0.028, 0.009, 0.048, 0.0022), M.anod);
      base.position.set(0, mountY + 0.0045, 0.004);
      grp.add(base);
      const postH = Math.max(0.004, tubeBottom - (mountY + 0.009));
      const post = new THREE.Mesh(box(0.024, postH, 0.030, 0.0025), M.anod);
      post.position.set(0, mountY + 0.009 + postH / 2, 0.004);
      grp.add(post);
      for (const sx of [-1, 1]) {
        const nut = new THREE.Mesh(tube([[0, 0], [0.005, 0], [0.005, 0.006], [0, 0.006]], 6), M.wear);
        nut.rotation.y = -sx * Math.PI / 2;
        nut.position.set(sx * 0.013, mountY + 0.006, 0.004);
        grp.add(nut);
      }
    }

    // Body — a hollow shell, so you actually look *through* the sight.
    const bodyProf = scope ? [
      [R * 1.16, -len / 2], [R * 1.42, -len / 2 + 0.002], [R * 1.42, -len / 2 + 0.030],
      [R * 1.05, -len / 2 + 0.042], [R * 0.99, -len / 2 + 0.054],
      [R * 0.99, len / 2 - 0.062], [R * 1.06, len / 2 - 0.054],
      [R * 1.06, len / 2 - 0.030], [R * 1.34, len / 2 - 0.020],
      [R * 1.34, len / 2 - 0.002], [R * 1.16, len / 2],
      [R * 0.90, len / 2 - 0.003], [R * 0.90, -len / 2 + 0.003], [R * 1.16, -len / 2],
    ] : [
      [R * 0.90, -len / 2], [R * 1.06, -len / 2], [R * 1.06, -len / 2 + 0.004],
      [R * 0.96, -len / 2 + 0.006], [R * 0.96, len / 2 - 0.008],
      [R * 1.08, len / 2 - 0.005], [R * 1.08, len / 2], [R * 0.90, len / 2],
      [R * 0.86, len / 2 - 0.004], [R * 0.86, -len / 2 + 0.004], [R * 0.90, -len / 2],
    ];
    const body = new THREE.Mesh(tube(bodyProf.map(([r, z]) => [r, -z]), 22), M.anod);
    body.material = M.anod.clone();
    body.material.side = THREE.DoubleSide;
    grp.add(body);

    const frontZ = -len / 2 + (scope ? 0.004 : 0.006);
    const rearZ = len / 2 - (scope ? 0.006 : 0.008);

    // Coated glass: cheap but the blue rim sells it.
    const glass = new THREE.MeshPhysicalMaterial({
      color: scope ? 0x0d1a24 : 0x102830, roughness: 0.03, metalness: 0.0,
      transparent: true, opacity: scope ? 0.12 : 0.22, depthWrite: false,
      envMapIntensity: 3.4, side: THREE.DoubleSide,
    });
    const objR = scope ? R * 1.30 : R * 0.9;
    const front = new THREE.Mesh(new THREE.CircleGeometry(objR, 24), glass);
    front.position.z = frontZ - 0.001;
    front.renderOrder = 3;
    grp.add(front);
    const rear = new THREE.Mesh(new THREE.CircleGeometry(scope ? R * 1.18 : R * 0.9, 24), glass);
    rear.position.z = rearZ + 0.001;
    rear.renderOrder = 3;
    grp.add(rear);

    this.lensFront = front;
    this.lensRear = rear;
    // No killflash. A honeycomb over the objective is a real accessory, but
    // modelled at viewmodel scale it is a dense grid across the one part of the
    // screen the player is actually reading. The hood alone gives the shape.
    if (!scope) {
      const hood = new THREE.Mesh(tube([
        [objR * 0.98, 0], [objR * 1.13, 0], [objR * 1.13, 0.009], [objR * 0.98, 0.009],
      ], 22), M.anod);
      hood.position.z = frontZ + 0.004;
      grp.add(hood);
    }

    // Turrets + battery cap.
    const turret = (x, y, ry) => {
      const t = new THREE.Mesh(tube([
        [0, 0], [0.0085, 0], [0.0085, 0.009], [0.0075, 0.010], [0.0075, 0.013], [0, 0.013],
      ], 12), M.anod);
      t.rotation.set(0, ry, 0);
      if (!ry) t.rotation.x = Math.PI / 2;
      t.position.set(x, y, scope ? 0.012 : 0.004);
      grp.add(t);
    };
    turret(0, R * 0.95, 0);
    turret(R * 0.95, 0, -Math.PI / 2);
    const cap = new THREE.Mesh(tube([
      [0, 0], [0.010, 0], [0.010, 0.008], [0, 0.008],
    ], 14), M.anod);
    cap.rotation.y = Math.PI / 2;
    cap.position.set(-R * 0.95, 0, scope ? 0.012 : 0.004);
    grp.add(cap);

    // Reticle. The dot lives on the sight line; ADS puts that line on the
    // screen centre so the dot and the crosshair coincide exactly.
    // A magnified optic reticles at the ocular; a red dot at the emitter.
    const sight = new THREE.Object3D();
    sight.position.set(0, 0, scope ? rearZ - 0.006 : frontZ + 0.004);
    grp.add(sight);

    // The ocular plane and its outer radius are what the eye actually sees as
    // "the sight". Eye relief is solved from these in _build so every optic
    // subtends the same share of the screen instead of being hand-tuned.
    const rearNode = new THREE.Object3D();
    rearNode.position.set(0, 0, len / 2);
    grp.add(rearNode);
    this.rearNode = rearNode;
    this.opticRearR = scope ? R * 1.34 : R * 1.08;

    // A 2 MOA dot is a few pixels across. Authored fat it reads as a tomato
    // and covers the target it is meant to be placed on.
    const dot = new THREE.Mesh(
      new THREE.CircleGeometry(scope ? 0.00060 : 0.00092, 14),
      new THREE.MeshBasicMaterial({ color: 0xff3a20, toneMapped: false, transparent: true, depthWrite: false })
    );
    dot.position.copy(sight.position);
    dot.renderOrder = 12;
    grp.add(dot);
    const halo = new THREE.Mesh(
      new THREE.CircleGeometry(scope ? 0.0022 : 0.0034, 16),
      new THREE.MeshBasicMaterial({
        color: 0xff2a10, toneMapped: false, transparent: true, opacity: 0.16,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })
    );
    halo.position.copy(sight.position);
    halo.renderOrder = 11;
    grp.add(halo);
    this.reticleDot = dot;
    this.reticleHalo = halo;

    if (scope) {
      // Duplex crosshair: black posts read against a bright world.
      const post = new THREE.MeshBasicMaterial({ color: 0x05070a, toneMapped: false, transparent: true });
      const th = 0.00055, arm = R * 1.05;
      for (let i = 0; i < 4; i++) {
        const p = new THREE.Mesh(new THREE.PlaneGeometry(i % 2 ? th : arm, i % 2 ? arm : th), post);
        p.position.set(i === 0 ? -arm / 2 - 0.0026 : i === 2 ? arm / 2 + 0.0026 : 0,
          i === 1 ? -arm / 2 - 0.0026 : i === 3 ? arm / 2 + 0.0026 : 0, sight.position.z);
        p.renderOrder = 12;
        grp.add(p);
      }
      // Scope shadow ring.
      const vig = new THREE.Mesh(tube([
        [R * 1.18, 0], [R * 1.55, 0], [R * 1.55, 0.004], [R * 1.18, 0.004],
      ], 24), new THREE.MeshBasicMaterial({ color: 0x02030a, toneMapped: false, side: THREE.DoubleSide }));
      vig.position.z = rearZ + 0.003;
      grp.add(vig);
    }

    grp.position.set(0, railY + (scope ? A.riser + R * 0.9 : A.riser + R * 0.55), -0.030);
    g.add(grp);
    this.optic = grp;
    this.sightNode = sight;
  }

  /**
   * Iron sights — an HK-pattern rotary drum rear and a hooded front post.
   *
   * Unlike an optic, the sight picture is not a thing you look AT but a line you
   * look ALONG: eye, rear aperture and front post have to be collinear. So the
   * rear aperture and the front post tip are authored at exactly the same height,
   * and `sightNode` sits at the rear aperture — aligning that one point to screen
   * centre in _build puts the whole sight line on the centre by construction.
   *
   * Eye relief cannot be solved from aperture size the way it is for an optic: a
   * 3 mm aperture would put the eye 3 cm from the receiver. Irons use a fixed
   * comfortable cheek-weld distance instead, published via `opticRearR` so the
   * shared solver in _build lands on it.
   */
  _ironSights(g) {
    const A = this.art, M = this.mats;
    const grp = new THREE.Group();
    const railY = this.railTop + 0.011;

    // Common sight-line height above the rail, shared by both sights.
    const SIGHT_Y = 0.019;
    const frontZ = -A.hgLen * 0.86;
    const rearZ = A.upperLen - 0.052;

    // NOTE: tube() already lathes along Z (it applies rotateX(-PI/2) itself), so
    // these must NOT be rotated again — doing so lays the sight on its side and
    // turns the aperture into a solid wall from the shooter's view.

    /* ---- rear: HK rotary drum, bored through so you can see the target ---- */
    const rear = new THREE.Group();
    // The bore IS the sight. A solid cylinder here reads as a black roller
    // blocking the whole picture, which is exactly what it did first time.
    //
    // Proportions are a SIGHT PICTURE decision, not a fidelity one. A real HK
    // drum has a small aperture in a fat body, and modelled literally it became
    // a black cog filling the middle of the screen with a pinhole in it — you
    // could not see the target you were aiming at, which is the one thing a
    // sight exists to let you do. Inverted here: a wide aperture in a thin
    // rim. The eye reads a ring, and everything inside it is the world.
    //
    // The rim can be thin because eye relief is FIXED (`_setIrons` derives
    // `opticRearR` from `ironRelief`, and the solver recovers exactly that), so
    // shrinking the body genuinely shrinks it on screen rather than pulling the
    // eye closer to compensate.
    const APERTURE = 0.0074;             // was 0.0036 — the hole you see through
    const RIM = 0.0098;                  // was 0.0134 — outer edge of the ring
    const drum = new THREE.Mesh(tube([
      [APERTURE, 0], [RIM, 0], [RIM, 0.012], [APERTURE, 0.012],
    ], 24), M.steel);
    drum.position.z = -0.006;
    rear.add(drum);
    // Blackened aperture faces: the inner edge the eye actually registers.
    for (const z of [-0.0061, 0.0061]) {
      const face = new THREE.Mesh(
        new THREE.RingGeometry(APERTURE, RIM, 24),
        new THREE.MeshStandardMaterial({ color: 0x0c0e10, roughness: 0.9, metalness: 0.25 })
      );
      face.position.z = z;
      if (z < 0) face.rotation.y = Math.PI;
      rear.add(face);
    }
    // Knurled index marks, kept just proud of the rim. At the old size these
    // were the spokes that made the whole thing read as a cog.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const nub = new THREE.Mesh(box(0.0016, 0.0018, 0.0112, 0.0003), M.anod);
      nub.position.set(Math.sin(a) * (RIM + 0.0008), Math.cos(a) * (RIM + 0.0008), 0);
      nub.rotation.z = -a;
      rear.add(nub);
    }
    const rearBase = new THREE.Mesh(box(0.016, 0.014, 0.020, 0.0012), M.steel);
    rearBase.position.y = -0.0128;
    rear.add(rearBase);
    rear.position.set(0, SIGHT_Y, rearZ);
    grp.add(rear);

    /* ---- front: hooded post ---- */
    const front = new THREE.Group();
    const hood = new THREE.Mesh(tube([
      [0.0106, 0], [0.0128, 0], [0.0128, 0.028], [0.0106, 0.028],
    ], 20), M.steel);
    hood.position.z = -0.014;
    front.add(hood);
    // Post: the thing you actually place on the target. It rises from the
    // bottom of the hood to the sight line, so its TIP is what you aim with.
    const post = new THREE.Mesh(box(0.0021, 0.0106, 0.0034, 0.0004), M.anod);
    post.position.y = -0.0053;
    front.add(post);
    const fBase = new THREE.Mesh(box(0.019, 0.014, 0.024, 0.0012), M.steel);
    fBase.position.y = -0.0175;
    front.add(fBase);
    front.position.set(0, SIGHT_Y, frontZ);
    grp.add(front);

    grp.position.set(0, railY, 0);
    g.add(grp);

    this.optic = grp;
    // Real HK drum sights are small; a literal cheek-weld distance leaves the
    // aperture too small to shoot with on a screen. `ironRelief` is pulled in
    // slightly for readability, which is the compromise shipped shooters make.
    this._setIrons(grp, 0, SIGHT_Y, rearZ, A.ironRelief);
  }

  /**
   * Register an iron sight line.
   *
   * `sightNode` is the point _build aligns to screen centre, so it must be the
   * REAR sight — put the front sight at the same height and the whole sight
   * line lands on the crosshair by construction. Iron relief cannot be solved
   * from aperture size (a 3 mm notch would put the eye 3 cm from the gun), so
   * each weapon publishes the standoff it actually wants and this inverts
   * _build's solver — relief = opticRearR / (tan(fov/2) * adsFill) — to get it.
   *
   * The x argument is what lets an offset optic work: an RPG's sight is
   * bracketed off the left of the tube, and aligning THAT to screen centre is
   * what pushes the bore over to the right where a real gunner sees it.
   */
  _setIrons(parent, x, y, z, wantRelief) {
    const A = this.art;
    const sight = new THREE.Object3D();
    sight.position.set(x, y, z);
    parent.add(sight);
    this.sightNode = sight;

    const rearNode = new THREE.Object3D();
    rearNode.position.set(x, y, z);
    parent.add(rearNode);
    this.rearNode = rearNode;

    const half = Math.tan(THREE.MathUtils.degToRad(VM_FOV) / 2);
    this.opticRearR = (wantRelief * half * A.adsFill) / A.scale;

    // No emitter on irons. Keep the fields defined so update() stays uniform.
    if (!this.reticleDot) { this.reticleDot = null; this.reticleHalo = null; }
  }

  /* ============================================================== pistol === */

  /**
   * M9 / 92FS-pattern service pistol.
   *
   * Proportions taken off the Commons side-profile photograph: 217 mm overall,
   * 125 mm barrel, 140 mm tall, 38 mm across the grip and 25 mm across the
   * slide. Modelled at ~0.9 of life size, which is the same compression the
   * rifles already use so the two read at a consistent scale side by side.
   *
   * The open-top slide is the load-bearing silhouette detail — two side rails
   * with the barrel bare between them is what makes this a Beretta instead of
   * a generic black brick, and it costs three boxes.
   */
  /**
   * Combat knife. A clip-point blade with a fuller, a short guard and a
   * checkered grip — held blade-forward in the right hand.
   *
   * The knife has no sight, but `_build` unconditionally reads `sightNode` and
   * `rearNode` to solve ADS eye relief, so both are provided at the blade tip.
   * With `fovZoom: 1` and no optic the "aim" pose just brings the blade up to a
   * guard position, which is what a knife's right mouse button should do.
   */
  _buildKnife(g) {
    const A = this.art, M = this.mats;
    const grp = new THREE.Group();

    const BL = 0.185;          // blade length
    const BW = 0.030;          // blade width
    const BT = 0.0042;         // blade thickness

    // Blade: a tapered slab. Two stacked boxes give the clip point without
    // needing a custom lathe.
    const blade = new THREE.Mesh(box(BT, BW, BL * 0.74, 0.0009), M.steel);
    blade.position.set(0, 0.004, -BL * 0.37 - 0.020);
    grp.add(blade);

    const tip = new THREE.Mesh(box(BT, BW * 0.62, BL * 0.30, 0.0009), M.steel);
    tip.position.set(0, 0.001, -BL * 0.86 - 0.020);
    tip.rotation.x = 0.05;
    grp.add(tip);

    // Fuller — the blood groove. Purely a highlight catcher, but it is the
    // detail that stops the blade reading as a flat plank.
    const fuller = new THREE.Mesh(box(BT * 1.25, 0.005, BL * 0.52, 0.0006), M.anod);
    fuller.position.set(0, 0.008, -BL * 0.40 - 0.020);
    grp.add(fuller);

    // Serrations along the spine.
    for (let i = 0; i < 7; i++) {
      const s = new THREE.Mesh(box(BT * 1.1, 0.0055, 0.0075, 0.0006), M.steel);
      s.position.set(0, 0.0165, -0.052 - i * 0.0125);
      s.rotation.x = 0.5;
      grp.add(s);
    }

    // Guard.
    const guard = new THREE.Mesh(box(0.010, 0.040, 0.011, 0.0018), M.steel);
    guard.position.set(0, 0.001, -0.014);
    grp.add(guard);

    // Grip: stacked rings read as checkering and catch light along the length.
    const grip = new THREE.Mesh(box(0.019, 0.026, A.gripLen, 0.0055), M.poly ?? M.furniture);
    grip.position.set(0, -0.002, A.gripLen * 0.5 - 0.006);
    grp.add(grip);
    for (let i = 0; i < 6; i++) {
      const r = new THREE.Mesh(box(0.0205, 0.0275, 0.0042, 0.0012), M.anod);
      r.position.set(0, -0.002, 0.006 + i * 0.0165);
      grp.add(r);
    }

    // Pommel with a lanyard hole.
    const pommel = new THREE.Mesh(box(0.020, 0.027, 0.014, 0.003), M.steel);
    pommel.position.set(0, -0.002, A.gripLen + 0.002);
    grp.add(pommel);

    grp.rotation.z = 0.06;
    g.add(grp);
    this.knifeNode = grp;

    // Muzzle stands in as the blade tip: main.js traces from here for melee.
    this.muzzle = new THREE.Object3D();
    this.muzzle.position.set(0, 0.0, -BL - 0.030);
    grp.add(this.muzzle);

    // Dummy sight nodes so the shared ADS solver has something to read.
    const sight = new THREE.Object3D();
    sight.position.set(0, 0.012, -0.10);
    grp.add(sight);
    this.sightNode = sight;
    const rear = new THREE.Object3D();
    rear.position.set(0, 0.012, -0.06);
    grp.add(rear);
    this.rearNode = rear;
    this.opticRearR = 0.030;
    this.reticleDot = null;
    this.reticleHalo = null;
  }

  _buildPistol(g) {
    const A = this.art, M = this.mats;
    const SL = A.slideLen, sw = A.slideW, sh = A.slideH;
    const zRear = 0.030, zF = zRear - SL;
    const top = 0.011, bot = top - sh, midY = top - sh / 2;

    /* ---- slide: reciprocates on every shot, so it gets its own group ---- */
    const sl = new THREE.Group();
    const railLen = SL - 0.082;
    const rearBlock = new THREE.Mesh(box(sw, sh, 0.052, 0.0026), M.steel);
    rearBlock.position.set(0, midY, zRear - 0.026);
    sl.add(rearBlock);
    const nose = new THREE.Mesh(box(sw, sh, 0.030, 0.0026), M.steel);
    nose.position.set(0, midY, zF + 0.015);
    sl.add(nose);
    for (const sx of [-1, 1]) {
      const rail = new THREE.Mesh(box(0.0070, sh, railLen, 0.0018), M.steel);
      rail.position.set(sx * (sw / 2 - 0.0035), midY, zF + 0.030 + railLen / 2);
      sl.add(rail);
      // Cocking serrations: seven cuts at the rear of the slide.
      const xf = [];
      for (let i = 0; i < 7; i++) xf.push([0, 0, -i * 0.0060]);
      const ser = new THREE.Mesh(scatter(box(0.0022, 0.018, 0.0030, 0.0006), xf), M.wear);
      ser.position.set(sx * (sw / 2 + 0.0004), midY - 0.001, zRear - 0.009);
      sl.add(ser);
      // Slide-mounted safety / decocker, the 92's most recognisable control.
      const dec = new THREE.Mesh(tube([
        [0, 0], [0.0068, 0], [0.0068, 0.004], [0.0050, 0.005], [0, 0.005],
      ], 12), M.anod);
      dec.rotation.y = -sx * Math.PI / 2;
      dec.position.set(sx * (sw / 2 - 0.0005), midY + 0.004, zRear - 0.030);
      sl.add(dec);
      const lev = new THREE.Mesh(box(0.0040, 0.0085, 0.020, 0.0016), M.anod);
      lev.position.set(sx * (sw / 2 + 0.0028), midY + 0.001, zRear - 0.040);
      lev.rotation.x = 0.30;
      sl.add(lev);
    }
    // Extractor / loaded-chamber indicator on the right.
    const ext = new THREE.Mesh(box(0.0040, 0.0075, 0.020, 0.0010), M.wear);
    ext.position.set(sw / 2 - 0.0012, top - 0.006, zRear - 0.056);
    sl.add(ext);

    /* ---- sights. Blade tip and notch floor share SIGHT_Y by construction --- */
    const SIGHT_Y = top + 0.0072;
    const blade = new THREE.Mesh(box(0.0032, 0.0090, 0.0042, 0.0006), M.dark);
    blade.position.set(0, top + 0.0036, zF + 0.014);
    sl.add(blade);
    const dotF = new THREE.Mesh(new THREE.CircleGeometry(0.0011, 10),
      new THREE.MeshStandardMaterial({ color: 0xd8e6c0, roughness: 0.5, emissive: 0x1a2410 }));
    dotF.position.set(0, top + 0.0040, zF + 0.0119);
    sl.add(dotF);
    // Rear notch: two posts with the gap between them ON the sight line.
    for (const sx of [-1, 1]) {
      const p = new THREE.Mesh(box(0.0062, 0.0088, 0.0090, 0.0008), M.dark);
      p.position.set(sx * 0.0055, top + 0.0035, zRear - 0.014);
      sl.add(p);
      const d = new THREE.Mesh(new THREE.CircleGeometry(0.0010, 10),
        new THREE.MeshStandardMaterial({ color: 0xd8e6c0, roughness: 0.5, emissive: 0x1a2410 }));
      d.position.set(sx * 0.0055, top + 0.0034, zRear - 0.0094);
      d.rotation.y = Math.PI;
      sl.add(d);
    }
    const notchBase = new THREE.Mesh(box(0.019, 0.0040, 0.0100, 0.0008), M.steel);
    notchBase.position.set(0, top + 0.0018, zRear - 0.014);
    sl.add(notchBase);

    g.add(sl);
    this.bolt = sl;
    this._boltZ = 0;
    this._boltTravel = 0.024;

    /* ---- barrel, bare between the rails ---- */
    const bar = new THREE.Mesh(tube([
      [0, 0], [A.barrelR, 0], [A.barrelR, SL - 0.050],
      [A.barrelR * 1.16, SL - 0.048], [A.barrelR * 1.16, SL - 0.010], [0, SL - 0.010],
    ], 16), M.wear);
    bar.position.set(0, 0.0005, zF + 0.012);
    g.add(bar);
    const crown = new THREE.Mesh(tube([
      [A.barrelR * 0.60, 0], [A.barrelR * 0.60, 0.014], [0, 0.016],
    ], 14), M.dark);
    crown.position.set(0, 0.0005, zF + 0.013);
    g.add(crown);

    /* ---- frame ---- */
    const dustLen = SL - 0.062;
    const dust = new THREE.Mesh(box(0.0245, 0.0165, dustLen, 0.0022), M.steel);
    dust.position.set(0, bot - 0.0075, zF + 0.010 + dustLen / 2);
    g.add(dust);
    // Frame rail relief along the flanks.
    const relief = new THREE.Mesh(box(0.0265, 0.0042, dustLen * 0.86, 0.0010), M.steel);
    relief.position.set(0, bot - 0.0035, zF + 0.014 + dustLen / 2);
    g.add(relief);
    const body = new THREE.Mesh(box(0.0290, 0.0270, 0.078, 0.0030), M.steel);
    body.position.set(0, bot - 0.0125, zRear - 0.036);
    g.add(body);
    // Beavertail tang: the thing the web of the hand is driven up under.
    const tang = new THREE.Mesh(box(0.0230, 0.0090, 0.026, 0.0035), M.steel);
    tang.position.set(0, bot - 0.0035, zRear - 0.002);
    tang.rotation.x = -0.28;
    g.add(tang);

    // Takedown lever, slide stop and mag release, all on the left except the
    // release, which a right-handed shooter reaches with the firing thumb.
    const td = new THREE.Mesh(tube([[0, 0], [0.0072, 0], [0.0072, 0.005], [0, 0.005]], 14), M.wear);
    td.rotation.y = Math.PI / 2;
    td.position.set(-(0.0145), bot - 0.010, zF + 0.052);
    g.add(td);
    const stop = new THREE.Mesh(box(0.0042, 0.0090, 0.030, 0.0012), M.wear);
    stop.position.set(-(0.0160), bot - 0.006, zF + 0.086);
    g.add(stop);
    this.slideStop = stop;
    this._slideStopY = stop.position.y;
    const mr = new THREE.Mesh(tube([[0, 0], [0.0058, 0], [0.0058, 0.005], [0, 0.005]], 12), M.wear);
    mr.rotation.y = -Math.PI / 2;
    mr.position.set(0.0140, bot - 0.014, zRear - 0.060);
    g.add(mr);
    this.magReleaseMesh = mr;

    /* ---- hammer: falls with the trigger, rides back with the slide ---- */
    const ham = new THREE.Group();
    const spur = new THREE.Mesh(box(0.0072, 0.020, 0.0080, 0.0022), M.wear);
    spur.position.y = 0.010;
    ham.add(spur);
    const ring = new THREE.Mesh(tube([
      [0.0028, 0], [0.0060, 0], [0.0060, 0.0060], [0.0028, 0.0060],
    ], 12), M.wear);
    ring.rotation.y = Math.PI / 2;
    ring.position.set(-0.003, 0.022, 0);
    ham.add(ring);
    ham.position.set(0, bot - 0.004, zRear + 0.002);
    g.add(ham);
    this.hammerNode = ham;
    this._hamRest = -0.62;
    ham.rotation.x = this._hamRest;

    /* ---- trigger guard and trigger ---- */
    const gbar = new THREE.Mesh(box(0.0095, 0.0055, 0.040, 0.0016), M.steel);
    gbar.position.set(0, bot - 0.052, zF + 0.086);
    g.add(gbar);
    const gfront = new THREE.Mesh(box(0.0095, 0.026, 0.0068, 0.0016), M.steel);
    gfront.position.set(0, bot - 0.038, zF + 0.068);
    gfront.rotation.x = -0.34;
    g.add(gfront);
    const grear = new THREE.Mesh(box(0.0095, 0.024, 0.0070, 0.0016), M.steel);
    grear.position.set(0, bot - 0.038, zF + 0.106);
    grear.rotation.x = 0.24;
    g.add(grear);
    const trg = new THREE.Mesh(box(0.0060, 0.0210, 0.0060, 0.0014), M.wear);
    trg.position.set(0, bot - 0.032, zF + 0.094);
    trg.rotation.x = 0.14;
    g.add(trg);
    this.triggerMesh = trg;
    this._trgZ = trg.position.z;

    /* ---- grip, checkered panels, lanyard loop ---- */
    const gripY = bot - 0.016 - A.gripLen / 2;
    const gripZ = zRear - 0.044;
    const grip = new THREE.Group();
    const core = new THREE.Mesh(box(0.0300, A.gripLen, 0.0470, 0.0050), M.steel);
    grip.add(core);
    for (const sx of [-1, 1]) {
      const panel = new THREE.Mesh(box(0.0038, A.gripLen * 0.94, 0.0430, 0.0035), M.poly);
      panel.position.x = sx * 0.0166;
      grip.add(panel);
      // Two grip screws per panel.
      for (const yy of [0.020, -0.024]) {
        const sc = new THREE.Mesh(tube([[0, 0], [0.0026, 0], [0.0026, 0.0016], [0, 0.0016]], 10), M.wear);
        sc.rotation.y = -sx * Math.PI / 2;
        sc.position.set(sx * 0.0182, yy, 0.002);
        grip.add(sc);
      }
    }
    const front = new THREE.Mesh(box(0.0250, A.gripLen * 0.86, 0.0050, 0.0018), M.rubber);
    front.position.set(0, -0.002, -0.0245);
    grip.add(front);
    const back = new THREE.Mesh(box(0.0250, A.gripLen * 0.92, 0.0050, 0.0018), M.rubber);
    back.position.set(0, 0, 0.0245);
    grip.add(back);
    const loop = new THREE.Mesh(tube([
      [0.0030, 0], [0.0055, 0], [0.0055, 0.0040], [0.0030, 0.0040],
    ], 10), M.wear);
    loop.rotation.y = Math.PI / 2;
    loop.position.set(0, -A.gripLen / 2 - 0.003, 0.016);
    grip.add(loop);
    grip.position.set(0, gripY, gripZ);
    grip.rotation.x = A.gripAngle;
    g.add(grip);
    this.gripNode = grip;

    /* ---- magazine: a floorplate and the spine, the rest lives inside ---- */
    const mag = new THREE.Group();
    const mbody = new THREE.Mesh(box(A.magW, A.magLen, A.magD, 0.0022), M.wear);
    mag.add(mbody);
    const plate = new THREE.Mesh(box(A.magW + 0.0060, 0.0075, A.magD + 0.0070, 0.0020), M.poly);
    plate.position.y = -A.magLen / 2 - 0.003;
    mag.add(plate);
    const witness = [];
    for (let i = 0; i < 4; i++) witness.push([0, -0.012 - i * 0.014, 0]);
    for (const sx of [-1, 1]) {
      const w = new THREE.Mesh(scatter(box(0.0030, 0.0050, 0.0050, 0.0007), witness), M.dark);
      w.position.set(sx * (A.magW / 2 - 0.0008), 0, 0);
      mag.add(w);
    }
    mag.position.set(0, gripY - 0.002, gripZ);
    mag.rotation.x = A.gripAngle;
    g.add(mag);
    this.magazine = mag;
    this._magHome = mag.position.clone();
    this._magRotHome = A.gripAngle;

    /* ---- sight line and muzzle ---- */
    this.optic = sl;
    this._setIrons(g, 0, SIGHT_Y, zRear - 0.014, A.ironRelief);

    this.muzzle = new THREE.Object3D();
    this.muzzle.position.set(0, 0.0005, zF - 0.006);
    g.add(this.muzzle);

    this.gripAnchor = new THREE.Vector3(0, gripY, gripZ);
  }

  /* ============================================================= shotgun === */

  /**
   * Remington 870-pattern pump gun.
   *
   * The two things that make a pump gun read as a pump gun in first person,
   * both taken from the Sandstorm M870 view: the barrel and the magazine tube
   * are two clearly separate parallel cylinders joined by a clamp, and the
   * ribbed forend is a fat sleeve UNDER the bore that the support hand is
   * wrapped round from below. Everything else is a rifle receiver.
   */
  _buildShotgun(g) {
    const A = this.art, M = this.mats;
    const RL = A.recvLen, RW = A.recvW, RH = A.recvH;
    const recvTop = 0.012, recvBot = recvTop - RH;
    const zRecv0 = -0.004, zRecv1 = zRecv0 + RL;

    /* ---- receiver: milled steel box with a big ejection port ---- */
    const body = new THREE.Mesh(box(RW, RH, RL, 0.0040), M.steel);
    body.position.set(0, recvTop - RH / 2, zRecv0 + RL / 2);
    g.add(body);
    const port = new THREE.Mesh(box(0.0070, 0.0270, 0.062, 0.0016), M.dark);
    port.position.set(RW / 2 - 0.0018, -0.0020, zRecv0 + 0.052);
    g.add(port);
    const portRim = new THREE.Mesh(box(0.0035, 0.0330, 0.068, 0.0014), M.wear);
    port.position.z = zRecv0 + 0.052;
    portRim.position.set(RW / 2 - 0.0038, -0.0020, zRecv0 + 0.052);
    g.add(portRim);
    // Bolt seen through the port. It rides back on the pump stroke, not on
    // the shot — which is exactly the point of a manually operated gun.
    const blt = new THREE.Mesh(box(0.0200, 0.0230, 0.050, 0.0022), M.wear);
    blt.position.set(RW / 2 - 0.0110, -0.0020, zRecv0 + 0.050);
    g.add(blt);
    this.bolt = blt;
    this._boltZ = blt.position.z;
    this._boltTravel = 0.044;
    // Loading port underneath, with a shell visible on the lifter.
    const load = new THREE.Mesh(box(0.0230, 0.0060, 0.056, 0.0012), M.dark);
    load.position.set(0, recvBot + 0.0022, zRecv0 + 0.048);
    g.add(load);
    const shell = new THREE.Mesh(tube([
      [0, 0], [0.0092, 0], [0.0092, 0.050], [0.0100, 0.050],
      [0.0100, 0.062], [0, 0.062],
    ], 14), new THREE.MeshStandardMaterial({ color: 0x8c1e1e, roughness: 0.62, metalness: 0.0 }));
    shell.rotation.y = Math.PI;
    shell.position.set(0, recvBot + 0.0090, zRecv0 + 0.016);
    g.add(shell);
    this.liftShell = shell;
    const head = new THREE.Mesh(tube([
      [0, 0], [0.0102, 0], [0.0102, 0.010], [0, 0.010],
    ], 14), M.brass);
    head.rotation.y = Math.PI;
    head.position.set(0, recvBot + 0.0090, zRecv0 + 0.068);
    g.add(head);
    this.liftHead = head;

    /* ---- barrel and magazine tube: two parallel cylinders ---- */
    const BR = A.barrelR, TR = A.tubeR, drop = A.tubeDrop;
    const zBar = zRecv0 - A.barrelLen;
    const bar = new THREE.Mesh(tube([
      [0, 0], [BR * 1.34, 0], [BR * 1.34, 0.034], [BR, 0.046],
      [BR, A.barrelLen - 0.006], [BR * 1.08, A.barrelLen - 0.004],
      [BR * 1.08, A.barrelLen], [0, A.barrelLen],
    ], 18), M.steel);
    bar.position.set(0, 0, zRecv0);
    g.add(bar);
    const crown = new THREE.Mesh(tube([
      [BR * 0.66, 0], [BR * 0.66, 0.020], [0, 0.022],
    ], 14), M.dark);
    crown.position.set(0, 0, zBar + 0.002);
    g.add(crown);
    // Ventilated rib along the top of the barrel.
    //
    // This is a shotgun signature, but it earns its place for a rendering
    // reason: at ADS the barrel is a dark cylinder pointing straight down the
    // view axis with nothing on it for the light to catch, and it read as a
    // black smear. A rib gives a narrow lit strip that converges on the ghost
    // ring, which is exactly the line the W1200 / M1014 ADS captures use to
    // lead the eye to the sight.
    const ribLen = A.barrelLen - 0.030;
    const ribY = BR + 0.0062;
    const ribTop = new THREE.Mesh(box(0.0092, 0.0034, ribLen, 0.0009), M.steel);
    ribTop.position.set(0, ribY, zRecv0 - 0.014 - ribLen / 2);
    g.add(ribTop);
    const postG = box(0.0052, 0.0072, 0.0090, 0.0008);
    const pxf = [];
    const nPost = Math.floor(ribLen / 0.028);
    for (let i = 0; i < nPost; i++) pxf.push([0, 0, -ribLen / 2 + 0.010 + i * (ribLen - 0.020) / (nPost - 1)]);
    const ribPosts = new THREE.Mesh(scatter(postG, pxf), M.steel);
    ribPosts.position.set(0, BR + 0.0016, zRecv0 - 0.014 - ribLen / 2);
    g.add(ribPosts);

    const tubeLen = A.barrelLen * 0.80;
    const mag = new THREE.Mesh(tube([
      [0, 0], [TR, 0], [TR, tubeLen - 0.014], [TR * 1.12, tubeLen - 0.012],
      [TR * 1.12, tubeLen], [0, tubeLen],
    ], 16), M.steel);
    mag.position.set(0, -drop, zRecv0 - 0.002);
    g.add(mag);
    // Barrel clamp: the fitting that says these are two tubes, not one lump.
    const clamp = new THREE.Mesh(box(0.0290, drop + BR + TR + 0.006, 0.020, 0.0026), M.steel);
    clamp.position.set(0, -drop / 2, zRecv0 - tubeLen + 0.014);
    g.add(clamp);
    const lug = new THREE.Mesh(box(0.0180, 0.0110, 0.014, 0.0022), M.steel);
    lug.position.set(0, -drop - TR - 0.004, zRecv0 - tubeLen + 0.014);
    g.add(lug);

    /* ---- forend: the pump. Slides, and drags the support hand with it ---- */
    const pump = new THREE.Group();
    const FR = A.forendR, FL = A.forendLen;
    const shellBody = new THREE.Mesh(tube([
      [TR * 0.98, 0], [FR * 0.80, 0.008], [FR, 0.020],
      [FR, FL - 0.024], [FR * 0.86, FL - 0.008], [TR * 0.98, FL],
    ], 18), M.matte);
    shellBody.position.set(0, 0, FL / 2);
    pump.add(shellBody);
    // Fore-aft ribs. On an 870 these are cut INTO the forend; modelled proud
    // they still catch the same highlight and cost one draw call.
    const ribG = box(0.0055, 0.0042, FL * 0.80, 0.0012);
    const rxf = [];
    for (let i = 0; i < 11; i++) {
      const a = -Math.PI * 0.86 + i * (Math.PI * 1.72) / 10;
      rxf.push([Math.cos(a) * (FR - 0.0010), Math.sin(a) * (FR - 0.0010), 0, a - Math.PI / 2]);
    }
    const ribs = new THREE.Mesh(scatter(ribG, rxf), M.matte);
    pump.add(ribs);
    pump.position.set(0, -drop, A.forendZ);
    g.add(pump);
    this.pumpNode = pump;
    this._pumpZ = pump.position.z;

    /* ---- sights: bead-on-a-post front, ghost ring rear ---- */
    const SIGHT_Y = A.sightY ?? 0.0250;
    const fpost = new THREE.Mesh(box(0.0030, SIGHT_Y - BR + 0.004, 0.0060, 0.0010), M.steel);
    fpost.position.set(0, (BR + SIGHT_Y) / 2, zBar + 0.022);
    g.add(fpost);
    const bead = new THREE.Mesh(new THREE.SphereGeometry(0.0034, 12, 10),
      new THREE.MeshStandardMaterial({ color: 0xfff3d2, roughness: 0.35, emissive: 0x6a4f14 }));
    bead.position.set(0, SIGHT_Y, zBar + 0.022);
    g.add(bead);
    // Rear: an OPEN NOTCH, not a ghost ring.
    //
    // A ghost ring is correct on a real fighting shotgun and wrong on screen:
    // an 11 mm annulus 24 cm from the eye becomes a black disc with a hole in
    // it, filling the middle of the sight picture and hiding whatever is being
    // aimed at. The reference (the CoD pump-gun ADS frame) uses the friendlier
    // arrangement — a low, wide rear blade with a square notch cut in it, and
    // the bead sitting up in the gap. Almost nothing is occluded: the eye reads
    // two posts and the space between them.
    //
    // Built as two posts plus a crossbar rather than a notched solid because
    // the primitives here are boxes and lathes; a subtractive notch would need
    // CSG for a shape the eye reads as a gap anyway.
    const rear = new THREE.Group();
    const NOTCH_W = 0.0125;              // the gap you actually look through
    const POST_W = 0.0040;
    const POST_H = 0.0072;
    const POST_D = 0.0055;
    for (const s of [-1, 1]) {
      const post = new THREE.Mesh(box(POST_W, POST_H, POST_D, 0.0011), M.steel);
      post.position.set(s * (NOTCH_W + POST_W) / 2, -POST_H / 2, 0);
      rear.add(post);
    }
    // Crossbar under the notch closes the U and gives the posts something to
    // sit on, so they do not read as two loose fins.
    const notchBar = new THREE.Mesh(box(NOTCH_W + POST_W * 2, 0.0042, POST_D, 0.0010), M.steel);
    notchBar.position.y = -POST_H - 0.0021;
    rear.add(notchBar);
    // Matte black on the face nearest the eye: a shiny rear sight throws a
    // highlight straight down the sight line and washes out the notch.
    const face = new THREE.Mesh(box(NOTCH_W + POST_W * 2, POST_H + 0.0042, 0.0012, 0.0008),
      new THREE.MeshStandardMaterial({ color: 0x0a0c0e, roughness: 0.92, metalness: 0.2 }));
    face.position.set(0, -(POST_H + 0.0042) / 2, POST_D / 2 + 0.0006);
    // Only the two posts should be dark, not the gap — so the face is split.
    face.visible = false;
    rear.add(face);
    for (const s of [-1, 1]) {
      const fp = new THREE.Mesh(box(POST_W, POST_H, 0.0012, 0.0006),
        new THREE.MeshStandardMaterial({ color: 0x0a0c0e, roughness: 0.92, metalness: 0.2 }));
      fp.position.set(s * (NOTCH_W + POST_W) / 2, -POST_H / 2, POST_D / 2 + 0.0007);
      rear.add(fp);
    }
    const gbase = new THREE.Mesh(box(0.0240, SIGHT_Y - recvTop + 0.006, 0.0150, 0.0014), M.steel);
    gbase.position.y = -POST_H - 0.0042 - (SIGHT_Y - recvTop + 0.006) / 2;
    rear.add(gbase);
    rear.position.set(0, SIGHT_Y, zRecv1 - 0.024);
    g.add(rear);
    const ghost = rear;

    /* ---- trigger group ---- */
    const tg = new THREE.Mesh(box(0.0270, 0.0180, 0.062, 0.0026), M.matte);
    tg.position.set(0, recvBot - 0.0080, zRecv1 - 0.048);
    g.add(tg);
    const gbar = new THREE.Mesh(box(0.0100, 0.0055, 0.042, 0.0016), M.steel);
    gbar.position.set(0, recvBot - 0.0400, zRecv1 - 0.048);
    g.add(gbar);
    for (const [zz, rx] of [[zRecv1 - 0.070, -0.30], [zRecv1 - 0.027, 0.26]]) {
      const post = new THREE.Mesh(box(0.0100, 0.0250, 0.0072, 0.0014), M.steel);
      post.position.set(0, recvBot - 0.0270, zz);
      post.rotation.x = rx;
      g.add(post);
    }
    const trg = new THREE.Mesh(box(0.0060, 0.0210, 0.0060, 0.0014), M.wear);
    trg.position.set(0, recvBot - 0.0230, zRecv1 - 0.050);
    trg.rotation.x = 0.14;
    g.add(trg);
    this.triggerMesh = trg;
    this._trgZ = trg.position.z;
    // Cross-bolt safety through the rear of the trigger plate.
    const saf = new THREE.Mesh(tube([[0, 0], [0.0042, 0], [0.0042, 0.030], [0, 0.030]], 12), M.wear);
    saf.rotation.y = Math.PI / 2;
    saf.position.set(-0.015, recvBot - 0.0080, zRecv1 - 0.024);
    g.add(saf);
    // Action-bar release on the left, forward of the guard.
    const arel = new THREE.Mesh(box(0.0040, 0.0140, 0.0110, 0.0012), M.wear);
    arel.position.set(-(RW / 2 + 0.0012), recvBot - 0.0060, zRecv1 - 0.082);
    g.add(arel);

    /* ---- stock. The wrist IS the grip on a shotgun ---- */
    const gripY = recvBot - 0.0110 - A.gripLen / 2;
    const gripZ = zRecv1 + 0.030;
    const grip = new THREE.Group();
    const gcore = new THREE.Mesh(box(0.0330, A.gripLen, 0.0500, 0.0090), M.matte);
    grip.add(gcore);
    const strap = new THREE.Mesh(box(0.0290, A.gripLen * 0.82, 0.0060, 0.0022), M.rubber);
    strap.position.set(0, 0.002, -0.0250);
    grip.add(strap);
    // Chequering panels on the wrist.
    for (const sx of [-1, 1]) {
      const p = new THREE.Mesh(box(0.0030, A.gripLen * 0.68, 0.0330, 0.0022), M.rubber);
      p.position.set(sx * 0.0168, -0.004, 0.002);
      grip.add(p);
    }
    grip.position.set(0, gripY, gripZ);
    grip.rotation.x = A.gripAngle;
    g.add(grip);
    this.gripNode = grip;

    // Stock, with DROP AT COMB.
    //
    // Built level with the receiver it was a 56 mm plank running forward past
    // the camera, and at ADS it filled the bottom third of the frame with an
    // unreadable pale lump — the material-ID probe traced the blob straight to
    // it. A real 870 comb sits well below the bore and drops further to the
    // heel, which is precisely what keeps it under the shooter's cheek and out
    // of the sight picture. Note the tilt sign: the old -0.055 lifted the heel
    // instead of dropping it.
    const SLn = A.stockLen;
    const combDrop = 0.118;
    const comb = new THREE.Mesh(box(0.0320, 0.0480, SLn, 0.0100), M.matte);
    comb.position.set(0, recvBot - 0.0330, gripZ + 0.022 + SLn / 2);
    comb.rotation.x = combDrop;
    g.add(comb);
    // Wrist fillet blending the comb up into the receiver, so the drop does
    // not read as the stock having fallen off.
    const wrist = new THREE.Mesh(box(0.0280, 0.0250, 0.0480, 0.0080), M.matte);
    wrist.position.set(0, recvBot - 0.0215, gripZ + 0.020);
    wrist.rotation.x = combDrop * 0.5;
    g.add(wrist);
    const cheek = new THREE.Mesh(box(0.0270, 0.0130, SLn * 0.66, 0.0055), M.matte);
    cheek.position.set(0, recvBot - 0.0130 - SLn * 0.40 * Math.sin(combDrop),
      gripZ + 0.020 + SLn * 0.42);
    cheek.rotation.x = combDrop;
    g.add(cheek);
    const pad = new THREE.Mesh(box(0.0350, 0.0760, 0.0180, 0.0060), M.rubber);
    pad.position.set(0, recvBot - 0.0330 - SLn * Math.sin(combDrop), gripZ + 0.030 + SLn);
    pad.rotation.x = combDrop;
    g.add(pad);
    const sling = new THREE.Mesh(tube([
      [0.0055, 0], [0.0095, 0], [0.0095, 0.0050], [0.0055, 0.0050],
    ], 10), M.wear);
    sling.rotation.y = Math.PI / 2;
    sling.position.set(0, recvBot - 0.0560 - SLn * 0.86 * Math.sin(combDrop),
      gripZ + 0.020 + SLn * 0.90);
    g.add(sling);

    /* ---- sight line, muzzle, magazine stand-in for the reload rig ---- */
    this.optic = ghost;
    this._setIrons(g, 0, SIGHT_Y, zRecv1 - 0.024, A.ironRelief);

    this.muzzle = new THREE.Object3D();
    this.muzzle.position.set(0, 0, zBar - 0.008);
    g.add(this.muzzle);

    // A pump gun has no detachable magazine. The reload rig still wants a
    // `magazine` node, so it gets the loose shell the support hand carries.
    const spare = new THREE.Group();
    const sb = new THREE.Mesh(tube([
      [0, 0], [0.0092, 0], [0.0092, 0.042], [0.0102, 0.042], [0.0102, 0.056], [0, 0.056],
    ], 14), new THREE.MeshStandardMaterial({ color: 0x8c1e1e, roughness: 0.62 }));
    sb.rotation.y = Math.PI;
    sb.position.z = -0.028;
    spare.add(sb);
    const sh = new THREE.Mesh(tube([[0, 0], [0.0104, 0], [0.0104, 0.012], [0, 0.012]], 14), M.brass);
    sh.rotation.y = Math.PI;
    sh.position.z = 0.028;
    spare.add(sh);
    spare.position.set(0, recvBot - 0.010, zRecv0 + 0.040);
    spare.visible = false;
    g.add(spare);
    this.magazine = spare;
    this._magHome = spare.position.clone();
    this._magRotHome = 0;

    this.gripAnchor = new THREE.Vector3(0, gripY, gripZ);
  }

  /* ============================================================ launcher === */

  /**
   * RPG-7 with a PG-7V warhead up the spout.
   *
   * Everything here is driven by the fact that this weapon is fired off the
   * SHOULDER, not from a cheek weld: the tube runs past the side of the head,
   * so in first person it sits high and outboard with the rear of it heading
   * off the edge of the frame, and the sight you look through is bracketed off
   * the LEFT of the tube. Aligning that offset optic to the crosshair — rather
   * than the bore — is what puts the tube and warhead over to the right of
   * screen centre, which is exactly the picture a real gunner gets.
   */
  _buildLauncher(g) {
    const A = this.art, M = this.mats;
    const R = A.tubeR, zF = A.tubeFront, zR = A.tubeRear;

    /* ---- launch tube: constant bore, fat combustion chamber, blast cone --- */
    const len = zR - zF;
    const body = new THREE.Mesh(tube([
      [0, 0], [R * 1.10, 0], [R * 1.10, 0.014], [R, 0.024],
      [R, len * 0.42], [R * 1.26, len * 0.46], [R * 1.26, len * 0.60],
      [R, len * 0.64], [R, len - 0.070], [R * 1.10, len - 0.056],
      [R * 1.10, len - 0.030], [R * 1.68, len], [R * 1.42, len], [0, len - 0.004],
    ], 22), M.steel);
    body.rotation.y = Math.PI;
    body.position.set(0, 0, zF);
    g.add(body);
    // Bore mouth, so the front of the tube is a hole rather than a cap.
    const mouth = new THREE.Mesh(tube([
      [R * 0.76, 0], [R * 0.76, 0.028], [0, 0.030],
    ], 20), M.dark);
    mouth.position.set(0, 0, zF + 0.004);
    g.add(mouth);

    /* ---- wooden heat shields ---- */
    for (const [z0, L] of [[zF + 0.120, 0.150], [zF + 0.320, 0.090]]) {
      const hs = new THREE.Mesh(tube([
        [R * 1.02, 0], [R * 1.34, 0.010], [R * 1.40, 0.024],
        [R * 1.40, L - 0.024], [R * 1.34, L - 0.010], [R * 1.02, L],
      ], 18), M.matte);
      hs.rotation.y = Math.PI;
      hs.position.set(0, 0, z0);
      g.add(hs);
      for (const zz of [0.006, L - 0.006]) {
        const band = new THREE.Mesh(tube([
          [R * 1.36, 0], [R * 1.46, 0], [R * 1.46, 0.0070], [R * 1.36, 0.0070],
        ], 18), M.wear);
        band.position.set(0, 0, z0 - zz);
        g.add(band);
      }
    }

    /* ---- PG-7V warhead, protruding a long way out of the muzzle ---- */
    const wh = new THREE.Group();
    const boost = new THREE.Mesh(tube([
      [0, 0], [R * 0.74, 0], [R * 0.74, 0.056], [0.0130, 0.062],
      [0.0130, 0.130], [0.0150, 0.134], [0.0150, 0.146], [0, 0.148],
    ], 16), M.steel);
    boost.position.set(0, 0, 0.052);
    wh.add(boost);
    // Stabiliser fin band on the booster.
    const finG = box(0.0038, 0.0180, 0.026, 0.0008);
    const fxf = [];
    for (let i = 0; i < 4; i++) {
      const a = Math.PI / 4 + i * Math.PI / 2;
      fxf.push([Math.cos(a) * 0.014, Math.sin(a) * 0.014, 0, a - Math.PI / 2]);
    }
    const fins = new THREE.Mesh(scatter(finG, fxf), M.steel);
    fins.position.set(0, 0, 0.026);
    wh.add(fins);
    const bulb = new THREE.Mesh(tube([
      [0.0150, 0], [0.0230, 0.014], [0.0310, 0.036], [0.0340, 0.062],
      [0.0340, 0.086], [0.0300, 0.104], [0.0210, 0.122], [0.0120, 0.138],
      [0.0058, 0.150], [0, 0.152],
    ], 20), new THREE.MeshStandardMaterial({
      color: 0x6d6a4e, metalness: 0.85, roughness: 0.52, envMapIntensity: 0.8,
    }));
    bulb.position.set(0, 0, -0.096);
    wh.add(bulb);
    const fuze = new THREE.Mesh(tube([
      [0, 0], [0.0052, 0], [0.0052, 0.026], [0.0034, 0.030], [0, 0.030],
    ], 12), M.dark);
    fuze.position.set(0, 0, -0.246);
    wh.add(fuze);
    // Yellow band: the marking that reads as "live warhead" at a glance.
    const bandM = new THREE.Mesh(tube([
      [0.0342, 0], [0.0348, 0], [0.0348, 0.010], [0.0342, 0.010],
    ], 20), new THREE.MeshStandardMaterial({ color: 0xb8933a, roughness: 0.6, metalness: 0.3 }));
    bandM.position.set(0, 0, -0.166);
    wh.add(bandM);
    wh.position.set(0, 0, zF - 0.008);
    g.add(wh);
    this.warhead = wh;

    /* ---- folding leaf iron sights, standing proud on top of the tube ------
     *
     * Rear is an upright rectangular leaf you look THROUGH; front is a post
     * inside protective ears. This is the CoD4 / MWR / AW picture: the frame
     * is mostly open sky, the post tip sits on the target, and the launch tube
     * is a lit wedge in the bottom third. It is also the real weapon's backup
     * sight, hinged off a base on the tube's top-left.
     * -------------------------------------------------------------------- */
    const SX = A.sightX, SY = A.sightY;
    const sights = new THREE.Group();

    // Rear leaf. Window 22 x 38 mm, top edge 95 mm above the bore, which is
    // the ~1.5 tube-diameters the firing photograph shows.
    const rear = new THREE.Group();
    const rw = 0.0300, rh = 0.0460, wall = 0.0038, rth = 0.0055;
    for (const [w, h, x, y] of [
      [rw, wall, 0, (rh - wall) / 2],
      [wall, rh - 2 * wall, -(rw - wall) / 2, 0],
      [wall, rh - 2 * wall, (rw - wall) / 2, 0],
      [rw, wall * 1.4, 0, -(rh - wall * 1.4) / 2],
    ]) {
      const b = new THREE.Mesh(box(w, h, rth, 0.0009), M.steel);
      b.position.set(x, y, 0);
      rear.add(b);
    }
    // Elevation slider riding the leaf, and its graduation notches. Detail
    // that reads at ADS because it sits right on the edge of the window.
    const slide = new THREE.Mesh(box(rw + 0.0034, 0.0044, rth + 0.0022, 0.0010), M.wear);
    slide.position.set(0, -rh * 0.30, 0);
    rear.add(slide);
    const notchG = box(0.0016, 0.0030, rth + 0.0042, 0.0004);
    const nxf = [];
    for (let i = 0; i < 3; i++) nxf.push([0, 0.0020 + i * 0.0072, 0]);
    const notches = new THREE.Mesh(scatter(notchG, nxf), M.anod);
    notches.position.set(-(rw - wall) / 2 - 0.0022, 0, 0);
    rear.add(notches);
    // Hinge base and stem down onto the tube.
    const stemH = SY - rh / 2 - R * 0.86;
    const stem = new THREE.Mesh(box(0.0170, Math.max(0.004, stemH), 0.0140, 0.0022), M.steel);
    stem.position.set(0, -(rh / 2 + stemH / 2), 0);
    rear.add(stem);
    const hinge = new THREE.Mesh(tube([
      [0, 0], [0.0072, 0], [0.0072, 0.0240], [0, 0.0240],
    ], 12), M.steel);
    hinge.rotation.y = Math.PI / 2;
    hinge.position.set(-0.0120, -(rh / 2 + stemH), 0);
    rear.add(hinge);
    rear.position.set(SX, SY, A.sightRearZ);
    sights.add(rear);

    // Front post inside protective ears. The POST TIP is on the sight line,
    // so it lands dead centre in the rear window by construction.
    const front = new THREE.Group();
    const postH = 0.0230;
    const fpostM = new THREE.Mesh(box(0.0038, postH, 0.0052, 0.0008), M.anod);
    fpostM.position.y = -postH / 2;
    front.add(fpostM);
    const tip = new THREE.Mesh(box(0.0062, 0.0072, 0.0066, 0.0014), M.wear);
    tip.position.y = -0.0034;
    front.add(tip);
    for (const sx of [-1, 1]) {
      const ear = new THREE.Mesh(box(0.0042, 0.0320, 0.0075, 0.0012), M.steel);
      ear.position.set(sx * 0.0116, -0.0090, 0);
      ear.rotation.z = -sx * 0.055;
      front.add(ear);
    }
    const fstemH = SY - postH - R * 0.90;
    const fstem = new THREE.Mesh(box(0.0150, Math.max(0.004, fstemH), 0.0150, 0.0022), M.steel);
    fstem.position.set(0, -postH - fstemH / 2, 0);
    front.add(fstem);
    const fcollar = new THREE.Mesh(tube([
      [R * 0.94, 0], [R * 1.12, 0], [R * 1.12, 0.0170], [R * 0.94, 0.0170],
    ], 18), M.steel);
    fcollar.position.set(-SX, -SY, 0.0085);
    front.add(fcollar);
    front.position.set(SX, SY, A.sightFrontZ);
    sights.add(front);

    g.add(sights);
    this.optic = sights;

    // Empty PGO-7 mounting bracket on the left of the tube. The boss is on
    // every RPG-7 ever issued and it is a chunk of the silhouette; the optic
    // itself is not fitted, which is what the shipped viewmodels show.
    const boss = new THREE.Mesh(box(0.0130, 0.0190, 0.0480, 0.0026), M.anod);
    boss.position.set(-(R + 0.0052), -0.0090, 0.1300);
    g.add(boss);
    const dovetail = new THREE.Mesh(box(0.0080, 0.0070, 0.0520, 0.0016), M.wear);
    dovetail.position.set(-(R + 0.0098), 0.0010, 0.1300);
    g.add(dovetail);

    /* ---- rear pistol grip and trigger ---- */
    const gripY = -R - 0.0140 - A.gripLen / 2;
    const gripZ = zF + 0.402;
    const grip = new THREE.Group();
    const gcore = new THREE.Mesh(box(0.0300, A.gripLen, 0.0440, 0.0060), M.matte);
    grip.add(gcore);
    const flare = new THREE.Mesh(box(0.0350, 0.0100, 0.0480, 0.0040), M.matte);
    flare.position.y = -A.gripLen / 2 - 0.002;
    grip.add(flare);
    const gstrap = new THREE.Mesh(box(0.0270, A.gripLen * 0.80, 0.0060, 0.0022), M.rubber);
    gstrap.position.set(0, 0.004, -0.0220);
    grip.add(gstrap);
    grip.position.set(0, gripY, gripZ);
    grip.rotation.x = A.gripAngle;
    g.add(grip);
    this.gripNode = grip;

    const yoke = new THREE.Mesh(box(0.0320, 0.0220, 0.0560, 0.0040), M.steel);
    yoke.position.set(0, -R - 0.0060, gripZ - 0.004);
    g.add(yoke);
    const gbar = new THREE.Mesh(box(0.0100, 0.0055, 0.0400, 0.0016), M.steel);
    gbar.position.set(0, -R - 0.0480, gripZ - 0.036);
    g.add(gbar);
    for (const [zz, rx] of [[gripZ - 0.054, -0.28], [gripZ - 0.017, 0.26]]) {
      const post = new THREE.Mesh(box(0.0100, 0.0260, 0.0072, 0.0014), M.steel);
      post.position.set(0, -R - 0.0340, zz);
      post.rotation.x = rx;
      g.add(post);
    }
    const trg = new THREE.Mesh(box(0.0060, 0.0210, 0.0060, 0.0014), M.wear);
    trg.position.set(0, -R - 0.0300, gripZ - 0.038);
    trg.rotation.x = 0.14;
    g.add(trg);
    this.triggerMesh = trg;
    this._trgZ = trg.position.z;
    // Hammer housing behind the grip, and the safety catch above the trigger.
    const hh = new THREE.Mesh(box(0.0240, 0.0300, 0.0240, 0.0035), M.steel);
    hh.position.set(0, -R - 0.0140, gripZ + 0.030);
    g.add(hh);
    const saf = new THREE.Mesh(box(0.0050, 0.0090, 0.0180, 0.0012), M.wear);
    saf.position.set(-0.0170, -R - 0.0110, gripZ - 0.030);
    g.add(saf);

    /* ---- forward grip ---- */
    const fgY = -R - 0.0120, fgZ = zF + 0.176;
    const fgrip = new THREE.Group();
    const fpost = new THREE.Mesh(tube([
      [0, 0], [0.0148, 0], [0.0165, 0.012], [0.0165, 0.052],
      [0.0182, 0.062], [0.0182, 0.070], [0, 0.072],
    ], 16), M.matte);
    fpost.rotation.x = Math.PI / 2;
    fpost.position.y = 0.002;
    fgrip.add(fpost);
    const collar = new THREE.Mesh(box(0.0280, 0.0180, 0.0300, 0.0030), M.steel);
    collar.position.y = 0.010;
    fgrip.add(collar);
    fgrip.position.set(0, fgY, fgZ);
    g.add(fgrip);
    this.foreGrip = fgrip;
    this._fgPost = new THREE.Vector3(0, fgY - 0.034, fgZ);

    /* ---- sight line and muzzle ---- */
    this._setIrons(g, SX, SY, A.sightRearZ, A.ironRelief);

    this.muzzle = new THREE.Object3D();
    this.muzzle.position.set(0, 0, zF - 0.030);
    g.add(this.muzzle);

    // The reload rig wants a `magazine`; on a launcher that is the warhead.
    this.magazine = wh;
    this._magHome = wh.position.clone();
    this._magRotHome = 0;

    this.gripAnchor = new THREE.Vector3(0, gripY, gripZ);
  }

  /* ------------------------------------------------------------- hands --- */

  /** Orthonormal basis from a fingers direction and a dorsal direction. */
  static _basis(fx, fy, fz, dx, dy, dz, out) {
    _ax.set(fx, fy, fz).normalize();
    _ay.set(dx, dy, dz);
    _ay.sub(_ax.clone().multiplyScalar(_ay.dot(_ax))).normalize();
    _az.crossVectors(_ax, _ay);
    _m.makeBasis(_ax, _ay, _az);
    return out.setFromRotationMatrix(_m);
  }

  /**
   * Gloved hand: palm, four segmented fingers, a two-segment thumb and a
   * forearm. No skinning — a joint chain posed into a grip is what actually
   * reads on screen, and it can be animated per-finger for the trigger pull.
   */
  _hand(side) {
    const M = this.mats;
    // Hand scale. Measured against the gun: a real palm is about twice the
    // diameter of an AR handguard, and at 0.94 this hand was well under that,
    // which is exactly why it read as a small lump stuck to the tube rather
    // than as something gripping it.
    const S = 1.15;
    const hand = new THREE.Group();
    // Handedness: with X along the fingers and Y out of the back of the hand,
    // the thumb lies on -Z for a right hand and +Z for a left one.
    const th = side === 'r' ? -1 : 1;

    // Palm, as four slabs that widen and thin from wrist to knuckles. A single
    // box reads as a brick; the taper is the whole reason a hand looks like a
    // hand in silhouette, and silhouette is all you get at viewmodel size.
    const slabs = [
      [0.006, 0.022, 0.030, 0.058],  // xCentre, xLen, yThick, zWidth
      [0.025, 0.022, 0.029, 0.065],
      [0.043, 0.021, 0.027, 0.071],
      [0.059, 0.017, 0.025, 0.074],
    ];
    for (const [xc, xl, yt, zw] of slabs) {
      // Generous chamfers: the slabs have to blend into one form, and a hard
      // step between them reads as a crumpled paper bag rather than a palm.
      const s = new THREE.Mesh(softBox(xl * 1.45 * S, yt * S, zw * S, 0.012), M.glove);
      s.position.set(xc * S, 0, 0);
      hand.add(s);
    }
    // Thenar (thumb muscle) and hypothenar (pinky heel) pads: without these
    // the palm is a flat card and the thumb looks bolted on.
    const thenar = new THREE.Mesh(softBox(0.044 * S, 0.027 * S, 0.030 * S, 0.012), M.glove);
    thenar.position.set(0.024 * S, -0.002 * S, th * 0.024 * S);
    thenar.rotation.y = -th * 0.12;
    hand.add(thenar);
    const hypo = new THREE.Mesh(softBox(0.040 * S, 0.027 * S, 0.026 * S, 0.011), M.glove);
    hypo.position.set(0.020 * S, -0.001 * S, -th * 0.026 * S);
    hand.add(hypo);

    // Tactical glove knuckle guard. A distinct plate on the dorsal surface is
    // what tells the eye "this is the BACK of the hand" at a glance.
    const kpad = new THREE.Mesh(softBox(0.046 * S, 0.006 * S, 0.064 * S, 0.0026), M.pad);
    kpad.position.set(0.042 * S, 0.013 * S, 0);
    hand.add(kpad);
    // Knuckle bumps along the guard's leading edge.
    const kn = softBox(0.011 * S, 0.008 * S, 0.013 * S, 0.0038);
    const kxf = [];
    for (let i = 0; i < 4; i++) kxf.push([0, 0, th * (0.029 - i * 0.0195) * S]);
    const knuckles = new THREE.Mesh(scatter(kn, kxf), M.pad);
    knuckles.position.set(0.064 * S, 0.013 * S, 0);
    hand.add(knuckles);

    const fingers = [];
    const lens = [
      [0.038, 0.025, 0.019],
      [0.041, 0.028, 0.020],
      [0.038, 0.026, 0.019],
      [0.031, 0.021, 0.017],
    ];
    const thicks = [0.0150, 0.0156, 0.0146, 0.0129];
    const zOff = [0.0295, 0.0098, -0.0098, -0.0292];
    // Knuckle line is an arc, not a straight edge: index and pinky sit back.
    const xOff = [0.0685, 0.0715, 0.0695, 0.0635];
    for (let f = 0; f < 4; f++) {
      const root = new THREE.Group();
      root.position.set(xOff[f] * S, 0.003 * S, th * zOff[f] * S);
      // Fan the fingers slightly apart, pinky most.
      root.rotation.y = th * (-0.06 + f * 0.045);
      hand.add(root);
      const joints = [];
      let node = root;
      const thick = thicks[f] * S;
      for (let i = 0; i < 3; i++) {
        const j = new THREE.Group();
        node.add(j);
        const L = lens[f][i] * S;
        // Segments taper toward the tip and the last one is rounded off.
        const t = thick * (1 - i * 0.09);
        const seg = new THREE.Mesh(limb(L * 1.04, t), M.glove);
        seg.position.x = L * 0.5;
        j.add(seg);
        if (i < 2) {
          // Knuckle swell at the joint, so a curled finger creases instead of
          // hinging like a mechanical linkage.
          const b = new THREE.Mesh(limb(t * 0.86, t * 1.06), M.glove);
          b.position.x = L;
          j.add(b);
        }
        const nx = new THREE.Group();
        nx.position.x = L;
        j.add(nx);
        joints.push(j);
        node = nx;
      }
      fingers.push({ root, joints });
    }

    // Thumb: three joints. The metacarpal is what lets the thumb swing across
    // the palm instead of hinging off the wrist like a crab claw.
    const tRoot = new THREE.Group();
    tRoot.position.set(0.014 * S, -0.004 * S, th * 0.030 * S);
    hand.add(tRoot);
    const thumb = [];
    let tn = tRoot;
    const tl = [0.030, 0.027, 0.021];
    for (let i = 0; i < 3; i++) {
      const j = new THREE.Group();
      tn.add(j);
      const L = tl[i] * S;
      const t = (0.022 - i * 0.002) * S;
      const seg = new THREE.Mesh(limb(L * 1.05, t, 0.96), M.glove);
      seg.position.x = L * 0.5;
      j.add(seg);
      const nx = new THREE.Group();
      nx.position.x = L;
      j.add(nx);
      thumb.push(j);
      tn = nx;
    }

    // Open ring, not a capped cylinder: a cap here reads as a giant disc
    // stuck to the back of the hand.
    const cuff = new THREE.Mesh(tube([
      [0.024 * S, 0], [0.027 * S, 0.004], [0.027 * S, 0.017], [0.024 * S, 0.021],
      [0.0215 * S, 0.017], [0.0215 * S, 0.004], [0.024 * S, 0],
    ], 16), M.pad);
    cuff.rotation.y = Math.PI / 2;
    cuff.position.set(-0.004 * S, 0, 0);
    hand.add(cuff);

    // The forearm is NOT parented to the hand: it aims from the wrist at a
    // fixed elbow anchor near the body, so the wrist can flex independently
    // and the arm still points somewhere anatomically believable.
    const arm = new THREE.Group();
    const sleeve = new THREE.Mesh(tube([
      [0, 0], [0.0235 * S, 0.001], [0.0255 * S, 0.016], [0.0295 * S, 0.075],
      [0.0345 * S, 0.165], [0.0360 * S, 0.240], [0, 0.240],
    ], 18), M.sleeve);
    sleeve.geometry.rotateY(Math.PI); // lookAt() aims +Z, so run the taper on +Z
    arm.add(sleeve);
    // Cuff seam where the glove gauntlet closes over the sleeve.
    const seam = new THREE.Mesh(tube([
      [0.0268 * S, 0], [0.0290 * S, 0.003], [0.0290 * S, 0.026], [0.0268 * S, 0.029],
    ], 18), M.pad);
    seam.geometry.rotateY(Math.PI);
    seam.position.z = 0.010;
    arm.add(seam);
    // Elastic strap across the gauntlet.
    const strap = new THREE.Mesh(tube([
      [0.0300 * S, 0], [0.0316 * S, 0.002], [0.0316 * S, 0.009], [0.0300 * S, 0.011],
    ], 18), M.rubber);
    strap.geometry.rotateY(Math.PI);
    strap.position.z = 0.040;
    arm.add(strap);

    // `knuckle` is the middle-finger MCP in hand-local space. Posing a grip is
    // much easier when you can say "put the knuckles *there*" and solve for the
    // wrist, rather than guessing a wrist position and seeing where the
    // fingers land.
    return {
      group: hand, fingers, thumb, arm, scale: S,
      knuckle: new THREE.Vector3(xOff[1] * S, 0.003 * S, 0),
    };
  }

  /**
   * Place a hand so its knuckle row lands on `target`, given an already-set
   * orientation. Returns the wrist position that was applied.
   */
  static _placeByKnuckle(h, target) {
    // Build-time only, so a fresh vector rather than the shared scratch — the
    // caller usually wants to hand us `_v` as the target.
    const off = h.knuckle.clone().applyQuaternion(h.group.quaternion);
    h.group.position.copy(target).sub(off);
    return h.group.position;
  }

  _hands(g) {
    const A = this.art;
    const bottom = -0.021;
    const fam = A.family;

    // Right hand on the pistol grip.
    //
    // Derived from the grip's own frame rather than authored by eye. `gU` runs
    // up the backstrap, `gF` out through the front strap, and grip-local
    // coordinates are (right, up-the-grip, rearward).
    //
    // The load-bearing fact is that a firing grip is NOT a fist closed around a
    // horizontal bar: the metacarpals run roughly PERPENDICULAR to the grip
    // axis, so the palm covers the backstrap, the knuckle row lands on the
    // front-right corner, and the web between thumb and index is driven up hard
    // under the tang. The previous pose had the metacarpals pointing down and
    // forward — a horizontal-bar grip — which dropped the web 40 mm below the
    // tang and left the hand closed around thin air ahead of the grip.
    //
    // With X (metacarpals) perpendicular to the grip axis and Y (back of hand)
    // outboard, Z falls parallel to the grip axis, which is exactly what puts
    // the thumb UP the receiver toward the selector instead of across it.
    const ga = A.gripAngle;
    const gU = new THREE.Vector3(0, Math.cos(ga), Math.sin(ga));
    const gF = new THREE.Vector3(0, Math.sin(ga), -Math.cos(ga));
    const gP = this.gripAnchor
      ? this.gripAnchor.clone()
      : new THREE.Vector3(0, bottom - 0.036 - A.gripLen / 2, 0.176);
    /** Grip-local (right, up, rear) -> model space. */
    const gLoc = (x, y, z, out = new THREE.Vector3()) => out.set(x, 0, 0)
      .addScaledVector(gU, y).addScaledVector(gF, -z).add(gP);

    const rh = this._hand('r');
    this.rh = rh;

    /*
     * A knife is not a pistol.
     *
     * The melee weapon had no grip solver of its own and fell through to the
     * pistol path below, which solves a hand onto a vertical grip hanging under
     * a receiver. On the knife that put the fist 110 mm BELOW the blade and
     * 70 mm behind the pommel — nothing was holding anything, and what showed
     * on screen was a hand floating near the blade.
     *
     * A knife handle is a short bar lying along the bore axis, so the grip is
     * the same problem as the support hand on a handguard, rotated for the
     * right hand: fingers curl in the plane perpendicular to the handle, which
     * puts the thumb axis ALONG it — pointing at the blade, which is where a
     * hammer grip's thumb goes.
     */
    if (fam === 'knife') {
      this._knifeGrip(rh, A);
      g.add(rh.group);
      g.add(rh.arm);

      // The off hand still has to exist — `_aimArms` walks both every frame —
      // it just is not on the weapon. Park it low and outboard, off frame,
      // where a knife-fighter's free hand actually sits.
      const lh = this._hand('l');
      this.lh = lh;
      g.add(lh.group);
      g.add(lh.arm);
      this._parkOffHand(lh);
      lh.thumbRest = [lh.thumb[0].rotation.clone(), lh.thumb[1].rotation.z, lh.thumb[2].rotation.z];

      this._lhHome = { p: lh.group.position.clone(), q: lh.group.quaternion.clone() };
      this._rhHome = { p: rh.group.position.clone(), q: rh.group.quaternion.clone() };
      this._rhElbow = new THREE.Vector3(0.104, -0.360, 0.300);
      this._lhElbow = new THREE.Vector3(-0.090, -0.380, 0.290);
      this.trigJoints = null;
      return;
    }

    _v.copy(gF).multiplyScalar(0.92);
    _v.x -= 0.30;                       // knuckles carry round to the left
    _v.addScaledVector(gU, -0.22);      // hand canted slightly muzzle-down
    Weapon._basis(_v.x, _v.y, _v.z, 1, 0, 0, rh.group.quaternion);
    // Knuckle row one glove thickness proud of the front strap, high on the
    // grip. Everything else — wrist, web, thumb — follows from this.
    Weapon._placeByKnuckle(rh, gLoc(0.019, 0.006, -0.027, _v));
    g.add(rh.group);
    g.add(rh.arm);
    // Middle/ring/pinky close hard on the grip; the index is the trigger
    // finger and is posed separately below.
    //
    // The turn is loaded into the first two joints so the phalanges hug the
    // front strap and the tips tuck against the left flank. Spread evenly they
    // trace a circle far bigger than the grip and the fingertips end up waving
    // in the air a centimetre clear of it.
    const rCurl = [
      [-1.10, -1.45, -0.45],
      [-1.20, -1.55, -0.45],
      [-1.22, -1.52, -0.42],
      [-1.24, -1.45, -0.40],
    ];
    rh.rest = rCurl;
    for (let f = 0; f < 4; f++) {
      for (let i = 0; i < 3; i++) rh.fingers[f].joints[i].rotation.z = rCurl[f][i];
    }
    // Thumb.
    //
    // On a long gun: NOT wrapped round the grip. It rides up the LEFT flank of
    // the receiver where a right-handed shooter's thumb works the selector,
    // which is also what stops it from fouling the trigger finger.
    //
    // On a pistol it is a different animal entirely. Every modern two-handed
    // pistol grip is THUMBS FORWARD: the firing thumb comes off the backstrap
    // and lies flat along the left of the frame pointing at the target, with
    // the support thumb stacked directly underneath it. Both thumbs make one
    // continuous line down the left of the slide, and that line is the single
    // loudest cue that the person holding the gun knows what they are doing.
    // Curled round the backstrap instead — the "teacup" of a 1970s cop show —
    // it reads as wrong to anyone who has held one.
    if (fam === 'pistol') {
      // ONE-HANDED, so this is not the thumbs-forward pose any more. That pose
      // exists to stack the firing thumb on top of the support thumb, and with
      // no support hand a thumb held straight out along the frame just reads as
      // a spare finger pointing at nothing.
      //
      // A one-handed pistol thumb rides UP the frame and folds slightly in
      // toward the slide — high enough to stay clear of the slide's travel,
      // curled enough to look like it is gripping rather than gesturing.
      rh.thumb[0].rotation.set(0, 0.06, 0.30);
      rh.thumb[1].rotation.z = -0.46;
      rh.thumb[2].rotation.z = -0.30;
    } else {
      rh.thumb[0].rotation.set(0, 0.30, 0.20);
      rh.thumb[1].rotation.z = -0.80;
      rh.thumb[2].rotation.z = -0.55;
    }
    rh.thumbRest = [rh.thumb[0].rotation.clone(), rh.thumb[1].rotation.z, rh.thumb[2].rotation.z];

    // Trigger finger.
    //
    // Two poses, not one. `trigIndexed` is the register position: finger
    // straight, laid along the outside of the lower receiver, clear of the
    // trigger entirely — trigger discipline is one of the loudest authenticity
    // cues a viewmodel has, and a finger permanently curled through the guard
    // reads as a toy. `trigOn` puts the PAD of the distal phalanx on the face
    // of the trigger with the proximal segment running parallel to the
    // receiver; contacting with the first joint instead is what pushes real
    // shots sideways. update() springs between the two.
    //
    // The index root is pulled back down the palm. On a real hand the first
    // web space sits AT the index knuckle, so with the web against the
    // backstrap the index MCP is ~60 mm behind the trigger and the finger has
    // room to reach forward and hook back onto it. Left at the front of a flat
    // slab palm the knuckle ends up 20 mm from the trigger and no combination
    // of joint angles can put the pad on the blade — the finger simply
    // overshoots past the guard.
    rh.fingers[0].root.position.x -= 0.024;
    this.trigJoints = rh.fingers[0].joints;
    this.trigIndexed = [0.33, -0.06, -0.06];
    this.trigOn = [0.66, -1.55, -0.45];
    for (let i = 0; i < 3; i++) this.trigJoints[i].rotation.z = this.trigIndexed[i];

    // Left hand: support grip wrapping the handguard.
    //
    // The previous two attempts both failed on the same geometry, so the
    // reasoning is worth writing down.
    //
    // A finger curls in the plane spanned by (its own direction, the palm
    // normal). To wrap a cylinder, that plane has to be PERPENDICULAR to the
    // cylinder's axis — which forces the remaining axis of the hand, the thumb
    // axis, to lie parallel to the bore. So the hand's orientation has exactly
    // one degree of freedom left: the roll about the bore. Everything else
    // follows.
    //
    // That single freedom then has to satisfy two things at once, and can only
    // manage one and a half:
    //   * the wrist must end up BELOW the bore, because the support arm comes
    //     up from the shooter's left hip and any pose that puts the wrist above
    //     the receiver drags the forearm straight across the optic;
    //   * the back of the hand should face the camera, because the knuckle
    //     guard is the silhouette that makes a gloved hand read as a hand.
    // With the wrist below, the fingers necessarily point up and to the right —
    // and for a LEFT hand, fingers up-right with the back of the hand facing
    // up-left puts the thumb axis pointing REARWARD along the handguard. A
    // thumb-forward C-clamp is the mirror of this pose and puts the wrist above
    // the receiver. So the thumb lies back along the top-left of the guard
    // instead of forward: an ordinary firm overhand support grip, which is what
    // the arm geometry actually allows.
    const lh = this._hand('l');
    this.lh = lh;
    g.add(lh.group);
    g.add(lh.arm);
    // The pistol is shot ONE-HANDED. A support hand cupping the firing hand is
    // the hardest grip in the game to make read: two gloved hands interlocking
    // at 180 mm from the lens is a single unreadable mass, and every attempt at
    // it has looked like one hand growing out of another. A clean isolated
    // firing hand is both easier to read and perfectly normal for a sidearm.
    //
    // The hand still exists and is still rigged — the reload needs it to carry
    // the magazine — it is simply parked off frame and hidden until then. See
    // `_oneHanded` in update().
    if (fam === 'pistol') this._parkOffHand(lh);
    else if (fam === 'launcher') this._supportOnPost(lh, this._fgPost);
    else this._supportOnTube(lh, fam === 'shotgun' ? A.forendR : A.hgR, A.lhZ, A.lhY);
    lh.thumbRest = [lh.thumb[0].rotation.clone(), lh.thumb[1].rotation.z, lh.thumb[2].rotation.z];

    this._lhHome = { p: lh.group.position.clone(), q: lh.group.quaternion.clone() };
    this._rhHome = { p: rh.group.position.clone(), q: rh.group.quaternion.clone() };

    // Elbow anchors sit down and back where the shooter's body would be. The
    // left one is pushed out to the side so the support forearm crosses the
    // frame on a diagonal instead of hanging as a vertical post.
    if (fam === 'pistol') {
      // Both arms are pushed out in front of the chest, so both elbows sit
      // well back and close to the centreline — a pistol shooter's forearms
      // converge, they do not splay like a rifle's support arm.
      //
      // Anchored at y = -0.29 the two forearms came in almost horizontally
      // from the left and right edges and read as a pair of scaffold poles
      // either side of the gun. Every pistol ADS capture — MWR, Ghosts, MW3 —
      // shows the arms rising from the BOTTOM of the frame and converging on
      // the grip, so the anchors drop well below the sight line and tuck in
      // toward the centreline.
      this._lhElbow = new THREE.Vector3(-0.074, -0.430, 0.238);
      this._rhElbow = new THREE.Vector3(0.058, -0.416, 0.272);
    } else if (fam === 'launcher') {
      // The tube is ON the shoulder, which lifts the firing elbow and throws
      // the support arm right out to the side to reach the forward grip.
      this._lhElbow = new THREE.Vector3(-0.245, -0.360, 0.170);
      this._rhElbow = new THREE.Vector3(0.190, -0.215, 0.360);
    } else if (fam === 'shotgun') {
      this._lhElbow = new THREE.Vector3(-0.205, -0.360, 0.130);
      this._rhElbow = new THREE.Vector3(0.155, -0.250, 0.350);
    } else {
      this._lhElbow = new THREE.Vector3(-0.190, -0.335, 0.120);
      this._rhElbow = new THREE.Vector3(0.150, -0.250, 0.345);
    }
    this._initLeftArmIK(g);
    this._aimArms();

    // Reload keyframes. Same convention as the grip pose: first triple is the
    // finger direction, second is the back of the hand. Getting these wrong is
    // very visible — a hand that rolls to a random orientation halfway through
    // a reload reads as a broken wrist even at speed.
    //
    // Down to the pouch: fingers reach down and across the body, back of the
    // hand still toward the camera so the glove keeps reading.
    this._lhMag = {
      p: new THREE.Vector3(0.020, -0.295, 0.075),
      q: new THREE.Quaternion(),
    };
    Weapon._basis(0.55, -0.78, 0.30, -0.62, 0.02, 0.78, this._lhMag.q);
    // Fresh mag presented under the magwell: fingers wrapped round the mag
    // body, thumb up the spine, hand below and left of the well.
    this._lhInsert = {
      p: new THREE.Vector3(-0.016, -0.172, 0.048),
      q: new THREE.Quaternion(),
    };
    Weapon._basis(0.86, 0.16, 0.48, -0.42, 0.28, 0.86, this._lhInsert.q);
    // Palm slaps the bolt catch on the left of the receiver.
    this._lhBolt = {
      p: new THREE.Vector3(-0.092, -0.030, 0.078),
      q: new THREE.Quaternion(),
    };
    Weapon._basis(0.92, 0.34, 0.18, -0.30, 0.94, -0.16, this._lhBolt.q);

    // Family overrides: the magwell is in a different place on every one of
    // these, and a hand that inserts a magazine into thin air is worse than no
    // reload animation at all.
    if (fam === 'pistol') {
      // Authored at y = -0.300 / -0.128 these two keyframes put the support
      // hand a full screen-height below the frame: the entire magazine change
      // happened out of shot. Raised until the hand and the fresh magazine are
      // actually on screen, the way the Ghosts M9A1 reload frames them.
      this._lhMag.p.set(0.052, -0.212, 0.116);
      // Fresh magazine presented under the grip, canted so the feed lips lead
      // into the well the way a real reload indexes off the front of the mag.
      this._lhInsert.p.set(gP.x - 0.034, gP.y - 0.062, gP.z + 0.028);
      Weapon._basis(0.30, 0.90, 0.32, -0.30, 0.24, -0.92, this._lhInsert.q);
      // Support palm sweeps back onto the slide stop on the left of the frame.
      this._lhBolt.p.set(-0.078, gP.y + 0.086, gP.z - 0.030);
    } else if (fam === 'launcher') {
      // A rocket is fetched from the back and pushed into the MUZZLE, so the
      // support hand travels the whole length of the tube.
      this._lhMag.p.set(0.062, -0.186, 0.208);
      this._lhInsert.p.set(-0.020, -0.070, A.tubeFront - 0.070);
      Weapon._basis(0.20, 0.36, -0.91, -0.36, 0.90, 0.28, this._lhInsert.q);
    } else if (fam === 'shotgun') {
      // Two stations, shuttled between once per shell: the carrier on the
      // chest rig, and the loading port under the receiver.
      // Same correction as the pistol: both stations were below the frame, so
      // the signature shell-by-shell animation was invisible.
      this._lhShellBelt = { p: new THREE.Vector3(0.104, -0.200, 0.138), q: new THREE.Quaternion() };
      Weapon._basis(0.46, -0.80, 0.38, -0.56, 0.06, 0.83, this._lhShellBelt.q);
      this._lhShellPort = { p: new THREE.Vector3(-0.024, -0.086, 0.044), q: new THREE.Quaternion() };
      Weapon._basis(0.62, 0.68, 0.39, -0.42, 0.68, -0.60, this._lhShellPort.q);
    }

    // MP5: the HK slap.
    //
    // The factory manual of arms is not a bolt release — there isn't one. The
    // bolt is held back by the cocking lever hooked up in its notch, and you
    // chamber by knocking the lever out of the notch with the meaty edge of the
    // support palm and letting the recoil spring slam the bolt home.
    //
    // So the hand has to arrive as a karate chop, not a press: fingers forward
    // and inboard, thumb UP, which puts the hypothenar edge of the palm facing
    // straight down onto the paddle.
    if (A.chargeStyle === 'hk' && this.charge) {
      const c = this.charge.position;
      // Where the paddle sits once it is rotated up into the notch.
      const kx = c.x - 0.035 * Math.cos(0.95), ky = c.y + 0.035 * Math.sin(0.95);
      const q = new THREE.Quaternion();
      Weapon._basis(-0.35, 0.0, -0.94, -0.94, 0.0, 0.35, q);
      // Wrist solved back from the striking point on the palm edge, hand-local
      // (0.026, 0, -0.038): forward*0.026 down*0.038 off the wrist.
      const strike = new THREE.Vector3(kx, ky + 0.010, c.z + 0.004);
      const wrist = strike.clone().sub(new THREE.Vector3(-0.009, -0.038, -0.024));
      this._lhSlap = { p: wrist, q };
      this._lhSlapUp = { p: wrist.clone().add(new THREE.Vector3(-0.012, 0.062, 0.026)), q };
    } else {
      this._lhSlap = this._lhSlapUp = null;
    }
  }

  /**
   * Support hand wrapped round a horizontal tube — a rifle handguard or a
   * shotgun forend. `yC` offsets the tube off the bore, which is how the
   * shotgun's forend rides 27 mm below the barrel.
   *
   * The previous two attempts both failed on the same geometry, so the
   * reasoning is worth writing down.
   *
   * A finger curls in the plane spanned by (its own direction, the palm
   * normal). To wrap a cylinder, that plane has to be PERPENDICULAR to the
   * cylinder's axis — which forces the remaining axis of the hand, the thumb
   * axis, to lie parallel to the bore. So the hand's orientation has exactly
   * one degree of freedom left: the roll about the bore. Everything else
   * follows.
   *
   * That single freedom then has to satisfy two things at once, and can only
   * manage one and a half:
   *   * the wrist must end up BELOW the bore, because the support arm comes
   *     up from the shooter's left hip and any pose that puts the wrist above
   *     the receiver drags the forearm straight across the optic;
   *   * the back of the hand should face the camera, because the knuckle
   *     guard is the silhouette that makes a gloved hand read as a hand.
   * With the wrist below, the fingers necessarily point up and to the right —
   * and for a LEFT hand, fingers up-right with the back of the hand facing
   * up-left puts the thumb axis pointing REARWARD along the handguard. A
   * thumb-forward C-clamp is the mirror of this pose and puts the wrist above
   * the receiver. So the thumb lies back along the top-left of the guard
   * instead of forward: an ordinary firm overhand support grip, which is what
   * the arm geometry actually allows.
   */
  /**
   * Support hand wrapped around a handguard/forend.
   *
   * CONSTRUCTED FROM GEOMETRY, not tuned by eye. Read
   * `docs/reference/grips/README.md` before touching this.
   *
   * The failure it replaces: the palm sat 92 mm from a bore axis whose
   * handguard is 30 mm in radius, so only the fingertips reached the gun and
   * the hand read as a closed fist floating beside the rail. The cause was the
   * palm facing the wrong way — with the back of the hand not pointing radially
   * outward, the wrist swings out on a radius instead of cupping the tube.
   *
   * The construction, with theta the angle round the tube where the palm sits:
   *   Y (back of hand) = outward radial  = ( cos t, sin t, 0)
   *   X (metacarpals)  = tangential      = (-sin t, cos t, 0)
   *   Z = X x Y = -Z model, which is the bore pointing at the muzzle — and for
   *       a LEFT hand the thumb lies on +Z local, so the thumb comes out along
   *       the guard toward the muzzle for free.
   * The palm then faces the axis, the knuckle row sits one glove thickness off
   * the surface, and the fingers curl INWARD around it.
   *
   * Curl follows from arc length, not taste: the finger chain is ~94 mm long
   * and the wrap circle at glove radius is 2*pi*(R+12), so the fingers close
   * about 130 deg total — roughly 0.75 rad per joint. Curling them like a fist
   * (~1.5 rad/joint) leaves no room for the gun inside the hand, which is
   * exactly how the floating fist happened.
   */
  _supportOnTube(lh, R, z, yC = 0) {
    // Where round the tube the palm sits. Exposed as a static so it can be
    // swept and CAPTURED rather than guessed — see tools/grip-sweep.mjs.
    const t = Weapon.GRIP_THETA;
    const ct = Math.cos(t), st = Math.sin(t);
    Weapon._basis(-st, ct, 0, ct, st, 0, lh.group.quaternion);

    // Knuckle row on the surface, one glove thickness proud.
    const kr = R + 0.012;
    Weapon._placeByKnuckle(lh, _v.set(ct * kr, st * kr + yC, z));

    // Arc length: a bigger tube needs a shallower curl to trace it.
    const span = 2.30;                    // total radians of wrap per finger
    const per = span / 3 * THREE.MathUtils.clamp(0.030 / R, 0.72, 1.35);
    const lCurl = [
      [-per * 0.95, -per * 1.05, -per * 0.95],
      [-per * 1.00, -per * 1.08, -per * 0.98],
      [-per * 1.00, -per * 1.05, -per * 0.95],
      [-per * 0.95, -per * 0.98, -per * 0.88],
    ];
    lh.rest = lCurl;
    for (let f = 0; f < 4; f++) {
      for (let i = 0; i < 3; i++) lh.fingers[f].joints[i].rotation.z = lCurl[f][i];
    }

    // Thumb along the near face toward the muzzle, opposing the fingers.
    lh.thumb[0].rotation.set(0, 0.15, 0.55);
    lh.thumb[1].rotation.z = -0.30;
    lh.thumb[2].rotation.z = -0.22;
  }

  /**
   * Support hand cupping the firing hand on a pistol — thumbs-forward.
   *
   * The support palm fills the gap on the LEFT of the grip and its fingers lie
   * ACROSS the firing hand's fingers, so the two read as one mass rather than
   * two fists either side of the gun. Tipping it forward over the front strap
   * closed the fingertip gap from 46-49 mm to 28 mm.
   */
  /**
   * Park the off hand out of frame, low and outboard, where a shooter's free
   * hand actually sits. Used by the knife and the pistol — anything fired one
   * handed. The fingers are given a relaxed curl rather than left splayed,
   * because the hand is briefly visible at the start of a reload.
   */
  _parkOffHand(lh) {
    this._oneHanded = true;
    Weapon._basis(0.2, 0.9, 0.3, -0.9, 0.2, 0.1, lh.group.quaternion);
    lh.group.position.set(-0.150, -0.230, 0.180);
    const curl = [
      [-0.55, -0.70, -0.50],
      [-0.60, -0.75, -0.52],
      [-0.60, -0.72, -0.50],
      [-0.56, -0.64, -0.44],
    ];
    lh.rest = curl;
    for (let f = 0; f < 4; f++) {
      for (let i = 0; i < 3; i++) lh.fingers[f].joints[i].rotation.z = curl[f][i];
    }
    lh.thumb[0].rotation.set(0, 0.10, 0.34);
    lh.thumb[1].rotation.z = -0.30;
    lh.thumb[2].rotation.z = -0.20;
  }

  _supportCup(lh, gLoc) {
    Weapon._basis(0.94, -0.30, 0.14, -0.27, -0.90, -0.34, lh.group.quaternion);
    _q.setFromAxisAngle(_xAxis, -0.4);
    lh.group.quaternion.premultiply(_q);
    Weapon._placeByKnuckle(lh, gLoc(-0.020, -0.002, -0.068, _v));
    // A support hand crossing a ~45 mm bundle of grip-plus-fingers barely
    // closes past 90 degrees; curled like a fist it grips thin air.
    const lCurl = [
      [-0.52, -0.82, -0.60],
      [-0.56, -0.86, -0.62],
      [-0.56, -0.84, -0.58],
      [-0.54, -0.74, -0.50],
    ];
    lh.rest = lCurl;
    for (let f = 0; f < 4; f++) {
      for (let i = 0; i < 3; i++) lh.fingers[f].joints[i].rotation.z = lCurl[f][i];
    }
    // Support thumb straight and forward, directly under the firing thumb:
    // the two make one line down the left of the slide.
    lh.thumb[0].rotation.set(0, -0.06, 0.30);
    lh.thumb[1].rotation.z = -0.12;
    lh.thumb[2].rotation.z = -0.08;
  }

  /**
   * Right hand closed round a knife handle — a hammer grip.
   *
   * Solved by sweeping the knuckle row round the handle axis and reading the
   * wrist back: at 4.4 rad it lands 71 mm right of the blade and 49 mm below
   * it, mid-handle, with the forearm coming in from the lower right.
   */
  _knifeGrip(rh, A) {
    const hz = A.gripLen * 0.46;          // sit the fist mid-handle
    const hy = -0.002;
    const r = 0.0125;                     // handle half-thickness
    const ang = 4.4;
    const nx = Math.cos(ang), ny = Math.sin(ang);
    Weapon._basis(ny, -nx, 0, nx, ny, 0, rh.group.quaternion);
    Weapon._placeByKnuckle(rh, _v.set(nx * (r + 0.014), hy + ny * (r + 0.014), hz));

    // A handle this thin needs most of a fist — unlike a handguard, there is
    // very little inside the grip.
    const curl = [
      [-1.37, -1.95, -1.37],
      [-1.43, -2.02, -1.37],
      [-1.43, -1.95, -1.30],
      [-1.37, -1.79, -1.17],
    ];
    rh.rest = curl;
    for (let f = 0; f < 4; f++) {
      for (let i = 0; i < 3; i++) rh.fingers[f].joints[i].rotation.z = curl[f][i];
    }
    rh.thumb[0].rotation.set(0, 0.18, 0.30);
    rh.thumb[1].rotation.z = -0.30;
    rh.thumb[2].rotation.z = -0.22;
    rh.thumbRest = [rh.thumb[0].rotation.clone(), rh.thumb[1].rotation.z, rh.thumb[2].rotation.z];
  }

  /**
   * Support hand on a VERTICAL foregrip — the RPG's forward grip.
   *
   * Same constraint as the tube grip, rotated 90°: the curl plane must be
   * perpendicular to the post, so the thumb axis runs UP the post and the
   * fingers sweep horizontally round it. The hand comes in from the left, so
   * the knuckle row lands on the near-left of the post and the fingertips come
   * round the far side.
   */
  _supportOnPost(lh, post) {
    Weapon._basis(0.55, 0.0, -0.80, -0.80, 0.10, -0.55, lh.group.quaternion);
    Weapon._placeByKnuckle(lh, _v.set(post.x - 0.030, post.y + 0.012, post.z + 0.014));
    const lCurl = [
      [-0.72, -1.10, -0.72],
      [-0.76, -1.14, -0.74],
      [-0.76, -1.10, -0.70],
      [-0.72, -0.98, -0.56],
    ];
    lh.rest = lCurl;
    for (let f = 0; f < 4; f++) {
      for (let i = 0; i < 3; i++) lh.fingers[f].joints[i].rotation.z = lCurl[f][i];
    }
    // Thumb wraps down over the top of the post rather than standing up off it.
    lh.thumb[0].rotation.set(0, -0.20, 0.34);
    lh.thumb[1].rotation.z = -0.62;
    lh.thumb[2].rotation.z = -0.42;
  }

  /* ------------------------------------------------------------ arm IK --- */

  /**
   * Left arm setup: a grip target on the weapon, and a shoulder to solve from.
   *
   * The old arrangement was backwards and that is the whole reason the support
   * wrist looked broken: the elbow was pinned to a constant point near the body
   * and the hand was posed independently, so every angular disagreement between
   * the two had nowhere to go except the wrist. Here the hand is authoritative
   * and the elbow is DERIVED from it, which is the order a real arm works in.
   *
   * Seeded from whatever the family's grip solver just produced, so switching
   * IK on changes nothing until the target is actually moved. The calibration
   * stand at /debug/weapon-grip/ is what moves it.
   */
  _initLeftArmIK(g) {
    const lh = this.lh;
    if (!lh) return;

    this.leftHandGrip = new THREE.Object3D();
    this.leftHandGrip.name = 'leftHandGrip';
    this.leftHandGrip.position.copy(lh.group.position);
    this.leftHandGrip.quaternion.copy(lh.group.quaternion);
    g.add(this.leftHandGrip);

    const cal = Weapon.LEFT_GRIP[this.spec.name];

    // The authored pose is kept so the IK weights have something to blend
    // AGAINST. Blending frame-to-frame against itself would just drift.
    this._lhAuthored = {
      p: lh.group.position.clone(),
      q: lh.group.quaternion.clone(),
    };

    this.ik = {
      enabled: true,
      weight: 1,
      posWeight: 1,
      rotWeight: 1,
      // Segment lengths. The forearm is not a free parameter: the sleeve mesh
      // built in `_hand` is 240 mm long and is drawn from wrist to elbow, so a
      // different number here would leave the elbow marker off the end of the
      // arm you can actually see.
      upper: 0.280,
      fore: 0.240,
      // Which point on the hand the grip target describes.
      anchor: 'wrist',
    };

    // Shoulder, in weapon-model space. At rest the viewmodel group is near
    // identity so this is also roughly camera space: down and left of the eye,
    // a little behind it.
    this.lhShoulder = new THREE.Vector3(-0.190, -0.240, 0.130);
    // Pole target. Seeded from the old fixed anchor so the first solve puts the
    // elbow where the shipped pose used to put it; stage 3 makes it authored.
    this.lhElbowHint = this._lhElbow.clone();

    this._initLeftFingers();

    // Calibrated poses win over whatever the family solver authored. Applied
    // last so `_lhAuthored` still holds the old pose and the IK weights can
    // still blend back to it for an A/B.
    if (cal) {
      this.leftHandGrip.position.fromArray(cal.position);
      this.leftHandGrip.quaternion.fromArray(cal.quaternion);
      this.lhShoulder.fromArray(cal.shoulder);
      this.lhElbowHint.fromArray(cal.elbowHint);
      this.ik.upper = cal.upper;
      this.ik.fore = cal.fore;
      if (cal.fingers) {
        // Either a preset name or an explicit set of values. Calibrated poses
        // use the latter — a preset is a starting point, not a result.
        Object.assign(this.lhFingers, typeof cal.fingers === 'string'
          ? Weapon.FINGER_PRESETS[cal.fingers] : cal.fingers);
        this.applyLeftFingers();
      }
    }

    /** Live solver readouts — what the calibration stand displays, and what a
     * probe asserts on. Angles in radians. */
    this.lhIK = {
      shoulder: new THREE.Vector3(),
      elbow: new THREE.Vector3(),
      wrist: new THREE.Vector3(),
      target: new THREE.Vector3(),
      insideElbow: 0,
      flexion: 0,
      wristDeviation: 0,
      targetError: 0,
      reachError: 0,
    };
  }

  /* -------------------------------------------------------- finger pose --- */

  /**
   * Support-hand finger pose as parameters rather than as a table of magic
   * joint angles.
   *
   * Seeded from whatever the family's grip solver just authored, so this
   * changes nothing until it is tuned — but from here on the curl of each
   * finger and the whole orientation of the thumb are single numbers that the
   * calibration stand can drive, which is the only way anyone is going to get
   * a thumb to lie along a handguard instead of vanishing inside the hand.
   *
   * Curl is radians PER JOINT, so it can be compared directly against the
   * arc-length figure in the grip docs: a 29.5 mm handguard needs ~0.75 rad
   * per joint, and a fist is ~1.5.
   */
  _initLeftFingers() {
    const lh = this.lh;
    if (!lh) return;
    // Mean authored curl per finger, so the parameter starts where the shipped
    // pose already is.
    const mean = (f) => -(lh.fingers[f].joints[0].rotation.z
      + lh.fingers[f].joints[1].rotation.z
      + lh.fingers[f].joints[2].rotation.z) / 3;
    this.lhFingers = {
      index: mean(0), middle: mean(1), ring: mean(2), pinky: mean(3),
      // Thumb: the metacarpal's own orientation, then curl down the chain.
      // `spread` swings it across the palm — that is the control that decides
      // whether the thumb lies along the near side of the tube or disappears
      // under the fingers.
      thumbSpread: lh.thumb[0].rotation.y,
      thumbRoll: lh.thumb[0].rotation.z,
      thumbLift: lh.thumb[0].rotation.x,
      thumbCurl: -(lh.thumb[1].rotation.z + lh.thumb[2].rotation.z) / 2,
    };
    this._lhFingersShipped = { ...this.lhFingers };
  }

  /**
   * Distribution of a finger's curl across its three joints. The middle joint
   * carries slightly more than the knuckle and the tip slightly less, which is
   * what makes a curled finger crease rather than hinge like a linkage.
   */
  static FINGER_SPLIT = [0.95, 1.08, 0.95];

  /** Named starting points. Tuning starts from one of these, not from zero. */
  static FINGER_PRESETS = {
    open: { index: 0.12, middle: 0.12, ring: 0.12, pinky: 0.14,
      thumbSpread: -0.30, thumbRoll: 0.20, thumbLift: 0, thumbCurl: 0.05 },
    // A 59 mm tube in the hand: fingers a little over a third of a turn, thumb
    // straight and lying along the near face of the guard.
    handguardGrip: { index: 0.76, middle: 0.80, ring: 0.78, pinky: 0.72,
      thumbSpread: -0.95, thumbRoll: 0.30, thumbLift: -0.10, thumbCurl: 0.06 },
    verticalForegripGrip: { index: 1.02, middle: 1.06, ring: 1.04, pinky: 0.96,
      thumbSpread: -0.20, thumbRoll: 0.34, thumbLift: 0, thumbCurl: 0.52 },
    angledForegripGrip: { index: 0.88, middle: 0.92, ring: 0.90, pinky: 0.84,
      thumbSpread: -0.62, thumbRoll: 0.38, thumbLift: -0.06, thumbCurl: 0.18 },
  };

  applyLeftFingers() {
    const lh = this.lh, F = this.lhFingers;
    if (!lh || !F) return;
    const W = Weapon.FINGER_SPLIT;
    const curls = [F.index, F.middle, F.ring, F.pinky];
    const rest = [];
    for (let f = 0; f < 4; f++) {
      const row = [];
      for (let i = 0; i < 3; i++) {
        const z = -curls[f] * W[i];
        lh.fingers[f].joints[i].rotation.z = z;
        row.push(z);
      }
      rest.push(row);
    }
    // Downstream animation (reloads, the trigger finger) reads `rest` as the
    // pose to return to, so it has to move with the parameters.
    lh.rest = rest;
    lh.thumb[0].rotation.set(F.thumbLift, F.thumbSpread, F.thumbRoll);
    lh.thumb[1].rotation.z = -F.thumbCurl;
    lh.thumb[2].rotation.z = -F.thumbCurl * 0.75;
    lh.thumbRest = [lh.thumb[0].rotation.clone(), lh.thumb[1].rotation.z, lh.thumb[2].rotation.z];
  }

  /** Hand-local offset from the wrist to whichever point the target describes. */
  _anchorOffset(lh, anchor, out) {
    if (anchor === 'knuckle') return out.copy(lh.knuckle);
    if (anchor === 'palm') return out.set(0.040 * lh.scale, 0, 0);
    return out.set(0, 0, 0);
  }

  /**
   * Two-bone IK. Given a shoulder, a wrist target and a pole hint, there is
   * exactly one elbow position per bend plane, and the hint picks the plane.
   * Standard law-of-cosines solve, written out rather than pulled from a
   * library so the degenerate cases are visible.
   */
  static _twoBone(S, T, hint, L1, L2, outElbow) {
    _ik0.subVectors(T, S);
    const reach = _ik0.length();
    const min = Math.abs(L1 - L2) + 1e-4;
    const max = L1 + L2 - 1e-4;
    const d = THREE.MathUtils.clamp(reach, min, max);
    if (reach < 1e-6) _ik0.set(0, 0, -1); else _ik0.multiplyScalar(1 / reach);

    // Angle at the shoulder between the shoulder->wrist line and the upper arm.
    const cos = THREE.MathUtils.clamp((L1 * L1 + d * d - L2 * L2) / (2 * L1 * d), -1, 1);
    const a = Math.acos(cos);

    // Bend direction: the hint, with everything along the shoulder->wrist line
    // removed. A hint sitting exactly on that line carries no plane, so fall
    // back to "down" — an arm bends downward far more often than any other way.
    _ik1.subVectors(hint, S).addScaledVector(_ik0, -_ik1.dot(_ik0));
    if (_ik1.lengthSq() < 1e-8) {
      _ik1.set(0, -1, 0).addScaledVector(_ik0, _ik0.y);
      if (_ik1.lengthSq() < 1e-8) _ik1.set(1, 0, 0);
    }
    _ik1.normalize();

    outElbow.copy(S)
      .addScaledVector(_ik0, L1 * Math.cos(a))
      .addScaledVector(_ik1, L1 * Math.sin(a));
    return reach - d;                 // >0 when the target is out of reach
  }

  /**
   * Solve the left arm from the grip target.
   *
   * `snap` writes the hand transform immediately — that is what the calibration
   * stand wants. In game the hand is left to the reload animator, which damps
   * toward `_lhHome`; this only moves that home, so a reload keyframe still
   * wins while it is playing and the hand returns to the solved grip after.
   */
  _solveLeftArm(snap = false) {
    const lh = this.lh, ik = this.ik, gt = this.leftHandGrip;
    if (!lh || !ik || !gt) return;
    const R = this.lhIK;

    // 1. Wrist transform implied by the target.
    _ikQ.copy(gt.quaternion);
    this._anchorOffset(lh, ik.anchor, _ik2).applyQuaternion(_ikQ);
    _ik3.copy(gt.position).sub(_ik2);

    // 2. Blend against the authored pose. Weight 1 is pure IK; 0 is exactly
    //    what the weapon shipped with, which makes an A/B possible instead of
    //    an argument.
    const A = this._lhAuthored;
    const pw = THREE.MathUtils.clamp(ik.weight * ik.posWeight, 0, 1);
    const rw = THREE.MathUtils.clamp(ik.weight * ik.rotWeight, 0, 1);
    _ik3.lerpVectors(A.p, _ik3, pw);
    _ikQ.copy(A.q).slerp(gt.quaternion, rw);

    this._lhHome.p.copy(_ik3);
    this._lhHome.q.copy(_ikQ);

    // A pump gun's forend hand rides the stroke: `_pumpApply` adds the change
    // in travel AFTER this runs, so the offset already applied has to be
    // carried or the solver would flatten it every frame.
    _ik3.z += this._pumpPrevDz || 0;

    // The very first solve is a snap: there is no previous pose to ease from,
    // and easing at build time leaves the hand parked partway between the
    // authored grip and the calibrated one.
    if (!this._lhSolved) { snap = true; this._lhSolved = true; }

    if (this.reloading) {
      // The reload keyframes own the hand while they play.
    } else if (snap || lh.group.position.distanceToSquared(_ik3) < 1.6e-5) {
      lh.group.position.copy(_ik3);
      lh.group.quaternion.copy(_ikQ);
    } else {
      // Coming back from a reload the hand can be 100 mm away; snapping it
      // there reads as a dropped frame.
      const k = 1 - Math.exp(-16 * (this._dt || 0.016));
      lh.group.position.lerp(_ik3, k);
      lh.group.quaternion.slerp(_ikQ, k);
    }

    // 3. Elbow from the wrist the hand ACTUALLY has — during a reload that is a
    //    keyframe, not the grip, and the arm still has to follow it.
    const W = lh.group.position;
    R.reachError = Weapon._twoBone(
      this.lhShoulder, W, this.lhElbowHint, ik.upper, ik.fore, R.elbow
    );

    // 4. Forearm mesh from the wrist to the solved elbow.
    lh.arm.position.copy(W);
    _m.lookAt(R.elbow, W, _UP);
    lh.arm.quaternion.setFromRotationMatrix(_m);

    // 5. Measurements. Eyeballing a wrist is exactly how this went wrong for
    //    seven rounds, so the angles that define the pose are computed, not
    //    judged.
    R.shoulder.copy(this.lhShoulder);
    R.wrist.copy(W);
    R.target.copy(_ik3);
    R.targetError = W.distanceTo(_ik3);

    _ik0.subVectors(this.lhShoulder, R.elbow).normalize();   // elbow -> shoulder
    _ik1.subVectors(W, R.elbow).normalize();                 // elbow -> wrist
    R.insideElbow = Math.acos(THREE.MathUtils.clamp(_ik0.dot(_ik1), -1, 1));
    R.flexion = Math.PI - R.insideElbow;

    // Wrist deviation: the angle between the forearm and the metacarpals. The
    // hand's local +X runs wrist -> knuckles, so a straight wrist is 0.
    _ik2.subVectors(W, R.elbow).normalize();                 // forearm direction
    _ik3.set(1, 0, 0).applyQuaternion(lh.group.quaternion);
    R.wristDeviation = Math.acos(THREE.MathUtils.clamp(_ik2.dot(_ik3), -1, 1));
  }

  /**
   * Point each forearm from its wrist at the elbow. The left arm is solved by
   * IK from the grip target; the right hand still uses its fixed anchor, which
   * is defensible for a firing hand welded to the pistol grip.
   *
   * Done in model space with an explicit matrix — Object3D.lookAt works in
   * world space and the model group is being animated underneath us.
   */
  _aimArms() {
    if (this.ik?.enabled && this.lh && this.leftHandGrip) this._solveLeftArm();
    else if (this.lh) {
      this.lh.arm.position.copy(this.lh.group.position);
      _m.lookAt(this._lhElbow, this.lh.group.position, _UP);
      this.lh.arm.quaternion.setFromRotationMatrix(_m);
    }
    const rh = this.rh;
    rh.arm.position.copy(rh.group.position);
    _m.lookAt(this._rhElbow, rh.group.position, _UP);
    rh.arm.quaternion.setFromRotationMatrix(_m);
  }

  /* ---------------------------------------------------------- gameplay --- */

  get spreadNow() {
    const s = THREE.MathUtils.lerp(this.spec.spread, this.spec.adsSpread, this._adsPublic);
    const move = Math.hypot(this.player.velocity.x, this.player.velocity.z) / 8;
    const air = this.player.grounded ? 0 : 1;
    return s * (1 + move * 1.5 + air * 2.2 + this.heat * 0.55);
  }

  canFire() {
    // Melee has no magazine, so the ammo gate must not apply — with mag 0 the
    // knife could never swing AND tried to reload on every attempt.
    if (this.spec.melee) return this.cooldown <= 0 && !this.player.sprinting;
    return this.cooldown <= 0 && !this.reloading && this.ammo > 0 && !this.player.sprinting;
  }

  tryFire(now, fx) {
    if (!this.canFire()) {
      if (!this.spec.melee && this.ammo === 0 && !this.reloading) this.reload();
      return false;
    }
    this.cooldown = 60 / this.spec.rpm;
    if (this.spec.melee) { this.swing(); return true; }
    this.ammo--;
    this.shotIndex++;
    this.heat = Math.min(1, this.heat + 0.14);
    this.inspectT = 0;

    const r = this.spec.recoil;
    const rise = 1 - Math.min(1, this.shotIndex / 9) * 0.45;
    const drift = Math.sin(this.shotIndex * 0.7) * 0.6 + (Math.random() - 0.5) * 0.5;
    const adsScale = THREE.MathUtils.lerp(1, 0.62, this._adsPublic);
    this.player.addRecoil(r.pitch * rise * adsScale * 22, r.yaw * drift * adsScale * 22);

    // Viewmodel recoil: rearward, muzzle up, and a roll away from the shoulder.
    // Weighted toward rearward travel rather than muzzle flip. A hard C-clamp
    // is a lever that turns climb into a straight push back into the shoulder,
    // so a gun held that way should punch back further and rise less.
    const k = r.kick * (1 - this._adsPublic * 0.3);
    this.rc.zv += k * 40;
    this.rc.pv += k * 37 * rise;
    this.rc.rv += (drift * 0.5 - 0.45) * k * 26;
    this.rc.yv += drift * k * 9;
    this.trigger = 1; this.triggerV = 0;
    this.boltT = 1;
    this._fireHold = 0.45;
    // Pump gun: the shot itself does nothing to the action. The support hand
    // then drives the forend back and forward, and THAT is what ejects the
    // fired hull and chambers the next shell.
    if (this.art.family === 'shotgun') {
      this.pumpT = 1;
      this._ejected = false;
      this._fx = fx || null;
    }
    // Last round: the bolt locks back. On the MP5 that means the cocking lever
    // is rotated up into its notch, which is what the reload then slaps down.
    // A pump gun has no bolt hold-open at all.
    if (this.ammo === 0 && this.art.family !== 'shotgun') this.boltLocked = true;
    this.player.addTrauma(0.04);
    return true;
  }

  reload() {
    if (this.reloading || this.ammo === this.spec.mag || this.reserve <= 0) return;
    this.reloading = true;
    this.reloadFromEmpty = this.ammo === 0;
    this.reloadTimer = this.spec.reloadTime;
    this.inspectT = 0;
    this.onReloadStart?.();
  }

  inspect() {
    if (this.reloading || this.inspectT > 0) return;
    this.inspectT = 1e-4;
  }

  /* ------------------------------------------------------------ update --- */

  update(dt, input) {
    const spec = this.spec, A = this.art;
    // `_aimArms` runs at the end of update with no dt of its own, and the arm
    // solver needs one to damp the hand back after a reload.
    this._dt = dt;
    this.t += dt;
    this.cooldown -= dt;
    this.heat = Math.max(0, this.heat - dt * (this.cooldown > 0 ? 0.15 : 1.1));
    if (this.cooldown <= 0 && performance.now() * 0.001 - this.lastFire > 0.35) this.shotIndex = 0;

    if (this.reloading) {
      this.reloadTimer -= dt;
      if (this.reloadTimer <= 0) {
        const need = spec.mag - this.ammo;
        const take = Math.min(need, this.reserve);
        this.ammo += take;
        this.reserve -= take;
        this.reloading = false;
        this.boltLocked = false;
        this._leverTarget = null;
      }
    }

    if (input?.pressed?.('KeyF')) this.inspect();
    if (this.inspectT > 0) {
      this.inspectT += dt / 1.5;
      if (this.inspectT >= 1) this.inspectT = 0;
    }

    this.wantsAds = (this._adsForce || input.mouse.right) && !this.reloading && !this.player.sprinting;

    // ADS spring: slight overshoot then settle, never a linear lerp.
    const target = this.wantsAds ? 1 : 0;
    const om = 6.4 / spec.adsTime;
    const zeta = 0.66;
    this._adsVel += (target - this._adsRaw) * om * om * dt;
    this._adsVel *= Math.exp(-2 * zeta * om * dt);
    this._adsRaw += this._adsVel * dt;
    if (Math.abs(target - this._adsRaw) < 0.0004 && Math.abs(this._adsVel) < 0.004) {
      this._adsRaw = target; this._adsVel = 0;
    }
    const adsRaw = THREE.MathUtils.clamp(this._adsRaw, -0.12, 1.14);
    const adsEase = THREE.MathUtils.clamp(adsRaw, 0, 1);
    this._adsPublic = adsEase;

    // Sway lags the mouse — the classic weight cue.
    const targetSwayX = THREE.MathUtils.clamp(-input.mouse.dx * 6, -0.06, 0.06);
    const targetSwayY = THREE.MathUtils.clamp(input.mouse.dy * 6, -0.06, 0.06);
    this.swayVel.x += (targetSwayX - this.sway.x) * 260 * dt;
    this.swayVel.y += (targetSwayY - this.sway.y) * 260 * dt;
    this.swayVel.multiplyScalar(Math.exp(-13 * dt));
    this.sway.x += this.swayVel.x * dt;
    this.sway.y += this.swayVel.y * dt;

    // Recoil springs.
    const rc = this.rc;
    const sp = (v, vv, k, c) => {
      vv += -v * k * dt;
      vv *= Math.exp(-c * dt);
      return [v + vv * dt, vv];
    };
    [rc.z, rc.zv] = sp(rc.z, rc.zv, 1000, 17);
    [rc.p, rc.pv] = sp(rc.p, rc.pv, 900, 15);
    [rc.r, rc.rv] = sp(rc.r, rc.rv, 700, 13);
    [rc.y, rc.yv] = sp(rc.y, rc.yv, 800, 14);

    // Trigger finger spring (the pull itself).
    this.triggerV += -this.trigger * 2600 * dt;
    this.triggerV *= Math.exp(-26 * dt);
    this.trigger = Math.max(0, this.trigger + this.triggerV * dt);

    // Trigger discipline. The finger only comes off the receiver when the
    // shooter actually intends to fire; it goes straight back to register
    // during a reload, a sprint or on an empty gun. Second-order so it snaps
    // across with a little follow-through instead of sliding linearly.
    this._fireHold = Math.max(0, this._fireHold - dt);
    const onTarget = (!this.reloading && !this.player.sprinting
      && (input?.mouse?.left || this._fireHold > 0) && this.ammo > 0) ? 1 : 0;
    this._fingerVel += (onTarget - this.fingerOn) * 1000 * dt;
    this._fingerVel *= Math.exp(-38 * dt);
    this.fingerOn = THREE.MathUtils.clamp(this.fingerOn + this._fingerVel * dt, -0.08, 1.06);

    if (this.boltT > 0) this.boltT = Math.max(0, this.boltT - dt / Math.min(0.055, 60 / spec.rpm * 0.62));

    // Pump stroke timing. Deliberately not symmetric: the hand SNAPS the
    // forend back and rides it forward more slowly, which is both what the
    // shooter actually does and what stops the stroke reading as a wobble.
    if (this.pumpT > 0) {
      this.pumpT = Math.max(0, this.pumpT - dt / 0.36);
      const u = 1 - this.pumpT;
      this._pumpS = u < 0.40
        ? Math.sin((u / 0.40) * Math.PI / 2)
        : Math.cos(((u - 0.40) / 0.60) * Math.PI / 2);
      // Ejection happens at the top of the rearward stroke, not at the shot —
      // that is the whole difference between a pump gun and a semi-auto.
      if (!this._ejected && u >= 0.36) {
        this._ejected = true;
        if (this._fx) {
          this.muzzleWorld(_v);
          this._fx.ejectShell(_v, this.engine.camera.quaternion, this.player.velocity);
        }
      }
      // The lifter is empty while the action is open.
      const open = this._pumpS > 0.35;
      if (this.liftShell) { this.liftShell.visible = !open; this.liftHead.visible = !open; }
    } else if (this._pumpS !== 0) {
      this._pumpS = 0;
    }

    const p = this.player;
    const hSpeed = Math.hypot(p.velocity.x, p.velocity.z);
    const bobT = p.bobPhase;
    const bobAmt = (1 - adsEase * 0.85) * THREE.MathUtils.clamp(hSpeed / 7.6, 0, 1) * (p.grounded ? 1 : 0.2);

    this._sprint = THREE.MathUtils.damp(this._sprint, p.sprinting && hSpeed > 3 ? 1 : 0, 9, dt);
    const sprint = this._sprint * this._sprint * (3 - 2 * this._sprint);

    const pos = _v.set(0, 0, 0);
    pos.lerpVectors(this.hipPose.pos, this.adsPose.pos, adsRaw);
    const rot = _e.set(0, 0, 0);
    rot.x = THREE.MathUtils.lerp(this.hipPose.rot.x, this.adsPose.rot.x, adsRaw);
    rot.y = THREE.MathUtils.lerp(this.hipPose.rot.y, this.adsPose.rot.y, adsRaw);
    rot.z = THREE.MathUtils.lerp(this.hipPose.rot.z, this.adsPose.rot.z, adsRaw);

    // Idle breathing: a slow figure-eight, killed off almost entirely at ADS.
    // Breathing must vanish completely at full ADS or the dot drifts off the
    // crosshair, which is the one thing a player will notice instantly.
    const br = (1 - adsEase) * (1 - sprint);
    pos.x += Math.sin(this.t * 0.9) * 0.0038 * br;
    pos.y += Math.sin(this.t * 1.55 + 0.6) * 0.0042 * br;
    rot.x += Math.sin(this.t * 1.55 + 0.6) * 0.010 * br;
    rot.z += Math.sin(this.t * 0.9) * 0.012 * br;

    const swayMul = 1 - adsEase;
    pos.x += this.sway.x * swayMul + Math.cos(bobT) * 0.017 * bobAmt;
    pos.y += this.sway.y * swayMul + Math.sin(bobT * 2) * 0.011 * bobAmt;
    rot.x += this.sway.y * 1.5 * swayMul + Math.sin(bobT * 2) * 0.019 * bobAmt;
    rot.y += -this.sway.x * 1.5 * swayMul;
    rot.z += Math.cos(bobT) * 0.028 * bobAmt + p.roll * 1.4;

    // Recoil, applied after pose blending so ADS keeps its own weight.
    pos.z += rc.z * 0.7;
    pos.y += rc.p * 0.09;
    pos.x += rc.y * 0.05;
    rot.x += rc.p;
    rot.z += rc.r;
    rot.y += rc.y * 0.5;

    // Sprint: gun canted in across the body, muzzle down.
    pos.x += sprint * 0.048;
    pos.y += sprint * -0.052;
    pos.z += sprint * 0.020;
    rot.y += sprint * 0.40;
    rot.z += -sprint * 0.52;
    rot.x += -sprint * 0.10;

    this._animateReload(dt, spec, pos, rot);
    this._animateInspect(pos, rot);
    this._updateSwing(dt, pos, rot);
    this._aimArms();

    // One-handed weapons keep a rigged off hand for the reload — it carries the
    // magazine — but it has no business being on screen the rest of the time.
    // Shown for the whole reload, including the tail, so it is never cut off
    // mid-motion.
    if (this._oneHanded && this.lh) {
      const show = this.reloading;
      this.lh.group.visible = show;
      this.lh.arm.visible = show;
    }

    this.group.position.copy(pos);
    this.group.quaternion.setFromEuler(rot);

    // Bolt carrier reciprocates in the ejection port; the AR charging handle
    // correctly stays put unless it is being racked. A pump gun's bolt is
    // driven by the forend instead — see _pumpApply.
    if (this.bolt && A.family !== 'shotgun') {
      const bt = Math.max(Math.sin(this.boltT * Math.PI), this.boltLocked ? 1 : 0);
      this.bolt.position.z = this._boltZ + bt * this._boltTravel;
      if (this.hammerNode) {
        // Cocked at rest, carried further back by the slide, and dropped by
        // the trigger break itself.
        const drop = this.trigger * 0.86 * THREE.MathUtils.clamp(this.fingerOn, 0, 1);
        this.hammerNode.rotation.x = this._hamRest * (1 - drop) - bt * 0.34;
      }
      if (this.slideStop) this.slideStop.position.y = this._slideStopY + (this.boltLocked ? 0.0028 : 0);
    }
    if (this.pumpNode) this._pumpApply();
    if (this.warhead && !this.reloading) this.warhead.visible = this.ammo > 0;
    const fOn = THREE.MathUtils.clamp(this.fingerOn, 0, 1.06);
    if (this.triggerMesh) this.triggerMesh.position.z = this._trgZ + this.trigger * fOn * 0.0035;
    const tpull = this.trigger * 0.30 * fOn;
    // A knife has no trigger finger — its index is wrapped round the handle
    // with the rest, so there is nothing to spring here.
    if (this.trigJoints) {
      for (let i = 0; i < 3; i++) {
        this.trigJoints[i].rotation.z =
          THREE.MathUtils.lerp(this.trigIndexed[i], this.trigOn[i], this.fingerOn)
          - tpull * (i === 0 ? 0.9 : 0.5);
      }
    }

    // HK cocking lever: up in its notch while the bolt is held back, slapped
    // down to chamber. Driven explicitly through the reload; outside it, it
    // just follows the bolt state.
    if (this.chargeLever) {
      const want = this._leverTarget ?? (this.boltLocked ? 1 : 0);
      this._lever = THREE.MathUtils.damp(this._lever, want, want > this._lever ? 26 : 46, dt);
      this.chargeLever.rotation.z = -this._lever * 0.95;
    }

    // Scope takeover: a fast wipe across the last of the ADS blend, then the
    // weapon is gone and the overlay owns the frame.
    if (this.scopeOverlay) {
      const a = THREE.MathUtils.clamp((adsEase - 0.86) / 0.14, 0, 1);
      const e = a * a * (3 - 2 * a);
      this.scopeOverlay.visible = e > 0.002;
      for (const f of this._scopeFades) f.mat.opacity = f.base * e;
      this.model.visible = e < 0.995;
    }

    // Iron sights have no emitter, so these are null on the MP5.
    if (this.reticleDot) {
      const dotVis = adsEase > 0.06;
      this.reticleDot.visible = dotVis;
      this.reticleHalo.visible = dotVis;
      this.reticleDot.material.opacity = THREE.MathUtils.clamp(adsEase * 1.6, 0, 1);
      this.reticleHalo.material.opacity = 0.17 * adsEase;
    }

    // Zoom the world camera, not the viewmodel camera — that keeps the optic
    // locked on the screen centre no matter what the FOV does.
    const baseFov = 80;
    const targetFov = THREE.MathUtils.lerp(baseFov, baseFov * spec.fovZoom, adsEase);
    const cam = this.engine.camera;
    if (Math.abs(cam.fov - targetFov) > 0.01) {
      cam.fov = THREE.MathUtils.damp(cam.fov, targetFov, 22, dt);
      cam.updateProjectionMatrix();
    }
    return adsEase;
  }

  /** Magazine release, drop, fresh mag, bolt release — hands do the work. */
  _animateReload(dt, spec, pos, rot) {
    // Melee weapons have no magazine and no support-hand rig to drive.
    if (!this.magazine || !this.lh) return;
    const lh = this.lh.group;
    const H = this._lhHome;
    const fam = this.art.family;
    if (!this.reloading) {
      if (this.magazine.position.y !== this._magHome.y || lh.position.x !== H.p.x) {
        this.magazine.position.lerp(this._magHome, 1 - Math.exp(-22 * dt));
        this.magazine.rotation.x = THREE.MathUtils.damp(this.magazine.rotation.x, this._magRotHome ?? 0, 20, dt);
        this.magazine.rotation.z = THREE.MathUtils.damp(this.magazine.rotation.z, 0, 20, dt);
        lh.position.lerp(H.p, 1 - Math.exp(-16 * dt));
        lh.quaternion.slerp(H.q, 1 - Math.exp(-16 * dt));
      }
      // A pump gun's "magazine" node is the loose shell the hand carries, so
      // it only exists mid-reload; a launcher's is the warhead, which is only
      // there while the tube is loaded.
      this.magazine.visible = fam === 'shotgun' ? false
        : fam === 'launcher' ? this.ammo > 0 : true;
      this._shellsLoaded = 0;
      this._endPumped = false;
      return;
    }

    if (fam === 'shotgun') return this._reloadShells(dt, spec, pos, rot);
    if (fam === 'launcher') return this._reloadRocket(dt, spec, pos, rot);

    const t = 1 - this.reloadTimer / spec.reloadTime;
    const seg = (a, b) => THREE.MathUtils.clamp((t - a) / (b - a), 0, 1);
    const smooth = (x) => x * x * (3 - 2 * x);

    // Whole-gun move: cant the weapon inboard and bring it left and up so the
    // magwell and both hands are actually ON SCREEN. Dropping the gun — which
    // is what this used to do — hides the entire piece of animation you just
    // spent the reload time building.
    const dip = Math.sin(smooth(seg(0, 0.86)) * Math.PI);
    pos.y += dip * 0.062;
    pos.x -= dip * 0.132;
    pos.z += dip * 0.012;
    rot.x -= dip * 0.14;
    rot.z += dip * 0.62;
    rot.y -= dip * 0.08;

    // 1. Right thumb hits the mag release.
    const press = Math.sin(seg(0.06, 0.20) * Math.PI);
    const tr = this.rh.thumbRest[0];
    this.rh.thumb[0].rotation.set(tr.x, tr.y + press * 0.50, tr.z - press * 0.42);
    this.rh.thumb[1].rotation.z = this.rh.thumbRest[1] - press * 0.30;

    // 2. Mag falls free.
    const drop = smooth(seg(0.16, 0.36));
    // 3. Fresh mag rides back up into the well. An HK magazine does not go
    //    straight up: it is rocked in front lug first and then levered back
    //    until the catch bites, so its approach tilt runs the other way and
    //    finishes with a rearward rotation rather than a straight push.
    const hk = this.art.chargeStyle === 'hk';
    const ins = smooth(seg(0.50, 0.70));
    const seat = Math.sin(THREE.MathUtils.clamp((t - 0.68) / 0.10, 0, 1) * Math.PI);
    let magY, magRot = 0;
    if (t < 0.50) {
      magY = this._magHome.y - drop * 0.42;
      magRot = drop * 0.55;
      this.magazine.visible = drop < 0.97;
    } else {
      this.magazine.visible = ins > 0.02;
      magY = this._magHome.y - (1 - ins) * 0.34 - seat * 0.006;
      magRot = hk ? -(1 - ins) * 0.46 : (1 - ins) * 0.34;
    }
    this.magazine.position.y = magY;
    this.magazine.position.x = this._magHome.x + (t < 0.5 ? drop * 0.012 : (1 - ins) * -0.020);
    this.magazine.rotation.x = magRot * 0.5;
    this.magazine.rotation.z = magRot;

    // Left hand: handguard -> pouch -> feed the mag -> bolt release -> back.
    const slapping = this.reloadFromEmpty && hk && this._lhSlap;
    const toPouch = smooth(seg(0.10, 0.34));
    const toWell = smooth(seg(0.42, 0.66));
    const offWell = smooth(seg(0.70, 0.80));
    const toBolt = (this.reloadFromEmpty && !hk) ? smooth(seg(0.74, 0.84)) : 0;
    // Raise, then chop. The strike itself is deliberately quicker than
    // anything else in the reload: a slap that eases in is a caress.
    const raise = slapping ? smooth(seg(0.66, 0.79)) : 0;
    const chop = slapping ? THREE.MathUtils.clamp((t - 0.79) / 0.055, 0, 1) ** 0.6 : 0;
    const back = smooth(seg(this.reloadFromEmpty ? 0.87 : 0.78, 0.99));

    const P = _lhP, Q = _lhQ;
    P.copy(H.p); Q.copy(H.q);
    if (toPouch > 0) { P.lerp(this._lhMag.p, toPouch); Q.slerp(this._lhMag.q, toPouch); }
    if (toWell > 0) { P.lerp(this._lhInsert.p, toWell); Q.slerp(this._lhInsert.q, toWell); }
    if (offWell > 0) {
      _lhT.copy(this._lhInsert.p).add(_lhOff);
      P.lerp(_lhT, offWell * 0.6);
    }
    if (toBolt > 0) { P.lerp(this._lhBolt.p, toBolt); Q.slerp(this._lhBolt.q, toBolt); }
    if (raise > 0) { P.lerp(this._lhSlapUp.p, raise); Q.slerp(this._lhSlapUp.q, raise); }
    if (chop > 0) P.lerp(this._lhSlap.p, chop);
    if (back > 0) { P.lerp(H.p, back); Q.slerp(H.q, back); }
    lh.position.copy(P);
    lh.quaternion.copy(Q);

    // Bolt catch slaps in on an empty reload; charging handle stays home.
    if (this.boltCatchMesh && !hk) {
      const slap = this.reloadFromEmpty ? Math.sin(seg(0.80, 0.88) * Math.PI) : 0;
      this.boltCatchMesh.position.x = -(this.art.upperW * 0.47 + 0.001) + slap * 0.004;
      if (this.reloadFromEmpty && t > 0.84) this.boltLocked = false;
    }
    // The lever stays hooked up in its notch until the palm actually reaches
    // it, then drops out of the notch at full spring speed.
    if (this.chargeLever) {
      this._leverTarget = (slapping && t < 0.845) ? 1 : 0;
      if (t > 0.845 && this.boltLocked) this.boltLocked = false;
    }
  }

  /**
   * Shell-by-shell reload.
   *
   * A tube-magazine gun is not reloaded, it is topped up, and the animation
   * that sells it is repetition: the gun rolls inboard so the loading port
   * faces the camera, and the support hand shuttles between the shell carrier
   * and the port once per round. One round left over from a previous magazine
   * means one trip; an empty gun means six, and the player can see the
   * difference — which is the whole reason this is worth animating properly.
   */
  _reloadShells(dt, spec, pos, rot) {
    const lh = this.lh.group, H = this._lhHome;
    const t = 1 - this.reloadTimer / spec.reloadTime;
    const seg = (a, b) => THREE.MathUtils.clamp((t - a) / (b - a), 0, 1);
    const smooth = (x) => x * x * (3 - 2 * x);
    const need = Math.max(1, Math.min(spec.mag - this.ammo, this.reserve));

    // Roll the receiver over so the loading port is actually on screen.
    // Bring the loading port to the camera.
    //
    // The old envelope rolled the receiver 0.56 rad while leaving it 196 mm out
    // to the right, which swung the whole rear of the gun — grip, port, both
    // hands, the shell — off the bottom-right corner. Solved by projecting the
    // port instead of eyeballing it: it now lands just left of and below the
    // crosshair, with less roll and a much larger inboard pull.
    const env = Math.sin(smooth(seg(0, 0.95)) * Math.PI);
    pos.y += env * 0.074;
    pos.x -= env * 0.128;
    pos.z -= env * 0.038;
    rot.z += env * 0.50;
    rot.y -= env * 0.14;
    rot.x -= env * 0.07;

    const T0 = 0.15, T1 = 0.88;
    const belt = this._lhShellBelt, port = this._lhShellPort;
    const P = _lhP, Q = _lhQ;
    let carry = 0;              // 0 at the belt, 1 at the port

    if (t < T0) {
      const k = smooth(seg(0, T0));
      P.copy(H.p).lerp(belt.p, k); Q.copy(H.q).slerp(belt.q, k);
    } else if (t > T1) {
      const k = smooth(seg(T1, 1));
      P.copy(belt.p).lerp(H.p, k); Q.copy(belt.q).slerp(H.q, k);
      this._shellsLoaded = need;
      // An empty gun still has to be pumped closed before it will fire.
      if (this.reloadFromEmpty && !this._endPumped && t > T1 + 0.02) {
        this._endPumped = true;
        this.pumpT = 1;
        this._ejected = true;   // nothing in the chamber to throw out
      }
    } else {
      const u = ((t - T0) / (T1 - T0)) * need;
      const i = Math.min(need - 1, Math.floor(u));
      const f = u - i;
      // reach in, press the shell home, come back for the next one
      carry = f < 0.50 ? smooth(f / 0.50) : f < 0.68 ? 1 : 1 - smooth((f - 0.68) / 0.32);
      P.copy(belt.p).lerp(port.p, carry); Q.copy(belt.q).slerp(port.q, carry);
      this._shellsLoaded = i + (f > 0.62 ? 1 : 0);
    }
    lh.position.copy(P);
    lh.quaternion.copy(Q);

    // The shell being carried rides pinched in the fingertips, and vanishes
    // the moment it is pressed into the port.
    const holding = carry > 0.02 && carry < 0.70;
    this.magazine.visible = holding;
    if (holding) {
      // NOT `_v`. update() hands us its pose accumulator as `pos`, and that
      // accumulator IS the module-scope `_v` — writing the shell offset into it
      // silently threw away the whole reload envelope every frame the hand was
      // carrying a shell, which is why the gun never moved and the loading port
      // never came on screen.
      this.magazine.position.copy(P).add(_lhT.copy(SHELL_CARRY).applyQuaternion(Q));
      this.magazine.quaternion.copy(Q).multiply(SHELL_FIX);
    }
    // The lifter shows a live round once the first shell is in.
    if (this.liftShell) {
      const has = this.ammo + this._shellsLoaded > 0;
      this.liftShell.visible = has; this.liftHead.visible = has;
    }
  }

  /**
   * Reload the launcher: a fresh PG-7V comes off the back and goes in the
   * FRONT of the tube. Nothing about this resembles a magazine change, which
   * is exactly why it is worth the separate animation — the support hand
   * travels the entire length of the weapon and the warhead reappears at the
   * muzzle rather than at the receiver.
   */
  _reloadRocket(dt, spec, pos, rot) {
    const lh = this.lh.group, H = this._lhHome;
    const t = 1 - this.reloadTimer / spec.reloadTime;
    const seg = (a, b) => THREE.MathUtils.clamp((t - a) / (b - a), 0, 1);
    const smooth = (x) => x * x * (3 - 2 * x);

    // Bring the muzzle back and inboard so the loading end is on screen; a
    // launcher held out at arm's length loads off the side of the frame.
    const dip = Math.sin(smooth(seg(0, 0.94)) * Math.PI);
    pos.y -= dip * 0.026;
    pos.x -= dip * 0.052;
    rot.z += dip * 0.30;
    rot.x += dip * 0.13;
    rot.y += dip * 0.10;

    const toPack = smooth(seg(0.04, 0.30));
    const toMuzzle = smooth(seg(0.34, 0.62));
    const back = smooth(seg(0.80, 1.0));
    const P = _lhP, Q = _lhQ;
    P.copy(H.p); Q.copy(H.q);
    if (toPack > 0) { P.lerp(this._lhMag.p, toPack); Q.slerp(this._lhMag.q, toPack); }
    if (toMuzzle > 0) { P.lerp(this._lhInsert.p, toMuzzle); Q.slerp(this._lhInsert.q, toMuzzle); }
    if (back > 0) { P.lerp(H.p, back); Q.slerp(H.q, back); }
    lh.position.copy(P);
    lh.quaternion.copy(Q);

    // The round appears in the hand at the pack and is pushed down the bore.
    const seat = smooth(seg(0.58, 0.80));
    const wh = this.magazine;
    wh.visible = t > 0.30;
    wh.position.copy(this._magHome);
    wh.position.x += (1 - seat) * -0.052;
    wh.position.y += (1 - seat) * -0.062;
    wh.position.z += (1 - seat) * -0.150;
    wh.rotation.z = (1 - seat) * 0.34;
    wh.rotation.x = (1 - seat) * -0.20;
  }

  /**
   * Apply the pump stroke.
   *
   * Runs after the reload animation has had its say about where the support
   * hand is, and shifts that hand along the bore by the same amount as the
   * forend. The offset is applied incrementally against the previous frame's
   * value so it composes with the reload keyframes instead of fighting them.
   */
  _pumpApply() {
    const dz = this._pumpS * this.art.pumpTravel;
    this.pumpNode.position.z = this._pumpZ + dz;
    if (this.bolt) this.bolt.position.z = this._boltZ + this._pumpS * this._boltTravel;
    this.lh.group.position.z += dz - this._pumpPrevDz;
    this._pumpPrevDz = dz;
  }

  /** Idle inspect: tilt the weapon up and in, look at the receiver. */
  _animateInspect(pos, rot) {
    if (this.inspectT <= 0) return;
    const t = this.inspectT;
    const env = Math.sin(THREE.MathUtils.clamp(t, 0, 1) * Math.PI);
    const e = env * env * (3 - 2 * env);
    pos.x -= e * 0.075;
    pos.y += e * 0.020;
    pos.z += e * 0.035;
    rot.y -= e * 0.72;
    rot.z += e * 0.55;
    rot.x += Math.sin(t * Math.PI * 2) * 0.12 * e;
  }

  /** Start a knife swing. Called by main.js when a melee attack lands. */
  swing() {
    if (this.swingT >= 0 && this.swingT < 0.45) return;
    this.swingT = 0;
  }

  /**
   * Knife swing animation. A diagonal slash: the blade cocks back and inboard,
   * then drives across and down through the target, then recovers. Driven off
   * a single normalised time so the arc stays consistent at any frame rate.
   */
  _updateSwing(dt, pos, rot) {
    if (this.swingT < 0) return;
    this.swingT += dt;
    const T = 0.42;
    const k = this.swingT / T;
    if (k >= 1) { this.swingT = -1; return; }

    // Wind-up to 0.28, strike to 0.55, recover to 1.
    let a;
    if (k < 0.28) a = -(k / 0.28) * 0.35;                     // cock back
    else if (k < 0.55) a = -0.35 + ((k - 0.28) / 0.27) * 1.75; // slash through
    else a = 1.40 * (1 - (k - 0.55) / 0.45);                   // recover

    pos.x += -a * 0.10;
    pos.y += Math.sin(a * 1.6) * 0.045;
    pos.z += a * 0.075;
    rot.z += a * 0.95;
    rot.y += a * 0.55;
    rot.x += -a * 0.30;
  }

  /**
   * World-space muzzle position for tracers and muzzle light.
   * The viewmodel camera is pinned to the identity transform, so viewmodel
   * space *is* camera space and one matrix multiply gets us to world space.
   */
  muzzleWorld(target) {
    this.muzzle.updateWorldMatrix(true, false);
    target.setFromMatrixPosition(this.muzzle.matrixWorld);
    target.applyMatrix4(this.engine.camera.matrixWorld);
    return target;
  }
}

const _lhP = new THREE.Vector3();
const _lhQ = new THREE.Quaternion();
const _lhT = new THREE.Vector3();
const _lhOff = new THREE.Vector3(-0.03, 0.02, 0);
// Where a carried shotgun shell sits relative to the support wrist, and the
// twist that lays its axis along the fingers rather than out of the palm.
const SHELL_CARRY = new THREE.Vector3(0.086, -0.006, -0.012);
const SHELL_FIX = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
